/**
 * View-model selectors — the screen-by-screen replacement for the prototype's
 * `renderVals()` (line 2233) and `ptVals()` (line 2036).
 *
 * Every value the template read is reproduced here with the same field names,
 * so a P3 UI lane can port markup without re-deriving anything. Two deliberate
 * differences, both from PORTING-NOTES P2-01:
 *
 *   1. No closures. `open`, `click`, `pick`, `add`, `rm` are store actions the
 *      component binds itself. A selector that minted functions would return a
 *      fresh object identity on every call and defeat memoization.
 *   2. Art is a *candidate ladder* (`coverCands` / `hdrCands`), not a resolved
 *      `coverBg`. The prototype resolved it with `Image()` probes inside
 *      `art()`; that is a component concern, and keeping the ladder pure makes
 *      the ordering testable (`artCands` in `lib/logic.ts`).
 *
 * All time-dependent selectors take an explicit `now`.
 */

import { inkOn } from '../lib/color.ts';
import {
  ago,
  MONTHS,
  capNumTxt,
  chipOf,
  chkAgo,
  commentParts,
  fallGradient,
  fitTxt,
  fmt$,
  fmtD,
  fmtDFull,
  fmtPk,
  norm,
  platTxt,
  playersSrcTxt,
  ptFmt,
  revMeta,
  REV_RUNG_NAMES,
  scoreClr,
} from '../lib/format.ts';
import type { Chip } from '../lib/format.ts';
import type { DetailFacts } from '../repo/Repository.ts';
import {
  agg,
  CALLS,
  detailVerdict,
  fltCount,
  gamesCount,
  gotyQueue,
  GOTY_TODO_ROW,
  GOTY_YEAR,
  intQueue,
  intRowIds,
  INT_TODO_ROW,
  isCall,
  LVL,
  matchesCount,
  mkBars,
  mkPriceBars,
  newForMe,
  pass,
  passBoard,
  passFlt,
  priceLabel,
  ptArt,
  ptBuckets,
  ptGroupOf,
  ptPass,
  ptSquads,
  ptWinBox,
  sharedRowGames,
  sharedWaitGames,
  sharedRowMeta,
  SHARED_ROW_KEYS,
  sortArr,
  stats,
  tierRowIds,
  tileVM,
  TIER_NAMES_SHEET,
  TIERS,
  PT_BROWSE,
} from '../lib/logic.ts';
import type { BarsVM, IntRowKey, Pair, PriceBarsVM, TagLens, TileCtx, TileVM } from '../lib/logic.ts';
import type {
  ArtCand,
  BacklogEntry,
  FilterKey,
  FilterScope,
  Filters,
  Game,
  GameId,
  ListEntry,
  Playtest,
  PtGroup,
  Screen,
  ScoreRec,
  SendRec,
  TriggerKey,
  SharedTier,
  Tier,
  TriState,
  User,
  VoteInput,
  VoteValue,
} from '../lib/types.ts';
import type { GotySignal } from '../repo/Repository.ts';
import { embedParentHost, twitchEmbedUrl, ytEmbedUrl, ytThumbUrl } from '../lib/videoUrl.ts';
import { browseMode, POPULAR_TAG_NAMES } from './slices/browse.ts';
import { SEND_NOTE_MAX } from './slices/recs.ts';
import { armedKeys, armedN, capPct, eventText, TAG, tagBg, tagClr, TR, trNa, watchLine } from '../lib/watch.ts';
import { feedMode } from './slices/feed.ts';
import { OFFLINE_AFTER } from './slices/freshness.ts';
import { PASSWORD_MIN, PASSWORD_MIN_COPY } from './slices/session.ts';
import { bTagsOf, fltKeyOf, fltOf, xTagsOf } from './filterKey.ts';
import { linkStateOf, ownableKeys, ownDisabledBadge, ownDisabledTip } from './ownedBy.ts';
import type { QdStore } from './types.ts';

// ---------------------------------------------------------------------------
// shared pieces
// ---------------------------------------------------------------------------

export interface CapsuleVM {
  key: string;
  id: GameId;
  nameUp: string;
  fall: string;
  ini: string;
  sheen: string;
  coverCands: ArtCand[];
  hdrCands: ArtCand[];
  filt: string;
}

function tileCtx(s: QdStore): TileCtx {
  return { listIds: new Set(s.list.map((e) => e.id)), backlogIds: new Set(s.myBacklog().map((e) => e.id)), assets: s.assets };
}

function capsuleOf(g: Game, t: TileVM, filt = 'none'): CapsuleVM {
  return {
    key: '' + g.id,
    id: g.id,
    nameUp: g.n.toUpperCase(),
    fall: t.fall,
    ini: t.ini,
    sheen: t.sheen,
    coverCands: t.coverCands,
    hdrCands: t.hdrCands,
    filt,
  };
}

/**
 * How old a `ccu` sample may be before the client stops calling it live.
 *
 * The SERVER'S rule, restated on this side rather than a second quieter one of
 * our own: `discover_page`'s `why` arm refuses a PLAYING NOW caption without
 * `ccu_at` inside 24h, so a client that used a different window would let the
 * two surfaces disagree about the same row. Phase 2's twice-daily sweep leaves
 * a swept row ~12h stale worst case, which is what gives this gate slack — it
 * is the display rule, not the sampling period.
 */
export const CCU_FRESH_MS = 24 * 36e5;

/**
 * The two popularity numbers a row carries, and how old the live one is.
 *
 * NOTHING RENDERS THESE YET, deliberately. Tile presentation is the user's own
 * design pass; this exists so that pass is a rendering decision rather than a
 * plumbing job — a later "12K PLAYING NOW" line, a freshness dot, or a
 * "TRENDING THIS WEEK" badge needs no new selector, no new store field and no
 * repository change, just a component reading a value that is already here.
 *
 * THE TWO NUMBERS ARE ALLOWED TO DISAGREE and a component must not "fix" that:
 * `popScore` is the decay-weighted weekly peak the feed ranks on and `ccu` is
 * one instant's sample (`lib/types.ts` spells out why the disagreement is the
 * design). Averaging them, or preferring whichever is larger, would produce a
 * number no query on either side of the wire could reproduce.
 */
export interface LiveStatsVM {
  /** `Game.popScore`. `null` = never sampled — NOT the claim `0` makes. */
  popScore: number | null;
  /** The stored sample (`steam_facts.ccu`); `0` = we hold no sample at all. */
  ccu: number;
  /**
   * Age of that sample in ms, `null` when the row carries no usable `ccuAt` —
   * a fixture, a live-search hit, an RPC deploy older than 2901. A future
   * stamp (clock skew) stays negative rather than being clamped: pretending a
   * skewed row is 0ms old would be the same lie in the other direction.
   */
  ccuAgeMs: number | null;
  /** `ccuAgeMs` inside `CCU_FRESH_MS` — the only licence to say "now". */
  ccuFresh: boolean;
}

/**
 * One row's live/popularity facts. Pure, and `now` is a parameter rather than a
 * `Date.now()` inside — the same rule every time-dependent selector in this
 * file follows, and what lets a freshness boundary be tested at all.
 */
export function liveStatsOf(g: Game, now: number): LiveStatsVM {
  // An unparseable stamp reads exactly like an absent one — `Date.parse` of
  // junk is NaN, and NaN must never come out the other side as an age.
  const at = g.ccuAt === undefined ? NaN : Date.parse(g.ccuAt);
  const ccuAgeMs = Number.isFinite(at) ? now - at : null;
  return {
    popScore: g.popScore ?? null,
    ccu: g.ccu,
    ccuAgeMs,
    ccuFresh: ccuAgeMs !== null && ccuAgeMs < CCU_FRESH_MS,
  };
}

/**
 * Where the CURRENT surface sends its picked tags.
 *
 * It no longer decides which FILTERS apply — every filter applies everywhere
 * now (`lib/logic.ts#passFlt`). What survives is the one difference that is
 * real: on `discover` the include list is the question asked of Steam, so the
 * rows arrive already matched; under `lens` nobody asked Steam anything and the
 * same tags have to narrow the rows in hand (`tagLensOf`).
 */
export function scopeOf(s: QdStore): FilterScope {
  return fltKeyOf(s) === 'discover' ? 'discover' : 'lens';
}

/**
 * The picked tags as a local predicate, or null when there is nothing to apply
 * — no tags picked, or the vocabulary that resolves `stags` into ids has not
 * landed. Built once per pass: the name→id map is 446 entries and rebuilding it
 * per row would make a board render O(rows × vocab).
 *
 * Discover has no business calling it — the key is typed to exclude it. Steam
 * already answered the tag question there, and the browse page's own exclude
 * arm runs in `discoverVM` over `Game.tids`.
 */
export function tagLensOf(s: QdStore, key: Exclude<FilterKey, 'discover'>): TagLens | null {
  const inc = s.bTagsBy[key];
  const exc = s.xTagsBy[key];
  if (inc.length === 0 && exc.length === 0) return null;
  const byName = new Map(s.tagVocab.map((t) => [t.name.toLowerCase(), t.id]));
  return { inc, exc, byName };
}

/**
 * `entries` — list ∩ catalog ∩ the filter set (prototype line 2304).
 *
 * Tonight and Interests show games the crew has already voted on, and each is
 * narrowed by ITS OWN filter set — the crew lens, price, status, tags, all of
 * it. They share this function and share nothing else: the key is a parameter
 * because these two boards read the same rows through different filters, and
 * inferring it from `s.screen` would make Tonight's answer depend on where the
 * user is standing (`store/filterKey.ts`).
 */
export function entriesOf(s: QdStore, key: 'tonight' | 'interests'): Pair[] {
  const flt = s.fltBy[key];
  const tl = tagLensOf(s, key);
  const out: Pair[] = [];
  for (const e of s.list) {
    const g = s.gById(e.id);
    // `passBoard` — the filters, the tag lens and the board search box, in one
    // predicate so the four board pools cannot drift (2026-08-28).
    if (g && passBoard(g, flt, tl, s.bqBy[key], s.pts, s.ownBy)) out.push({ e, g });
  }
  return out;
}

/**
 * How many rows the board would show with NOTHING narrowing it — the list ∩ the
 * catalog and no more.
 *
 * This is the number that tells a board searched down to nothing from a board
 * that is genuinely empty, which the empty state used to conflate: it said
 * `NOTHING ON THE LIST YET — GO TO DISCOVER AND HIT + ON A GAME` to someone
 * whose list had eighteen games and whose query matched none of them
 * (DESIGN-CHANGELOG 2026-08-28, "The empty state stops lying").
 */
export function listRawCount(s: QdStore): number {
  let n = 0;
  for (const e of s.list) if (s.gById(e.id)) n++;
  return n;
}

/** `NOTHING ON THE LIST YET` vs `NO MATCHES` — see `listRawCount`. */
export function boardEmptyTxt(rawN: number): string {
  return rawN
    ? 'NO MATCHES ON THE LIST — CLEAR THE SEARCH OR TRY FEWER FILTERS'
    : 'NOTHING ON THE LIST YET — GO TO DISCOVER AND HIT + ON A GAME';
}

// ---------------------------------------------------------------------------
// header
// ---------------------------------------------------------------------------

export interface FreshnessChip {
  txt: string;
  clr: string;
}

/** docs/ARCHITECTURE.md §5.4 chip table, verbatim. */
export function freshnessChip(s: QdStore, now: number = Date.now()): FreshnessChip {
  const f = s.freshness;
  if (!s.steamLink || !s.steamLink.linked) return { txt: 'LINK STEAM', clr: 'var(--acc2)' };
  if (f.state === 'busy' && f.lastOkAt === null) return { txt: 'CHECKING…', clr: 'var(--gt-muted)' };
  if (f.state === 'retrying') {
    const head = f.failures >= OFFLINE_AFTER ? 'OFFLINE' : 'RETRYING';
    return { txt: head + ' · LAST OK ' + chkAgo(f.lastOkAt, now), clr: 'var(--acc2)' };
  }
  if (f.state === 'ok') return { txt: 'LIVE · CHECKED ' + chkAgo(f.lastOkAt, now), clr: 'var(--acc)' };
  return { txt: 'CHECKING…', clr: 'var(--gt-muted)' };
}

export interface TabItem {
  key: string;
  l: string;
  /** the tab's drawn icon — one `<path d>`, painted `currentColor` */
  d: string;
  /** the grid `d` is drawn on: `0 0 9 9`, except TONIGHT?'s 14×14 quarter */
  vb: string;
  clr: string;
  bd: string;
}

/*
 * The tab bar's five icons (DESIGN-CHANGELOG 2026-09-16), drawn 9×9 the way the
 * bell and the doors are, never a font glyph. They replaced `▲ ▦ ▸ ◆ ◈`, three
 * of which collided: `▸` is the GO prefix, not a search; `◆` and `◈` blur into
 * one at 14px and `◆` was BACKLOG's door; `▲` was MY INTERESTS'. The glyphs
 * keep their other jobs — they just stop being navigation.
 *
 * PATHS ARE THE DESIGN'S BYTES — `GT_LOGIC.NAV_*` in `design/gt-logic.js`,
 * copied, not redrawn. ME is one figure and PARTY two: a matched pair, so never
 * redraw one without the other.
 */
const NAV_ME = 'M3 0h3v3H3zM3 4h3v1H3zM2 5h5v2H2zM1 7h7v2H1z';
/**
 * TONIGHT? is the quarter, not a board (DESIGN-CHANGELOG 2026-09-19): the site
 * is QUARTERDOWN, and putting a quarter down is how you call next game. The one
 * nav icon on a 14×14 grid, because 25 will not read in 9 cells — it is the gold
 * coin's own silhouette with the numerals carved out. Still not the brand coin:
 * monochrome, the tab's colour, never animated. Never let the two converge.
 */
const NAV_COIN =
  'M5 0h4v1H5zM3 1h8v1H3zM2 2h10v1H2zM1 3h12v2H1zM0 5h3v1H0zM6 5h1v1H6zM10 5h4v1H10zM0 6h5v1H0zM6 6h1v1H6zM8 6h6v1H8zM0 7h3v1H0zM6 7h1v1H6zM10 7h4v1H10zM0 8h3v1H0zM4 8h5v1H4zM10 8h4v1H10zM1 9h2v1H1zM6 9h1v1H6zM10 9h3v1H10zM1 10h12v1H1zM2 11h10v1H2zM3 12h8v1H3zM5 13h4v1H5z';
/** DISCOVER is the one screen that asks Steam a question — the lens says so. */
const NAV_LENS =
  'M2 0h3v1H2zM1 1h1v1H1zM5 1h1v1H5zM0 2h1v3H0zM6 2h1v3H6zM1 5h1v1H1zM5 5h1v1H5zM2 6h3v1H2zM6 6h2v1H6zM7 7h2v2H7z';
/** A flask with liquid: a build you try before it is finished. */
const NAV_FLASK = 'M3 0h3v1H3zM3 1h1v2H3zM5 1h1v2H5zM2 3h1v1H2zM6 3h1v1H6zM1 4h1v4H1zM7 4h1v4H7zM1 8h7v1H1zM2 6h5v2H2z';
const NAV_PEOPLE = 'M1 1h2v2H1zM6 1h2v2H6zM1 4h2v1H1zM6 4h2v1H6zM0 5h4v3H0zM5 5h4v3H5z';
/**
 * SCHEDULE (DESIGN-CHANGELOG 2026-10-14): a page with a ring and two rows of
 * dots — `GT_LOGIC.NAV_CAL`, the design's bytes. No nav icon repeats across two
 * surfaces, so it is drawn rather than borrowed from a door.
 */
const NAV_CAL = 'M2 0h1v1H2zM6 0h1v1H6zM0 1h9v2H0zM0 3h1v6H0zM8 3h1v6H8zM0 8h9v1H0zM2 4h1v1H2zM4 4h1v1H4zM6 4h1v1H6zM2 6h1v1H2zM4 6h1v1H4zM6 6h1v1H6z';

/**
 * The phone tab bar, in render order — `headerVM` maps this array straight
 * through and `MobileApp` renders it in map order, so array position IS the
 * ordering and nothing else pins it.
 *
 * MINE sits before DISCOVER: the two crew-decision surfaces are adjacent on the
 * left and browsing moves right of them. Desktop's tab order is a separate list
 * and is deliberately untouched.
 */
const TABS: ReadonlyArray<{ l: string; d: string; vb?: string; s: QdStore['screen'] }> = [
  // ME leads (DESIGN-CHANGELOG 2026-09-03 "ME leads the tab bar"): the page
  // about you is the screen every sign-in lands on, so it is the first tab. The
  // old MINE tab pointed at MY INTERESTS; that board is a door inside ME now,
  // and GOTY went with it — both light this tab while they are up. PARTY is a
  // phone tab for the first time, the way the desktop nav already had it.
  { l: 'ME', d: NAV_ME, s: 'mine' },
  { l: 'TONIGHT?', d: NAV_COIN, vb: '0 0 14 14', s: 'tonight' },
  // SCHEDULE sits between TONIGHT? and DISCOVER (DESIGN-CHANGELOG 2026-10-14):
  // "now" and "later" side by side, browsing to the right of both.
  { l: 'SCHEDULE', d: NAV_CAL, s: 'schedule' },
  { l: 'DISCOVER', d: NAV_LENS, s: 'discover' },
  { l: 'TESTS', d: NAV_FLASK, s: 'playtests' },
  { l: 'PARTY', d: NAV_PEOPLE, s: 'crew' },
];

/**
 * The tab a screen lights (DESIGN-CHANGELOG 2026-09-03). MY INTERESTS, GOTY and
 * PROFILE are MY GAMES' sub-pages, so they light ME; BACKLOG, MONITORING and
 * RECOMMENDATIONS join them as their phases land (docs/porting/AA.md). Shared
 * by the phone tab bar and the desktop nav, so the two cannot disagree about
 * which tab a page belongs to.
 */
export function navCurOf(cur: Screen): Screen {
  if (cur === 'manage' || cur === 'member') return 'crew';
  // A night of the SCHEDULE is the tab's own sub-page (2026-10-14).
  if (cur === 'schedDay') return 'schedule';
  return cur === 'interests' ||
    cur === 'goty' ||
    cur === 'profile' ||
    cur === 'backlog' ||
    cur === 'scores' ||
    cur === 'recs' ||
    cur === 'monitoring'
    ? 'mine'
    : cur;
}

/**
 * The landing screen, which serves two audiences the mirrors only drew one of.
 *
 * Signed OUT it is the entry point: START A CREW, or LOG IN. Signed IN with no
 * crew it is where `boot.ts` and `removeCrew` both send you, because Tonight
 * would render an empty board as though the crew were quiet.
 *
 * THE MIRRORS HAVE NO CREWLESS STATE and that is not an omission we should
 * copy: the prototype refuses such an account at the login form outright
 * (`THAT ACCOUNT IS IN NO CREW — YOU NEED AN INVITE`), because in its world an
 * account cannot outlive its crew. Ours can, twice over — phase B's removal
 * deletes the membership and keeps the account (D5), and an owner can delete
 * their last crew. So the screen has to answer somebody who is already signed
 * in, and offering them LOG IN would be answering the wrong question.
 */
export function landingVM(s: QdStore) {
  const crewless = !!s.me() && s.activeCrew === null;
  /* TWO FACTS, TWO PREDICATES, and they are not the same question.
     `crewless` needs the ROSTER, which is crew-scoped and therefore empty for
     exactly the account this screen exists to serve; `session` survives with
     no crew at all. Folding them together would let a snapshot outage tell
     somebody who has a crew that they are not in one. */
  const signedIn = !!s.session || !!s.me();
  return {
    crewless,
    /* An invite link is the only way into somebody else's crew — there is no
       browser and no request-to-join — so the note says so rather than
       implying a door that does not exist. */
    noteTxt: crewless ? 'YOU ARE NOT IN A PARTY. START ONE, OR OPEN AN INVITE LINK.' : '',
    altLbl: crewless ? 'SIGN OUT ▸' : 'LOG IN ▸',
    /* WHO YOU ARE, which this screen never said (Marcus, 2026-08-20). It is
       the only screen a crewless account can reach — no header, no nav, no
       Profile — so without this the app is signed in as somebody it will not
       name, and the SIGN OUT beneath it is a guess.

       `pfAuthEmail` is the LOGIN address, not the recovery one, and for an
       invited member that is the synthesized `<key>@crew.quarterdown.com`.
       That is the right thing to show precisely because it is what they must
       type to get back in — see the invite panel, which now says the same. */
    signedIn,
    whoLbl: signedIn ? 'SIGNED IN AS' : '',
    whoTxt: signedIn ? s.pfAuthEmail || s.session : '',
  };
}

/**
 * The JOIN screen (3026, docs/INVITES.md) — everything it can say, and to whom.
 *
 * The reader is one of three people and the screen must not guess wrong:
 * somebody with no account, somebody already signed in AS the invited address,
 * or somebody signed in as SOMEBODY ELSE. Only the third case is a dead end,
 * and it is a common one — an owner testing an invitation on their own machine
 * hits it every time — so it gets a way out rather than a refusal.
 *
 * TWO REFUSALS DELIBERATELY SAY LESS. An expired or revoked invitation does not
 * name the crew or the person who sent it, and an unknown token is worded
 * identically to a deleted crew: whoever is holding a stale forwarded link
 * learns nothing from it.
 */
export function joinVM(s: QdStore) {
  const p = s.joinPeek;
  const me = s.me();
  const signedIn = Boolean(s.session);
  const state = p?.state ?? 'invalid';
  const crew = (p?.crewName ?? '').toUpperCase();
  const who = p?.inviterName ?? '';
  const masked = p?.emailMasked ?? '';

  /* Signed in as the WRONG person. The address the invitation names is the
     only thing that decides it, and the server checks the same two places —
     so this is a preview of that answer, not a second gate. */
  const myEmail = (s.pfAuthEmail || '').toLowerCase();
  const wrongAccount =
    signedIn && state === 'live' && masked !== '' && !maskMatches(masked, myEmail);

  const headline =
    state !== 'live'
      ? ''
      : who
        ? who + ' INVITED YOU TO ' + crew
        : 'YOU HAVE BEEN INVITED TO ' + crew;

  const closedTxt =
    state === 'expired' || state === 'revoked' || state === 'superseded'
      ? 'THIS INVITE HAS EXPIRED — ASK FOR A NEW ONE.'
      : state === 'accepted'
        ? /* NOT "you're already in <crew>", which this used to say and which
             stops being true the moment somebody is removed. Marcus was
             removed from the crew on staging, re-opened the link, and was told
             he was still in it (2026-08-21). The invitation's state is a fact
             about the INVITATION — it was used — and says nothing about who is
             in the crew now. */
          'THIS INVITE HAS ALREADY BEEN USED.'
        : state === 'declined'
          ? 'YOU DECLINED THIS INVITE.'
          : "THIS INVITE LINK ISN'T VALID.";

  return {
    loading: s.joinBusy && !p,
    state,
    live: state === 'live',
    headline,
    /** `SENT TO ch****@example.com` — never the whole address. */
    sentToTxt: masked ? 'SENT TO ' + masked : '',
    closedTxt,
    wrongAccount,
    wrongAccountTxt: wrongAccount
      ? "YOU'RE SIGNED IN AS " + (me ? me.name.toUpperCase() : 'SOMEBODY ELSE') + '.'
      : '',
    wrongAccountHint: wrongAccount ? 'SIGN OUT TO ACCEPT IT.' : '',
    /** Signed in and it IS them: one tap, no fields, nothing about their
     *  account changes. */
    joinAsMe: signedIn && state === 'live' && !wrongAccount && !s.joinNeedsLogin,
    /**
     * Signed out AND no account to sign into: they choose their own name and
     * password here.
     *
     * `joinNeedsLogin` excludes the one case where those two come apart — a
     * session that died mid-screen. Its owner has an account by definition, and
     * showing them this form asks a member to invent a second identity on an
     * address that already has one. See `needsLogin`.
     */
    needsAccount: !signedIn && !s.joinNeedsLogin && state === 'live',
    /** The session went stale under the screen. Sign in; do not sign up. */
    needsLogin: s.joinNeedsLogin && state === 'live',
    needsLoginTxt: 'YOU ARE NO LONGER SIGNED IN.',
    needsLoginHint: 'SIGN IN AND YOU WILL COME STRAIGHT BACK HERE TO JOIN ' + crew + '.',
    joinLbl: s.joinBusy
      ? 'JOINING…'
      : signedIn && me
        ? 'JOIN AS ' + me.name.toUpperCase()
        : 'JOIN ' + crew,
    joinBusy: s.joinBusy,
    joinName: s.joinName,
    joinPw: s.joinPw,
    joinPw2: s.joinPw2,
    /* Live, but only once the second field has something in it — flagging a
       mismatch against an empty box would be scolding somebody mid-type. */
    pwMismatch: s.joinPw2.length > 0 && s.joinPw !== s.joinPw2,
    joinErr: s.joinErr,
    hasErr: Boolean(s.joinErr),
    pwHint: 'USE AT LEAST 8 CHARACTERS',
    haveAccountTxt: 'ALREADY HAVE AN ACCOUNT? LOG IN',
  };
}

/** Does a masked address plausibly describe this one?
 *
 *  `ch****@example.com` against `chris@example.com`: the domain must match
 *  exactly and the visible prefix must match. It cannot be exact — the point of
 *  masking is that the screen was never told the whole address — so this is
 *  only ever a PREVIEW of the server's answer, which compares the real values.
 *  A false "looks like you" ends in an honest refusal from `accept`; a false
 *  "not you" would strand somebody, so it errs toward showing the join button.
 */
function maskMatches(masked: string, email: string): boolean {
  if (!email) return false;
  const at = masked.indexOf('@');
  const eat = email.indexOf('@');
  if (at <= 0 || eat <= 0) return false;
  if (masked.slice(at).toLowerCase() !== email.slice(eat).toLowerCase()) return false;
  const prefix = masked.slice(0, at).replace(/\*+$/, '');
  return email.slice(0, eat).toLowerCase().startsWith(prefix.toLowerCase());
}

export function headerVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  // `landing` and `startCrew` join login/reset as the bare-chrome screens: no
  // header, no bottom nav. Both are reachable while signed OUT, and `landing`
  // is also where a signed-in account with no crew ends up — where a header
  // naming a crew would have nothing to name.
  const bare =
    s.screen === 'login' ||
    s.screen === 'reset' ||
    // `recover` is bare for the same reason `login` is — its reader is by
    // definition locked out — and for one more: it is the screen an emailed
    // reset LANDS beside, so it must render identically whether or not a stale
    // session happens to be sitting in this browser's storage.
    s.screen === 'recover' ||
    s.screen === 'landing' ||
    s.screen === 'startCrew' ||
    // `join` is bare in the strongest sense of the rule: its reader may have no
    // account at all, and it is ALSO reachable while signed in — a member of
    // one crew invited to a second lands here — so a header naming their
    // current crew would be describing something the screen is not about.
    s.screen === 'join' ||
    // `ask` and `waiting` join them on the same rule (3056). `ask` matches
    // `join` exactly — its reader may have no account, and it is reachable
    // signed in too. `waiting` is bare for a sharper reason: its reader HAS an
    // account and is in no party at all, so every piece of chrome would be
    // naming or navigating to something they cannot reach yet.
    s.screen === 'ask' ||
    s.screen === 'waiting';
  const authed = !!me && !bare;
  const dg = s.screen === 'detail' ? s.gById(s.detailId) : null;
  const scr = s.screen === 'detail' && !dg ? 'discover' : s.screen;
  const cur = scr === 'detail' ? s.from : scr;
  const navCur = navCurOf(cur);
  const meC = chipOf(me);
  const newCount = newForMe(s.list, me, (id) => s.gById(id)).length;
  // MY GAMES' doors open FULL-SCREEN pages (DESIGN-CHANGELOG 2026-09-03 "The
  // page bar is navigation only"): no app header, no nav, no tab bar while one
  // is up — the page draws its own `◂ BACK TO MY GAMES` bar. Same shape as
  // `round` and `detail` below; listed here so both trees gate on one flag.
  const meAdminHdr = !!me && me.role === 'admin';
  // A player who reaches `manage` gets the PARTY page — the guard is on the
  // entry row AND here (AA phase 5 §K).
  const manageOk = scr === 'manage' && meAdminHdr;
  const fullPage =
    scr === 'interests' ||
    scr === 'goty' ||
    scr === 'backlog' ||
    scr === 'scores' ||
    scr === 'recs' ||
    scr === 'monitoring' ||
    manageOk ||
    scr === 'member';
  return {
    authed,
    scr,
    cur,
    /** the tab this screen lights — `mine` for its sub-pages */
    navCur,
    isMine: authed && scr === 'mine',
    isBacklog: authed && scr === 'backlog',
    /** MY SCORES — the ledger of every game you have scored, backlog or not (2026-09-12) */
    isScores: authed && scr === 'scores',
    isRecs: authed && scr === 'recs',
    isMonitoring: authed && scr === 'monitoring',
    isLogin: s.screen === 'login',
    isReset: s.screen === 'reset',
    isRecover: s.screen === 'recover',
    isLanding: s.screen === 'landing',
    isStartCrew: s.screen === 'startCrew',
    isJoin: s.screen === 'join',
    isAsk: s.screen === 'ask',
    isWaiting: s.screen === 'waiting',
    isDiscover: authed && scr === 'discover',
    isTonight: authed && scr === 'tonight',
    isInterests: authed && scr === 'interests',
    isGoty: authed && scr === 'goty',
    isPlaytests: authed && scr === 'playtests',
    isDetail: authed && scr === 'detail' && !!dg,
    isCrew: authed && (scr === 'crew' || (scr === 'manage' && !meAdminHdr)),
    isManage: authed && manageOk,
    isMember: authed && scr === 'member' && !!s.memK && !!s.user(s.memK),
    isProfile: authed && scr === 'profile',
    /**
     * GAME NIGHT is full screen (3039): while it is up both trees hide
     * their own chrome — `hdrOn` is what the header / nav / tab bar gate on,
     * not `authed`. A `round` screen with no round (it ended under us) falls
     * through to the board via `refreshRound`, so the guard here is belt and
     * braces.
     */
    isRound: authed && scr === 'round' && s.round !== null,
    /** SCHEDULE — the month (3071, DESIGN-CHANGELOG 2026-10-14). */
    isSchedule: authed && scr === 'schedule',
    /** One night of it: full screen like the round, its own `◂ SCHEDULE` bar. */
    isSchedDay: authed && scr === 'schedDay' && !!s.schDay,
    /**
     * GAME DETAIL is full screen on both trees too, and joins `round` in here
     * rather than being gated per-tree. Desktop landed first and spent its own
     * `!isDetail` in the shell precisely BECAUSE this flag is shared and mobile
     * still wanted its tab bar; once mobile wanted the same screen bare, the
     * shared flag became the honest place for it and the desktop special case
     * came back out.
     *
     * The two screens are the same shape: each hides the chrome and puts the
     * only way out — `◂ <where you came from>` — in a bar of its own, `Round`'s
     * on both trees and `Detail`'s alongside it. Anything gated on `hdrOn` is
     * therefore asserting "this screen has the app's chrome", which is the
     * question every caller was already asking.
     */
    hdrOn:
      authed &&
      !(scr === 'round' && s.round !== null) &&
      !(scr === 'detail' && !!dg) &&
      !(scr === 'schedDay' && !!s.schDay) &&
      !fullPage,
    tabItems: TABS.map<TabItem>((t) => ({
      key: t.s,
      l: t.l,
      d: t.d,
      vb: t.vb ?? '0 0 9 9',
      clr: navCur === t.s ? 'var(--acc)' : 'var(--gt-muted)',
      bd: navCur === t.s ? 'var(--acc)' : 'transparent',
    })),
    crewCount: '' + s.users.length,
    meIni: meC.ini,
    meHueBg: meC.hueBg,
    meHasAv: meC.hasAv,
    meAvBg: meC.avBg,
    newCount: '' + newCount,
    hasNew: newCount > 0,
    wnBd: newCount > 0 ? 'var(--gt-info)' : 'var(--gt-border-2)',
    wnClr: newCount > 0 ? 'var(--gt-text)' : 'var(--gt-muted)',
    backTxt:
      s.from === 'round'
        ? 'GAME NIGHT'
        : s.from === 'schedDay'
        ? 'SCHEDULE'
        : s.from === 'tonight'
        ? 'GAMES TONIGHT?'
        : s.from === 'goty'
          ? 'GOTY 2026'
          : s.from === 'interests'
            ? 'MY INTERESTS'
            : s.from === 'mine'
              ? 'MY GAMES'
              : s.from === 'backlog'
                ? 'BACKLOG'
                : s.from === 'scores'
                  ? 'MY SCORES'
                : s.from === 'recs'
                  ? 'RECOMMENDATIONS'
                  : s.from === 'monitoring'
                    ? 'MONITORING'
                    : s.from === 'member'
                      ? (s.user(s.memK)?.name ?? 'PLAYER').toUpperCase()
                      : 'DISCOVER',
    freshness: freshnessChip(s, now),
    toasts: s.toasts,
  };
}

// ---------------------------------------------------------------------------
// my games (DESIGN-CHANGELOG 2026-09-03; docs/porting/AA.md phase 1)
// ---------------------------------------------------------------------------

/** Which drawn 9×9 glyph a door wears (`components/DoorIcon.tsx`). */
export type DoorIcon = 'dia' | 'bell' | 'tri' | 'star' | 'hand' | 'party' | 'bars';

export interface MineDoor {
  key: string;
  icon: DoorIcon;
  /** the rail label (desktop) */
  t: string;
  /** the icon-strip label (phone) — at the 5.5px pixel floor, so short */
  short: string;
  screen: Screen;
  hasN: boolean;
  n: string;
  tip: string;
}

/**
 * A NOTICE BOARD row. Two sources in the design: party scores in the last 14
 * days (phase 2, here) and world events a trigger you armed let through
 * (phase 4). The row shape is fixed so the hub's markup is written once.
 */
export interface NoticeRow {
  key: string;
  id: GameId;
  /** epoch ms — what the board sorts by */
  at: number;
  /** lowercase age — `2d`, not `2D`, which read as twenty */
  w: string;
  n: string;
  t: string;
  tag: string;
  tagClr: string;
  tagBg: string;
  tagBs: 'solid' | 'dashed';
  tip: string;
  ini: string;
  fall: string;
  coverCands: ArtCand[];
}

/**
 * MY GAMES — the hub (`design/GT Mine.dc.html` · `GT Mobile Mine.dc.html`).
 *
 * THE DOORS ARE THE ONES THAT OPEN SOMEWHERE. The design draws five; phase 1
 * ships the two whose pages exist — MY INTERESTS and GAME OF THE YEAR — and
 * each later phase adds its own (BACKLOG · MONITORING · RECOMMENDATIONS). A
 * door with nowhere behind it would be a control that promises a page the app
 * does not have, which is the claim the copy rules forbid.
 *
 * TWO COUNTS IN THE RAIL. MY INTERESTS' — the party-list games you have not
 * called yet, the same queue the TO-DO panel and the Tonight nudge run
 * (`intQueue`), so the three numbers cannot disagree — and, since 2026-09-12,
 * MY SCORES' — how many games you have put a number on.
 */
export function mineVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const meC = chipOf(me);
  const meAdmin = !!me && me.role === 'admin';
  const uncalled = me ? intQueue(s.list, me.k, s.intSkips[me.k] ?? []).length : 0;
  const ctx = tileCtx(s);
  // MY SCORES' door carries its count (DESIGN-CHANGELOG 2026-09-12): every
  // game you have put a number on, backlog or not.
  const scoredN = me ? Object.keys(s.scores[me.k] ?? {}).length : 0;
  const doors: MineDoor[] = [
    { key: 'bk', icon: 'dia', t: 'BACKLOG', short: 'BACKLOG', screen: 'backlog', hasN: false, n: '', tip: '' },
    {
      key: 'sc',
      icon: 'bars',
      t: 'MY SCORES',
      short: 'SCORES',
      screen: 'scores',
      hasN: scoredN > 0,
      n: String(scoredN),
      tip: scoredN + (scoredN === 1 ? ' GAME' : ' GAMES') + ' SCORED',
    },
    { key: 'mon', icon: 'bell', t: 'MONITORING', short: 'MONITORING', screen: 'monitoring', hasN: false, n: '', tip: '' },
    {
      key: 'int',
      icon: 'tri',
      t: 'MY INTERESTS',
      short: 'INTERESTS',
      screen: 'interests',
      hasN: uncalled > 0,
      n: String(uncalled),
      tip: uncalled + (uncalled === 1 ? ' GAME' : ' GAMES') + ' WAITING ON YOUR CALL',
    },
    { key: 'goty', icon: 'star', t: 'GAME OF THE YEAR', short: 'GOTY', screen: 'goty', hasN: false, n: '', tip: '' },
    // No count on this door (design): MY INTERESTS keeps the rail's only one.
    { key: 'recs', icon: 'hand', t: 'RECOMMENDATIONS', short: 'RECS', screen: 'recs', hasN: false, n: '', tip: '' },
  ];
  // RECOMMENDED TO YOU — only what is still waiting on you (same-day revision
  // "carries only what is still waiting on you"): answer a card and it leaves;
  // the section hides when nothing is pending. `ALL ▸` opens the full ledger.
  // A card whose game the catalog cannot draw waits for `refreshMine`'s stray
  // fetch rather than drawing blank.
  const pend = me
    ? s.sends
        .filter((r) => r.to === me.k && r.st !== 'x' && r.st !== 'added' && !s.soloHas(r.gid))
        .sort((a, b) => b.at - a.at)
    : [];
  const recs: RecToYouCard[] = [];
  for (const r of pend) {
    const g = s.gById(r.gid);
    if (!g) continue;
    const t = tileVM(g, ctx);
    const from = chipOf(s.user(r.from));
    recs.push({
      key: r.id,
      id: r.id,
      gid: g.id,
      n: g.n,
      note: r.note,
      by: from.name.toUpperCase() + ' RECOMMENDED THIS · ' + ago(r.at, now),
      fromIni: from.ini,
      fromHueBg: from.hueBg,
      fromHasAv: from.hasAv,
      fromAvBg: from.avBg,
      ini: t.ini,
      fall: t.fall,
      hdrCands: t.hdrCands,
    });
  }
  // THE NOTICE BOARD reads `events` and nothing else (3063; Marcus 2026-09-04:
  // every row is persisted). PARTY rows — `Mike scored it 10.`, a re-score
  // with the previous value — are the trigger on game_scores' rows, never your
  // own; WORLD rows are the triggers you armed that fired, which the sweep
  // wrote for you alone, so there is no gate to apply here. Last 14 days,
  // newest first; no controls on a row, the click opens the game.
  const nb: NoticeRow[] = [];
  const cutoff = now - NOTICE_DAYS * 864e5;
  for (const e of s.board) {
    if (e.at < cutoff) continue;
    if (!me) break;
    if (e.kind === 'score' && (!e.actor || e.actor === me.k)) continue;
    if (e.kind !== 'score' && e.actor !== null) continue;
    const g = s.gById(e.gid);
    if (!g) continue;
    const t = tileVM(g, ctx);
    const world = e.kind !== 'score';
    const actorName = e.actor ? chipOf(s.user(e.actor)).name : '';
    nb.push({
      key: e.id,
      id: g.id,
      at: e.at,
      w: agoShort(e.at, now),
      n: g.n,
      t: eventText(e.kind, e.payload, actorName),
      tag: world ? TAG[e.kind as TriggerKey] : 'PARTY',
      tagClr: world ? tagClr(e.kind as TriggerKey) : 'var(--gt-text-4)',
      tagBg: world ? tagBg(e.kind as TriggerKey) : 'transparent',
      tagBs: 'solid',
      tip: world ? TR.find((x) => x.k === e.kind)?.full ?? '' : '',
      ini: t.ini,
      fall: t.fall,
      coverCands: t.coverCands,
    });
  }
  nb.sort((a, b) => b.at - a.at);
  return {
    meIni: meC.ini,
    meHueBg: meC.hueBg,
    meHasAv: meC.hasAv,
    meAvBg: meC.avBg,
    meName: (me ? me.name : '').toUpperCase(),
    /** `LEADER · PIZZA DOGS` — role and party, the rail's one line about you */
    meSubTxt: (meAdmin ? 'LEADER' : 'PLAYER') + ' · ' + (s.activeCrew?.name ?? '').toUpperCase(),
    /** the profile's STEAM ACCOUNT line, unchanged — one computation, two readers */
    steamTxt: profileVM(s, now).pfSteamTxt,
    doors,
    recs,
    hasRecs: recs.length > 0,
    nb,
    nbEmpty: nb.length === 0,
  };
}

/** A RECOMMENDED TO YOU card on the hub — one pending send. */
export interface RecToYouCard {
  key: string;
  /** the send's id — what ADD TO BACKLOG ▸ / NOT FOR ME answer */
  id: string;
  gid: GameId;
  n: string;
  note: string;
  /** `DAVE RECOMMENDED THIS · 1D AGO` */
  by: string;
  fromIni: string;
  fromHueBg: string;
  fromHasAv: boolean;
  fromAvBg: string;
  ini: string;
  fall: string;
  hdrCands: ArtCand[];
}

// ---------------------------------------------------------------------------
// RECOMMEND (the send modal / sheet) and RECOMMENDATIONS (the page)
// ---------------------------------------------------------------------------

/**
 * RECOMMEND — the send surface (`design/GT Mine.dc.html:476-528` / `GT Mobile
 * Mine.dc.html:466-513`, view model at 699-716). Any number of recipients +
 * ONE note. Each roster row peeks at what they already have, first match
 * wins: their score · their shelf · their call · your earlier send. The shelf
 * peek is the one the app CANNOT honour — 3060 made the backlog self-only and
 * 3062's header keeps it that way — so it is absent here: a recipient's shelf
 * is not the sender's to see.
 *
 * SEND is dead until both halves are set, and the footer says which is
 * missing. The prototype's hint for "nobody picked" is empty (`sd.hint`,
 * `GT Mine.dc.html:706` — the changelog's PICK WHO GETS IT never landed), and
 * the app follows the code.
 */
export function sendModalVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const id = s.sdId;
  const g = id ? s.gById(id) : null;
  const ctx = tileCtx(s);
  const t = g ? tileVM(g, ctx) : null;
  const entry = g ? s.list.find((e) => e.id === g.id) : undefined;
  const others = me ? s.users.filter((u) => u.k !== me.k) : [];
  const rows = others.map((u) => {
    const c = chipOf(u);
    const sc = g ? s.scoreOf(u.k, g.id) : null;
    const call = entry?.votes[u.k];
    const prior = me && g ? s.sends.find((r) => r.from === me.k && r.to === u.k && r.gid === g.id) : undefined;
    const peek =
      sc !== null
        ? 'SCORED IT ' + sc
        : call && isCall(call)
          ? 'CALLED ' + LVL[call].t
          : prior
            ? 'YOU SENT IT ' + ago(prior.at, now)
            : '';
    return {
      key: u.k,
      k: u.k,
      name: u.name.toUpperCase(),
      ini: c.ini,
      hueBg: c.hueBg,
      hasAv: c.hasAv,
      avBg: c.avBg,
      on: s.sdTo.includes(u.k),
      peek,
      hasPeek: peek !== '',
      peekClr: sc !== null ? 'var(--gt-text-5)' : 'var(--gt-muted-2)',
    };
  });
  const nTo = s.sdTo.length;
  const noteLen = s.sdNote.trim().length;
  const canSend = (nTo > 0 || s.sdEdit) && (noteLen > 0 || (s.sdEdit && nTo === 0));
  const allOn = others.length > 0 && others.every((u) => s.sdTo.includes(u.k));
  return {
    open: id !== 0 && !!g,
    id,
    title: s.sdEdit ? 'EDIT RECOMMENDATION' : 'RECOMMEND',
    n: g ? g.n : '',
    dev: g ? (g.dev || '').toUpperCase() : '',
    ini: t ? t.ini : '',
    fall: t ? t.fall : '',
    hdrCands: t ? t.hdrCands : [],
    rows,
    allOn,
    note: s.sdNote,
    notePh: 'The pitch, in your words. This is what they see.',
    noteMax: SEND_NOTE_MAX,
    canSend,
    sendL: s.sdEdit ? (nTo === 0 ? 'REVOKE ▸' : 'SAVE ▸') : nTo > 1 ? 'SEND TO ' + nTo + ' ▸' : 'SEND ▸',
    hint:
      s.sdEdit && nTo === 0
        ? 'NOBODY LEFT — SAVING REVOKES IT'
        : nTo === 0
          ? ''
          : noteLen === 0
            ? 'THE NOTE IS THE RECOMMENDATION — SAY WHY'
            : noteLen + ' CHARS',
    sendBd: canSend ? 'var(--acc)' : 'var(--gt-border-2)',
    sendClr: canSend ? 'var(--acc)' : 'var(--gt-ghost)',
    sendBg: canSend ? 'var(--accSoft)' : 'transparent',
    isRevoke: s.sdEdit && nTo === 0,
  };
}

/** One TO YOU row on the RECOMMENDATIONS page. */
export interface RecInRow {
  key: string;
  id: string;
  gid: GameId;
  n: string;
  note: string;
  by: string;
  fromIni: string;
  fromHueBg: string;
  fromHasAv: boolean;
  fromAvBg: string;
  /** `'x'` — the NOT FOR ME chip, and the row dims */
  dismissed: boolean;
  added: boolean;
  /** `NOT FOR ME` ↔ `BRING IT BACK` — only while not added */
  dismissL: string;
  op: number;
  ini: string;
  fall: string;
  hdrCands: ArtCand[];
}

/** One FROM YOU row — a (me, game) group with a chip per recipient. */
export interface RecOutRow {
  key: string;
  gid: GameId;
  n: string;
  note: string;
  by: string;
  who: Array<{
    key: string;
    name: string;
    ini: string;
    hueBg: string;
    hasAv: boolean;
    avBg: string;
    chip: string;
    chipClr: string;
    chipBs: 'solid' | 'dashed';
  }>;
  armed: boolean;
  ini: string;
  fall: string;
  hdrCands: ArtCand[];
}

/**
 * RECOMMENDATIONS — the page (`design/GT Mine.dc.html:183-265` / `GT Mobile
 * Mine.dc.html:177-259`). TO YOU is every send to me, newest first, dismissed
 * ones included and dimmed (the prototype computes `op` and never binds it;
 * the changelog says dimmed; the app binds it). FROM YOU is one row per game
 * I sent, grouped, newest group first, a status chip per recipient.
 *
 * "Added" on FROM YOU is the STORED answer only (3062's header): a recipient
 * who shelved it through the card's own control is `✓ ADDED`; one who shelved
 * it some other way stays `WAITING` — their shelf is not the sender's to see.
 * On TO YOU it is the reader's own shelf as well, which is theirs to see.
 */
export function recsVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const k = me ? me.k : '';
  const ctx = tileCtx(s);
  const rcIn: RecInRow[] = [];
  for (const r of s.sends.filter((x) => x.to === k).sort((a, b) => b.at - a.at)) {
    const g = s.gById(r.gid);
    if (!g) continue;
    const t = tileVM(g, ctx);
    const from = chipOf(s.user(r.from));
    const x = r.st === 'x';
    const added = r.st === 'added' || s.soloHas(r.gid);
    rcIn.push({
      key: r.id,
      id: r.id,
      gid: g.id,
      n: g.n,
      note: r.note,
      by: from.name.toUpperCase() + ' RECOMMENDED THIS · ' + ago(r.at, now),
      fromIni: from.ini,
      fromHueBg: from.hueBg,
      fromHasAv: from.hasAv,
      fromAvBg: from.avBg,
      dismissed: x,
      added,
      dismissL: x ? 'BRING IT BACK' : 'NOT FOR ME',
      op: x ? 0.5 : 1,
      ini: t.ini,
      fall: t.fall,
      hdrCands: t.hdrCands,
    });
  }
  const groups = new Map<GameId, SendRec[]>();
  for (const r of s.sends) {
    if (r.from !== k) continue;
    // A recipient who has since left the party is not a recipient any more.
    if (!s.user(r.to)) continue;
    (groups.get(r.gid) ?? groups.set(r.gid, []).get(r.gid)!).push(r);
  }
  const rcOut: RecOutRow[] = [];
  for (const [gid, rows] of groups) {
    const g = s.gById(gid);
    if (!g) continue;
    const t = tileVM(g, ctx);
    const newest = rows.reduce((a, b) => (b.at > a.at ? b : a));
    rcOut.push({
      key: 'out:' + gid,
      gid: g.id,
      n: g.n,
      note: newest.note,
      by: 'YOU RECOMMENDED THIS · ' + ago(newest.at, now) + ' · ' + rows.length + (rows.length === 1 ? ' PLAYER' : ' PLAYERS'),
      who: rows
        .slice()
        .sort((a, b) => a.at - b.at)
        .map((r) => {
          const c = chipOf(s.user(r.to));
          return {
            key: r.id,
            name: c.name.toUpperCase(),
            ini: c.ini,
            hueBg: c.hueBg,
            hasAv: c.hasAv,
            avBg: c.avBg,
            chip: r.st === 'added' ? '✓ ADDED' : r.st === 'x' ? 'NOT FOR ME' : 'WAITING',
            chipClr: r.st === 'added' ? 'var(--acc)' : r.st === 'x' ? 'var(--acc2)' : 'var(--gt-muted-2)',
            chipBs: r.st === '' ? 'dashed' : 'solid',
          };
        }),
      armed: s.rcArm === gid,
      ini: t.ini,
      fall: t.fall,
      hdrCands: t.hdrCands,
    });
  }
  rcOut.sort((a, b) => {
    const na = Math.max(...(groups.get(a.gid) ?? []).map((r) => r.at));
    const nb = Math.max(...(groups.get(b.gid) ?? []).map((r) => r.at));
    return nb - na;
  });
  const tabs = (['in', 'out'] as const).map((key) => {
    const on = s.rcTab === key;
    return {
      key,
      l: key === 'in' ? 'TO YOU · ' + rcIn.length : 'FROM YOU · ' + rcOut.length,
      bd: on ? 'var(--acc)' : 'var(--gt-border-2)',
      clr: on ? 'var(--acc)' : 'var(--gt-text-5)',
      bg: on ? 'var(--accSoft)' : 'transparent',
      z: on ? 1 : 0,
    };
  });
  return {
    tab: s.rcTab,
    tabs,
    rcIn,
    rcOut,
    inEmpty: s.rcTab === 'in' && rcIn.length === 0,
    outEmpty: s.rcTab === 'out' && rcOut.length === 0,
  };
}

/**
 * The desktop header's account menu (DESIGN-CHANGELOG 2026-09-03 "Account
 * chip"). The chip is a menu now: role · party and the STEAM line above two
 * actions, EDIT PROFILE and SIGN OUT, which used to sit in MY GAMES' rail and
 * before that on the profile page. The phone has no menu — its avatar opens
 * the profile screen, which carries both.
 */
export function accountMenuVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const meAdmin = !!me && me.role === 'admin';
  return {
    open: s.meOpen,
    meBd: s.meOpen ? 'var(--acc)' : 'var(--gt-border-2)',
    roleLine: (meAdmin ? 'LEADER' : 'PLAYER') + ' · ' + (s.activeCrew?.name ?? '').toUpperCase(),
    steamLine: 'STEAM · ' + profileVM(s, now).pfSteamTxt,
  };
}

/** The notice board's window, in days (DESIGN-CHANGELOG 2026-09-03). */
export const NOTICE_DAYS = 14;

/**
 * The board's age column: LOWERCASE and short — `2d`, not `2D`, which read as
 * twenty (same-day revision "the NOTICE BOARD rows … age column is lowercase").
 */
export function agoShort(at: number, now: number): string {
  const h = (now - at) / 36e5;
  return h < 1 ? 'now' : h < 24 ? Math.round(h) + 'h' : Math.round(h / 24) + 'd';
}

export interface BacklogRow extends TileVM {
  /** the score badge — `—` when unscored */
  scTxt: string;
  scClr: string;
  scLab: string;
  scTip: string;
  /** the ✕ — resting, or armed to `SURE?` */
  rmArmed: boolean;
  rmTxt: string;
  rmClr: string;
  /** the caption's watch line — `EA EXIT · UNDER $6`, or NOTHING WATCHED */
  w: string;
  wClr: string;
  /** the bell: solid + count when something is armed, hollow when nothing is */
  bellOn: boolean;
  bellN: string;
  bellBd: string;
  bellClr: string;
  bellTip: string;
}

/**
 * BACKLOG (`design/GT Mine.dc.html` · `GT Mobile Mine.dc.html`, the
 * `isBacklog` blocks; AA phase 2).
 *
 * The page's lens is the SHARED BOARD FILTER — its own `backlog` set, so
 * PLAYERS / STATUS / DISCOUNT / REVIEWS / OWNED BY / TAGS all narrow it — plus
 * a query of its own, `bkQ`. NOT `bq`: the design keeps the backlog out of the
 * shared board query on purpose ("it is not a board, so it stays out of `bq`").
 *
 * SHELF is everything; SCORED is the shelf's scored rows. WATCHING joins in
 * phase 4 with the bell, and so does the `N TRIGGERS ARMED` tail of the stat
 * line — a stat that names a feature the app does not have is a claim.
 */
export function backlogVM(s: QdStore) {
  const me = s.me();
  const k = me ? me.k : '';
  const ctx = tileCtx(s);
  const shelf = (s.backlog[k] ?? [])
    .map((e) => ({ e, g: s.gById(e.id) }))
    .filter((x): x is { e: BacklogEntry; g: Game } => !!x.g);
  const mine = s.scores[k] ?? {};
  const scoreOf = (id: GameId): number | null => mine[String(id)]?.v ?? null;
  const scoredN = shelf.filter((x) => scoreOf(x.g.id) !== null).length;
  const avg = scoredN
    ? (shelf.reduce((sum, x) => sum + (scoreOf(x.g.id) ?? 0), 0) / scoredN).toFixed(1)
    : '—';
  const plural = (n: number, one: string, many: string): string => n + ' ' + (n === 1 ? one : many);

  const armedOf = (g: Game): number => armedN(s.watch[String(g.id)], g);
  // `soloArmed` sums the SHELF's watches only — a game watched but not shelved
  // is on MONITORING, not here.
  const soloArmed = shelf.reduce((n, x) => n + armedOf(x.g), 0);
  // ALL / PLAYED / OWNED / UNOWNED (Marcus, 2026-09-15). STILL no SHELF /
  // SCORED / WATCHING — those were removed on his instruction (18261b5) and
  // have not come back; this is a different axis, asking what Steam says about
  // the member rather than what the shelf says about the game.
  //
  // `myLib` HAS THREE STATES AND ALL THREE ARE LOAD-BEARING (the field's
  // doc-comment): a number is owned, `null` is asked-and-unowned, ABSENT is
  // unasked. An unasked game is in none of the three narrow tabs — never in
  // UNOWNED, which is the tab a careless `!myLib[appid]` would quietly fill
  // with the member's whole shelf while the read is still in the air.
  //
  // WISHLISTED is the one tab NOT backed by `myLib`. Its read is whole-list
  // rather than per-appid — one call answers the entire wishlist — so `wishIds`
  // is a single nullable set instead of a map with a pending state per game.
  // `null` still means the same thing: we cannot claim to know, so the tab
  // shows nothing rather than reporting an empty wishlist.
  const tab = s.bkTab;
  const libOf = (g: Game): number | null | undefined => s.myLib[g.appid];
  const wish = s.wishIds;
  const tabPool =
    tab === 'played'
      ? shelf.filter((x) => (libOf(x.g) ?? 0) > 0)
      : tab === 'owned'
        ? shelf.filter((x) => typeof libOf(x.g) === 'number')
        : tab === 'wishlisted'
          ? (wish ? shelf.filter((x) => wish.has(x.g.appid)) : [])
          : tab === 'unowned'
            ? shelf.filter((x) => libOf(x.g) === null)
            : shelf;
  // What the three narrow tabs are still waiting to hear about. Drives the
  // pending copy below: a tab that is empty because nobody has answered yet
  // must not borrow the sentence meant for a tab that is genuinely empty.
  const libPending = shelf.some((x) => libOf(x.g) === undefined);
  // THROUGH `linkStateOf`, NOT `!!s.steamLink?.linked`, and the difference is
  // deviation P2-08 in this page's costume. `steamLink` is `null` until boot's
  // fetch lands and again after any read that failed, so the shorthand tells
  // the member they have not connected Steam — during the ordinary first second
  // of the page, about the one person who can see it is false. `pending` and
  // `unlinked` are different sentences and only the second is an instruction.
  const linkState = me ? linkStateOf(s, me) : 'pending';
  const flt = s.fltBy.backlog;
  const tl = tagLensOf(s, 'backlog');
  const pool = tabPool.filter((x) => passBoard(x.g, flt, tl, s.bkQ, s.pts, s.ownBy));
  const armed = s.bkArm;

  const rows: BacklogRow[] = pool.map((x) => {
    const sc = scoreOf(x.g.id);
    const isArmed = armed !== 0 && armed === x.g.id;
    const n = armedOf(x.g);
    const wl = watchLine(s.watch[String(x.g.id)], x.g);
    return {
      ...tileVM(x.g, ctx),
      scTxt: sc === null ? '—' : String(sc),
      scClr: scoreClr(sc),
      scLab: sc === null ? 'UNSCORED' : 'MY SCORE',
      scTip: sc === null ? 'SCORE IT — 0 TO 10' : 'CHANGE MY SCORE',
      rmArmed: isArmed,
      rmTxt: isArmed ? 'SURE?' : '✕',
      rmClr: isArmed ? 'var(--acc2)' : 'var(--gt-text-4)',
      w: wl || 'NOTHING WATCHED',
      wClr: wl ? 'var(--gt-text-4)' : 'var(--gt-ghost)',
      bellOn: n > 0,
      bellN: n > 0 ? String(n) : '',
      bellBd: n > 0 ? 'var(--acc)' : 'var(--gt-border-2)',
      bellClr: n > 0 ? 'var(--acc)' : 'var(--gt-text-4)',
      bellTip: n > 0 ? 'WATCHING ' + n + ' — CHANGE WHAT WAKES YOU' : 'NOTHING WATCHED — SET IT UP',
    };
  });
  const q = s.bkQ.trim();
  // The tab strip, dressed as the removed one was (`seg` below is `18261b5`'s,
  // restored unchanged) so the page keeps the segmented control the design drew
  // — only the labels behind it are new.
  const seg = (on: boolean) => ({
    bd: on ? 'var(--acc)' : 'var(--gt-border-2)',
    clr: on ? 'var(--acc)' : 'var(--gt-muted)',
    bg: on ? 'var(--accSoft)' : 'transparent',
    z: on ? 2 : 1,
  });
  // COUNTED OFF THE SHELF, NOT OFF `pool`. The number beside a tab says how big
  // that tab is, so it must not move when the search or the filter narrows what
  // is on screen — otherwise ALL reads `2` after typing a query and the strip
  // looks broken. The counts are omitted entirely while the read is pending
  // rather than shown as zeros, which is the same "absent is not an answer"
  // rule the pool above follows.
  const libN = (pred: (v: number | null | undefined) => boolean): string =>
    libPending ? '' : ' ' + shelf.filter((x) => pred(libOf(x.g))).length;
  // The wishlist's count has its own pending rule: it is whole-list, so it is
  // known the moment `wishIds` is non-null and unknowable until then — it does
  // not wait on `libPending` the way its neighbours do.
  const wishN = wish ? ' ' + shelf.filter((x) => wish.has(x.g.appid)).length : '';
  const bkTabs = (['all', 'played', 'owned', 'wishlisted', 'unowned'] as const).map((t) => ({
    key: t,
    l:
      t === 'all'
        ? 'ALL ' + shelf.length
        : t === 'played'
          ? 'PLAYED' + libN((v) => (v ?? 0) > 0)
          : t === 'owned'
            ? 'OWNED' + libN((v) => typeof v === 'number')
            : t === 'wishlisted'
              ? 'WISHLISTED' + wishN
              : 'UNOWNED' + libN((v) => v === null),
    /**
     * The phone's label. IDENTICAL EXCEPT FOR `wishlisted`, and that one is
     * arithmetic rather than taste: five tabs share a 360px phone's 332px of
     * content, so each cell is ~66px, and `WISHLISTED 3` needs ~72px at the
     * strip's 10px mono. It would clip — the strip is `nowrap` — so the phone
     * says WISHLIST, which fits with room to spare. The desktop strip is a
     * flex row in a 24px-padded page and has no such bound, so it keeps the
     * word Marcus asked for.
     */
    lS: t === 'wishlisted' ? 'WISHLIST' + wishN : '',
    ...seg(tab === t),
  }));
  /**
   * Why a narrow tab is empty, in the member's terms — three different reasons
   * and only the last one is about the games.
   *
   * THE ORDER IS THE PRIORITY ORDER. Unlinked comes first because it is the one
   * the member can act on and the one that makes the other two unreachable;
   * pending comes before the real answer because a tab that has not been told
   * anything yet must not claim to have looked. Collapsing any of these into
   * "nothing matches" would be the same fabrication `ensureMyLibrary` declines
   * to cache — it would just be spelled on screen instead of in the store.
   */
  const tabWhy =
    tab === 'all'
      ? ''
      : linkState === 'unlinked'
        ? 'LINK STEAM TO SORT YOUR BACKLOG BY WHAT YOU OWN — PLAYTESTS ▸ STEAM'
        : tab === 'wishlisted'
          ? // WISHLISTED reads its own source, so it has its own pending ladder
            // and must not borrow the library's: the two land independently,
            // and saying READING YOUR STEAM LIBRARY here would name the wrong
            // request. `wishClosed` comes FIRST among the answers because it is
            // an answer — Steam replied — and it is the one state where the copy
            // has to own up to two possibilities it cannot separate (§7.1a).
            linkState === 'pending' || (!wish && !s.wishClosed)
            ? 'READING YOUR STEAM WISHLIST…'
            : s.wishClosed
              ? "YOUR STEAM WISHLIST IS PRIVATE — OR EMPTY. STEAM ANSWERS THE SAME EITHER WAY. MAKE IT PUBLIC IN PROFILE ▸ PRIVACY."
              : ''
          : linkState === 'pending' || libPending
            ? 'READING YOUR STEAM LIBRARY…'
            : '';
  // The wishlist import's pill, dressed exactly like GOTY's `⟳ AUTO-FILL FROM
  // STEAM` (`gotyVM.steam*`). One difference in the gate: GOTY dims unless the
  // profile is PUBLIC, this dims only when Steam is UNLINKED — `wishImport`
  // asks Steam regardless of the last-seen privacy flag, because that flag is
  // the library sync's and can be stale either way.
  const canWish = !!(s.steamLink && s.steamLink.linked);
  const wishBusy = s.wishBusy;
  return {
    wishLbl: wishBusy ? '⟳ READING YOUR STEAM WISHLIST…' : '⟳ PULL IN YOUR STEAM WISHLIST',
    wishBd: canWish ? 'var(--acc)' : 'var(--gt-border-2)',
    // Dimmed while busy, so the button reads as "working" rather than "ready".
    wishClr: wishBusy ? 'var(--gt-muted)' : canWish ? 'var(--acc)' : 'var(--gt-faint)',
    wishBusy,
    /** the phone's sub-line and the desktop's `data-tip`, one string for both */
    wishSub: wishBusy
      ? 'ASKING STEAM WHAT YOU WISHLISTED — THIS TAKES A FEW SECONDS'
      : canWish
        ? 'ADDS YOUR STEAM WISHLIST TO YOUR BACKLOG — GAMES ALREADY HERE ARE SKIPPED'
        : 'LINK STEAM TO PULL IN YOUR WISHLIST',
    bkSub:
      plural(shelf.length, 'GAME', 'GAMES') +
      ' · ' +
      scoredN +
      ' SCORED · AVG ' +
      avg +
      ' · ' +
      plural(soloArmed, 'TRIGGER', 'TRIGGERS') +
      ' ARMED',
    bkQ: s.bkQ,
    /** ALL / PLAYED / OWNED / UNOWNED, each with its count off the whole shelf */
    bkTabs,
    hasBkNote: q.length > 0 && tabPool.length > 0,
    bkNote: pool.length + ' OF ' + tabPool.length + ' ON YOUR BACKLOG',
    bkRows: rows,
    /**
     * Nothing on screen, and the shelf is not empty — so the page SAYS why
     * rather than looking broken. Three causes now share this slot: the tab
     * has nothing in it, the search/filter cleared it, or (the two `tabWhy`
     * cases) the library read cannot answer yet.
     */
    bkNarrowed: pool.length === 0 && shelf.length > 0,
    bkNarrowTxt:
      tabWhy ||
      (tabPool.length === 0
        ? tab === 'played'
          ? 'NOTHING ON YOUR BACKLOG IS IN YOUR STEAM PLAYTIME — TRY OWNED, OR ALL'
          : tab === 'owned'
            ? "NOTHING ON YOUR BACKLOG IS IN YOUR STEAM LIBRARY YET — THAT'S WHAT A BACKLOG IS FOR"
            : tab === 'wishlisted'
              ? 'NOTHING ON YOUR BACKLOG IS ON YOUR STEAM WISHLIST — ⟳ ABOVE PULLS THE WISHLIST IN'
              : tab === 'unowned'
                ? 'YOU OWN EVERYTHING ON YOUR BACKLOG — TIME TO PLAY THEM'
                : 'NOTHING ON YOUR BACKLOG MATCHES THAT — CLEAR THE SEARCH OR TRY FEWER FILTERS'
        : 'NOTHING ON YOUR BACKLOG MATCHES THAT — CLEAR THE SEARCH OR TRY FEWER FILTERS'),
    /**
     * The dashed TRACK A SOLO GAME tile ends the grid (desktop) — on ALL only.
     * On a narrowed tab it would invite adding a game that the tab would then
     * refuse to show, because what puts a game in OWNED is Steam, not the shelf.
     */
    bkAddTile: tab === 'all',
    shelfN: shelf.length,
    /**
     * Every appid on the shelf — what both screens hand `ensureMyLibrary`.
     *
     * THE WHOLE SHELF, NOT `pool`, and that is the difference between tabs that
     * fill in and tabs that never do: asking only about what is on screen means
     * OWNED asks about the games it already decided are owned, so a game it has
     * not heard about can never join. A game with no appid (a locally minted
     * row) is dropped — `steam_owned_apps` is keyed by appid and has nothing to
     * say about it, which leaves it honestly absent rather than in UNOWNED.
     */
    bkAppids: shelf.map((x) => x.g.appid).filter((a) => a > 0),
    /* the phone's `+ FILTERS` trigger and chip rail — the same fields every
       board screen carries (`filterButton`), read from the current screen's
       set, which on this page is `backlog`'s */
    ...filterButton(s),
  };
}

/** One tile on MY SCORES — a scored game, backlog or not. */
export interface ScoreRow extends TileVM {
  /** the badge — the number, in its own colour; click = re-score */
  scTxt: string;
  scClr: string;
  scTip: string;
  /** `WAS 6 · 2D AGO` — the previous number when there was one, then the age */
  when: string;
  /** `◈` — also on your backlog (desktop marks it; the phone does not) */
  srcShow: boolean;
}

/**
 * MY SCORES (DESIGN-CHANGELOG 2026-09-12 "Scoring is not a backlog privilege";
 * `design/GT Mine.dc.html` / `GT Mobile Mine.dc.html`, the `isScores` block).
 *
 * THE LEDGER OF EVERYTHING YOU SCORED, backlog or not. A score is a PLAYED
 * axis on ANY game — `scores[me]` always admitted any id, and this is the page
 * that shows the result. Tiles are 2:3 cover art sorted high → low, ties on
 * recency; the badge is the score control (the same YOUR SCORE modal), and
 * `WAS 6 · 2D AGO` under the name says what a re-score changed (`prev`, 3067).
 * The stat line is `N GAMES SCORED · AVG 8.3 · HIGH 10`; its own search
 * (`rtQ`) and the shared `+ FILTERS`, exactly as BACKLOG carries them.
 *
 * NOT MY RATINGS: `RATING` already means the Steam review score in this app
 * (`SORT: RATING`, `VERY POSITIVE 92%`); the personal number has been SCORE
 * everywhere since it shipped.
 */
export function scoresVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const k = me ? me.k : '';
  const ctx = tileCtx(s);
  const shelf = new Set((s.backlog[k] ?? []).map((e) => String(e.id)));
  const all = Object.entries(s.scores[k] ?? {})
    .map(([id, r]) => ({ g: s.gById(Number(id) as GameId), r }))
    .filter((x): x is { g: Game; r: ScoreRec } => !!x.g);
  const flt = s.fltBy.scores;
  const tl = tagLensOf(s, 'scores');
  const pool = all.filter((x) => passBoard(x.g, flt, tl, s.rtQ, s.pts, s.ownBy));
  const rows: ScoreRow[] = pool
    .slice()
    .sort((a, b) => b.r.v - a.r.v || b.r.at - a.r.at)
    .map((x) => {
      const age = ago(x.r.at, now);
      // `WAS 6 · 2D AGO` — the previous number leads when there is one
      const was = x.r.prev === undefined ? '' : 'WAS ' + x.r.prev + ' · ';
      return {
        ...tileVM(x.g, ctx),
        scTxt: String(x.r.v),
        scClr: scoreClr(x.r.v),
        scTip: 'CHANGE MY SCORE — 0 TO 10',
        when: was + age,
        srcShow: shelf.has(String(x.g.id)),
      };
    });
  const avg = all.length ? (all.reduce((sum, x) => sum + x.r.v, 0) / all.length).toFixed(1) : '—';
  const high = all.length ? Math.max(...all.map((x) => x.r.v)) : null;
  const plural = (n: number, one: string, many: string): string => n + ' ' + (n === 1 ? one : many);
  const q = s.rtQ.trim();
  return {
    rtSub: plural(all.length, 'GAME', 'GAMES') + ' SCORED · AVG ' + avg + (high === null ? '' : ' · HIGH ' + high),
    rtNoteLine: 'A SCORE IS WHAT YOU PLAYED · THE PARTY SEES IT',
    rtQ: s.rtQ,
    hasRtNote: q.length > 0 && all.length > 0,
    rtNote: pool.length + ' OF ' + all.length + ' SCORED',
    rtRows: rows,
    /** narrowed to nothing by the search or the filter — says so, never looks empty */
    rtNarrowed: pool.length === 0 && all.length > 0,
    rtNarrowTxt: 'NOTHING YOU SCORED MATCHES THAT — CLEAR THE SEARCH OR TRY FEWER FILTERS',
    rtEmpty: all.length === 0,
    rtEmptyTxt: 'NOTHING SCORED YET — OPEN ANY GAME AND PUT A NUMBER ON IT',
    scoredN: all.length,
    ...filterButton(s),
  };
}

/**
 * YOUR SCORE (`design/GT Mine.dc.html`, `scShow`). 0–10 cells, one tap sets
 * and closes; `✕ CLEAR MY SCORE` when one is set. `null` when nothing is open.
 */
export function scoreModalVM(s: QdStore) {
  const me = s.me();
  const g = s.scId ? s.gById(s.scId) : null;
  if (!g || !me) return null;
  const mine = s.scores[me.k]?.[String(g.id)]?.v ?? null;
  const t = tileVM(g, tileCtx(s));
  return {
    id: g.id,
    name: g.n,
    dev: (g.dev || '').toUpperCase(),
    ini: t.ini,
    fall: t.fall,
    hdrCands: t.hdrCands,
    hasScore: mine !== null,
    cells: Array.from({ length: 11 }, (_, v) => {
      const on = mine === v;
      const clr = scoreClr(v);
      return {
        key: 'c' + v,
        v,
        label: String(v),
        bd: on ? clr : 'var(--gt-border-2)',
        bg: on ? clr : 'transparent',
        fg: on ? 'var(--gt-ink)' : 'var(--gt-text-5)',
      };
    }),
  };
}

/** How many catalog rows the TRACK A GAME picker lists at once. */
export const TRACK_ROWS = 14;

/**
 * TRACK A GAME (`design/GT Mine.dc.html`, `trkShow`): a Search Box, `TRACK ▸`
 * per row, `✓ ON IT` once shelved. Stays open for a second pick.
 *
 * THE PICKER IS A STEAM SEARCH (Marcus, 2026-09-04) — the app moved first
 * here; the prototype's picker lists the catalog. Two populations in one
 * list: the catalog's own matches lead (the party's vocabulary, instant,
 * there even when Steam is not), then Steam's page-0 answer from
 * `slices/mine.ts#trkSearch`, minus any appid the catalog half already showed.
 * With nothing typed the list is the catalog's first fourteen, as before —
 * Steam is not asked a question that has no term. `note` says what the Steam
 * half is doing in the picker's own words, so an empty list under a two-letter
 * term is never mistaken for "no such game".
 */
export function trackModalVM(s: QdStore) {
  const me = s.me();
  const k = me ? me.k : '';
  const have = new Set((s.backlog[k] ?? []).map((e) => e.id));
  const ctx = tileCtx(s);
  const term = norm(s.trkQ);
  const row = (g: Game) => {
    const t = tileVM(g, ctx);
    return {
      key: '' + g.id,
      id: g.id,
      n: g.n,
      meta: (g.dev || '').toUpperCase() + ' · ' + fmt$(g),
      ini: t.ini,
      fall: t.fall,
      hdrCands: t.hdrCands,
      has: have.has(g.id),
    };
  };
  const local = s.catalog
    .filter((g) => !g.live && (!term || norm(g.n).includes(term) || norm(g.dev).includes(term)))
    .sort((a, b) => a.n.localeCompare(b.n))
    .slice(0, TRACK_ROWS);
  const seen = new Set(local.map((g) => g.id));
  const steam = s.trkRows.filter((g) => !seen.has(g.id));
  const rows = [...local.map(row), ...steam.map(row)];
  const busy = s.trkState === 'busy';
  const note =
    s.trkState === 'busy'
      ? 'SEARCHING STEAM…'
      : s.trkState === 'err'
        ? 'STEAM SEARCH OFFLINE · CATALOG ONLY'
        : s.trkState === 'ok' && steam.length > 0
          ? steam.length + ' FROM STEAM'
          : '';
  return {
    open: s.trkOpen,
    q: s.trkQ,
    rows,
    busy,
    note,
    hasNote: note !== '',
    // Nothing to show AND nothing still coming — a busy picker is not empty.
    empty: rows.length === 0 && !busy,
    emptyTxt: s.trkState === 'err' ? 'NOTHING IN THE CATALOG MATCHES THAT' : 'NOTHING ON STEAM MATCHES THAT',
  };
}

/**
 * CALL IT (AA phase 5 §H; `design/GT Mine.dc.html:419-437` / `GT Mobile
 * Mine.dc.html:409-430`): the game's name, the one S/A/B/C strip the board
 * uses, and `SKIP — CALL IT LATER`. A call is offered, never forced. It
 * writes the tonight-list vote, nothing else.
 */
export function callItVM(s: QdStore) {
  const g = s.askId ? s.gById(s.askId) : null;
  return {
    open: s.askId !== 0 && !!g,
    id: s.askId,
    n: g ? g.n : '',
    strap: 'ON THE LIST — HOW BADLY DO YOU WANT IT?',
    cells: CALLS.map((l) => ({ key: l, l, g: LVL[l].g, t: LVL[l].t, clr: LVL[l].c })),
    skipL: 'SKIP — CALL IT LATER',
  };
}

/**
 * ADD IT (docs/ADD-IT.md): Discover's `+` asks where the game goes. Two rows —
 * `GAMES TONIGHT?` (the party's list, everyone sees it) and `MY BACKLOG` (yours
 * alone). A row the game is already on is a FACT, not a button: it reads
 * `✓ ON …` and does nothing; removal stays where it lives. Both rows done
 * still opens — it never refuses.
 */
export interface AddItRowVM {
  key: 'tonight' | 'backlog';
  icon: 'party' | 'dia';
  l: string;
  gloss: string;
  /** `ADD ▸` while the row can act; `✓ ON …` once it is a fact. */
  state: string;
  done: boolean;
}

export function addItVM(s: QdStore) {
  const g = s.addId ? s.gById(s.addId) : null;
  const inL = !!g && s.list.some((e) => e.id === g.id);
  const inB = !!g && s.soloHas(g.id);
  const rows: AddItRowVM[] = [
    {
      key: 'tonight',
      icon: 'party',
      l: 'GAMES TONIGHT?',
      gloss: "THE PARTY'S LIST — EVERYONE SEES IT",
      state: inL ? "✓ ON TONIGHT'S LIST" : 'ADD ▸',
      done: inL,
    },
    {
      key: 'backlog',
      icon: 'dia',
      l: 'MY BACKLOG',
      gloss: 'YOURS ALONE — NOBODY ELSE SEES IT',
      state: inB ? '✓ ON YOUR BACKLOG' : 'ADD ▸',
      done: inB,
    },
  ];
  return {
    open: s.addId !== 0 && !!g,
    id: s.addId,
    n: g ? g.n : '',
    strap: 'WHERE DOES IT GO?',
    rows,
  };
}

// ---------------------------------------------------------------------------
// TELL ME WHEN and MONITORING (AA phase 4; 3063)
// ---------------------------------------------------------------------------

/**
 * TELL ME WHEN (`design/GT Mine.dc.html:355-418` / `GT Mobile Mine.dc.html:
 * 348-410`). Seven rows in the matrix's order; a row the game cannot carry is
 * dashed, greyed and SAYS WHY — only facts about the game rule a trigger out.
 * ANY SALE wears THE NOISY ONE when on; the PRICE row carries the `− $N +`
 * floor. `ALSO PUT IT ON MY BACKLOG` is offered only when the game is not
 * shelved — a watch is not a list membership. Edits the draft; SAVE commits.
 */
export function tellMeWhenVM(s: QdStore) {
  const g = s.wmId ? s.gById(s.wmId) : null;
  const d = s.wmDraft;
  const t = g ? tileVM(g, tileCtx(s)) : null;
  const rows = TR.map((tr) => {
    const na = g ? trNa(g, tr.k) : '';
    const on = na === '' && d[tr.k];
    return {
      key: tr.k,
      k: tr.k,
      l: tr.full,
      sub: na || tr.sub,
      on,
      na: na !== '',
      hasWarn: tr.k === 'sale' && on,
      isPrice: tr.k === 'price',
      rowBg: on ? 'var(--accSoft)' : 'transparent',
      labClr: na ? 'var(--gt-ghost)' : on ? 'var(--acc)' : 'var(--gt-text)',
      subClr: na ? 'var(--gt-ghost-2)' : 'var(--gt-faint)',
      boxBd: na ? 'var(--gt-border-2)' : on ? 'var(--acc)' : 'var(--gt-border-2)',
      boxBs: na ? 'dashed' : 'solid',
      cur: na ? 'not-allowed' : 'pointer',
    };
  });
  const armed = g ? armedKeys(d, g).length : 0;
  return {
    open: s.wmId !== 0 && !!g,
    id: s.wmId,
    n: g ? g.n : '',
    dev: g ? (g.dev || '').toUpperCase() : '',
    ini: t ? t.ini : '',
    fall: t ? t.fall : '',
    hdrCands: t ? t.hdrCands : [],
    stateChip: g ? (g.ea ? 'EARLY ACCESS' : g.up ? 'UNRELEASED' : '') : '',
    priceChip: g ? fmt$(g) : '',
    rows,
    cap: d.cap,
    capTxt: '$' + d.cap,
    capPct: g ? capPct(g, d.cap) : '',
    hasCapPct: !!g && capPct(g, d.cap) !== '',
    alsoShown: !!g && !s.soloHas(g.id),
    also: s.wmAlso,
    armed,
    saveL: 'SAVE ▸',
  };
}

/** One MONITORING row — a watched game and its seven lamps. */
export interface MonRow {
  key: string;
  id: GameId;
  n: string;
  sub: string;
  src: 'backlog' | 'party' | 'neither';
  srcL: string;
  srcClr: string;
  srcBs: 'solid' | 'dashed';
  ini: string;
  fall: string;
  coverCands: ArtCand[];
  cells: Array<{
    key: string;
    k: TriggerKey;
    on: boolean;
    na: boolean;
    tip: string;
    isPrice: boolean;
    capTxt: string;
    capPct: string;
  }>;
  cap: number;
  capPct: string;
  capRowTxt: string;
  bellOn: boolean;
  bellN: string;
  bellTip: string;
}

/**
 * MONITORING (`design/GT Mine.dc.html:266-347` / `GT Mobile Mine.dc.html:
 * 262-343`): derived, never curated — every game with a trigger armed, in the
 * order the page was entered with (`monPin`, so a row you just silenced stays
 * until you leave), one lamp per trigger, the PRICE cell an in-place stepper.
 * No source FILTER: the prototype computes one and never renders it, and the
 * changelog says the `EVERYTHING / BACKLOG / PARTY / NEITHER` filter was
 * removed (Marcus, 2026-09-04: "the design doesn't have the monitoring view
 * filters"). The source CHIP on each row stays — that one is drawn.
 * WHAT FIRED is the board's WORLD rows, last 14 days — written by the
 * watcher, so the header says so and nothing here is a fixture.
 */
export function monitoringVM(s: QdStore, now: number = Date.now()) {
  const ctx = tileCtx(s);
  const me = s.me();
  const k = me ? me.k : '';
  const onList = new Set(s.list.map((e) => String(e.id)));
  const shelf = new Set((s.backlog[k] ?? []).map((e) => String(e.id)));
  const watchedIds = Object.keys(s.watch).map((x) => Number(x) as GameId);
  const ids = [...new Set([...s.monPin, ...watchedIds])];
  const all = ids
    .map((id) => ({ id, g: s.gById(id) }))
    .filter((x): x is { id: GameId; g: Game } => !!x.g);
  const term = norm(s.monQ);
  const src = (g: Game): 'backlog' | 'party' | 'neither' =>
    shelf.has(String(g.id)) ? 'backlog' : onList.has(String(g.id)) ? 'party' : 'neither';
  const rows: MonRow[] = all
    .filter((x) => !term || norm(x.g.n).includes(term) || norm(x.g.dev).includes(term))
    .map((x) => {
      const g = x.g;
      const rec = s.watch[String(g.id)];
      const t = tileVM(g, ctx);
      const sk = src(g);
      const n = armedN(rec, g);
      const pct = rec ? capPct(g, rec.cap) : '';
      return {
        key: '' + g.id,
        id: g.id,
        n: g.n,
        sub: (g.ea ? 'EARLY ACCESS' : g.up ? 'UNRELEASED' : 'RELEASED') + ' · ' + fmt$(g),
        src: sk,
        srcL: sk === 'backlog' ? '◈ BACKLOG' : sk === 'party' ? '▦ PARTY' : '◇ NEITHER',
        srcClr: sk === 'neither' ? 'var(--gt-muted-2)' : 'var(--gt-text-4)',
        srcBs: sk === 'neither' ? 'dashed' : 'solid',
        ini: t.ini,
        fall: t.fall,
        coverCands: t.coverCands,
        cells: TR.map((tr) => {
          const na = trNa(g, tr.k);
          const on = na === '' && !!rec?.[tr.k];
          return {
            key: tr.k,
            k: tr.k,
            on,
            na: na !== '',
            tip:
              tr.k === 'price' && on && rec
                ? 'FLOOR $' + rec.cap + ' · LIST ' + fmt$(g) + ' · CLICK TO STOP WATCHING THE PRICE'
                : na || (on ? 'ON — ' : 'OFF — ') + tr.full,
            isPrice: tr.k === 'price',
            capTxt: on && rec && tr.k === 'price' ? '$' + rec.cap : '$',
            capPct: pct,
          };
        }),
        cap: rec ? rec.cap : 0,
        capPct: pct,
        capRowTxt: 'PRICE FLOOR · LIST ' + fmt$(g) + (pct ? ' · ' + pct + ' OFF' : ''),
        bellOn: n > 0,
        bellN: n > 0 ? String(n) : '',
        bellTip: 'OPEN TELL ME WHEN FOR THIS GAME',
      };
    });
  const live = all.filter((x) => armedN(s.watch[String(x.g.id)], x.g) > 0).length;
  const armedTotal = all.reduce((n, x) => n + armedN(s.watch[String(x.g.id)], x.g), 0);
  const plural = (n: number, one: string, many: string): string => n + ' ' + (n === 1 ? one : many);
  const cols = TR.map((tr) => {
    const nOn = all.filter((x) => trNa(x.g, tr.k) === '' && !!s.watch[String(x.g.id)]?.[tr.k]).length;
    return {
      key: tr.k,
      k: tr.k,
      l: tr.l,
      nOn: nOn + ' ON',
      anyOn: nOn > 0,
      clr: nOn > 0 ? 'var(--acc)' : 'var(--gt-text-5)',
      tip: tr.full + ' — CLICK TO ARM IT ON EVERY ROW THAT CAN USE IT',
    };
  });
  // WHAT FIRED: the WORLD rows, last 14 days, newest first.
  const cutoff = now - NOTICE_DAYS * 864e5;
  const fired = s.board
    .filter((e) => e.kind !== 'score' && e.actor === null && e.at >= cutoff)
    .sort((a, b) => b.at - a.at)
    .map((e) => {
      const g = s.gById(e.gid);
      if (!g) return null;
      const kind = e.kind as TriggerKey;
      return {
        key: e.id,
        id: g.id,
        n: g.n,
        t: eventText(kind, e.payload),
        w: agoShort(e.at, now),
        tag: TAG[kind],
        tagClr: tagClr(kind),
        tagBg: tagBg(kind),
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
  return {
    monStat: plural(live, 'GAME', 'GAMES') + ' · ' + plural(armedTotal, 'TRIGGER', 'TRIGGERS') + ' ARMED',
    monQ: s.monQ,
    monCountTxt: rows.length === all.length ? plural(rows.length, 'ROW', 'ROWS') : rows.length + ' OF ' + all.length + ' ROWS',
    cols,
    rows,
    empty: rows.length === 0,
    emptyTxt:
      all.length > 0
        ? 'NO MONITORED GAME MATCHES THAT — ' + plural(live, 'IS', 'ARE').replace(/^(\d+) /, '$1 ') + ' STILL BEING WATCHED'
        : 'NOTHING WATCHED YET — THE BELL ON ANY GAME STARTS ONE',
    fired,
    firedEmpty: fired.length === 0,
    firedSub: 'LAST 14 DAYS',
    firedTip: 'THE WATCHER JOB WRITES THESE EVERY NIGHT',
    firedEmptyTxt: 'NOTHING YOU WATCH HAS FIRED IN 14 DAYS',
  };
}

// ---------------------------------------------------------------------------
// A player's page (AA phase 5 §J; 3064)
// ---------------------------------------------------------------------------

/**
 * A player's page (`design/GT Crew.dc.html:371-433` / `GT Mobile
 * Crew.dc.html:364-423`): avatar, name, role chip, a tab strip — RATINGS and
 * one per GOTY year they have a list for — then WHAT THEY PLAYED (their
 * scores as 2:3 covers, high to low, the badge on the art) or THEIR GOTY
 * (their tier rows as cover chips). Their S/A/B/C calls are NOT here: the
 * shared board answers that by filtering on a player. Nothing is editable.
 */
export function memberVM(s: QdStore) {
  const u = s.memK ? s.user(s.memK) : null;
  const c = chipOf(u);
  const ctx = tileCtx(s);
  const years = (s.memGoty[s.memK] ?? []).filter((y) => y.potentials.length > 0);
  const tab = s.memTab === 'sc' || years.some((y) => 'y' + y.year === s.memTab) ? s.memTab : 'sc';
  const scores = Object.entries(s.scores[s.memK] ?? {})
    .map(([id, rec]) => ({ id: Number(id) as GameId, v: rec.v, g: s.gById(Number(id)) }))
    .filter((x): x is { id: GameId; v: number; g: Game } => !!x.g)
    .sort((a, b) => b.v - a.v);
  const scoreRows = scores.map((x) => {
    const t = tileVM(x.g, ctx);
    return { key: 'sc' + x.id, id: x.id, n: x.g.n, scTxt: String(x.v), scClr: scoreClr(x.v), ini: t.ini, fall: t.fall, coverCands: t.coverCands };
  });
  const yr = years.find((y) => 'y' + y.year === tab);
  const TIER_CLR: Record<string, string> = { S: 'var(--acc)', A: 'var(--gt-text)', B: 'var(--gt-text-5)', X: 'var(--acc2)' };
  const memTiers = yr
    ? TIERS.map((t) => {
        const ids = yr.tierOrder.filter((id) => yr.tiers[String(id)] === t);
        const chips = ids
          .map((id) => ({ id, g: s.gById(id) }))
          .filter((x): x is { id: GameId; g: Game } => !!x.g)
          .map((x) => {
            const tv = tileVM(x.g, ctx);
            return { key: 'g' + x.id, id: x.id, n: x.g.n, ini: tv.ini, fall: tv.fall, coverCands: tv.coverCands };
          });
        return { key: 't' + t, t, clr: TIER_CLR[t] ?? 'var(--gt-muted-2)', chips, has: chips.length > 0 };
      }).filter((row) => row.has)
    : [];
  const avg = scores.length ? (scores.reduce((n, x) => n + x.v, 0) / scores.length).toFixed(1) : '—';
  const tabs = [
    { key: 'sc', l: 'RATINGS', on: tab === 'sc' },
    ...years.map((y) => ({ key: 'y' + y.year, l: 'GOTY ' + y.year, on: tab === 'y' + y.year })),
  ].map((t) => ({
    ...t,
    bd: t.on ? 'var(--acc)' : 'var(--gt-border-2)',
    clr: t.on ? 'var(--acc)' : 'var(--gt-text-5)',
    bg: t.on ? 'var(--accSoft)' : 'transparent',
    bdB: t.on ? 'var(--acc)' : 'transparent',
  }));
  return {
    open: !!u,
    k: s.memK,
    name: c.name,
    nameUp: c.name.toUpperCase(),
    ini: c.ini,
    hueBg: c.hueBg,
    hasAv: c.hasAv,
    avBg: c.avBg,
    roleLbl: u && u.role === 'admin' ? 'LEADER' : 'PLAYER',
    roleClr: u && u.role === 'admin' ? 'var(--gt-gold)' : 'var(--gt-muted)',
    statTxt:
      scores.length +
      (scores.length === 1 ? ' SCORE' : ' SCORES') +
      ' · AVG ' +
      avg +
      (years.length ? ' · GOTY ' + years.map((y) => y.year).join(' · ') : ''),
    tabs,
    showScores: tab === 'sc',
    showGoty: tab !== 'sc',
    scoreRows,
    scoresEmpty: scoreRows.length === 0,
    scoresEmptyTxt: 'NOTHING SCORED YET — SCORES SHOW UP HERE ONCE THEY RATE SOMETHING THEY PLAYED',
    memTiers,
    gotyEmpty: memTiers.length === 0,
    gotyEmptyTxt: s.memBusy ? 'READING THEIR LIST…' : 'NO GOTY LIST FOR THAT YEAR',
    busy: s.memBusy,
  };
}

// ---------------------------------------------------------------------------
// login / reset
// ---------------------------------------------------------------------------

export interface LoginTile extends Chip {
  key: string;
  nameUp: string;
  bd: string;
  rowBg: string;
}

export function loginVM(s: QdStore) {
  const source: User[] = s.users.length
    ? s.users
    : s.roster.map((r) => ({
        k: r.userKey,
        name: r.displayName,
        role: 'user' as const,
        hue: r.hue,
        col: r.col,
        email: '',
        // The pre-auth roster carries no addresses and must not: `login_roster`
        // is anon-callable, so it is served to anybody who loads the page.
        loginEmail: '',
        mustReset: false,
        av: r.avatarUrl,
        lastRev: 0,
        seen: [],
      }));
  const loginUsers = source.map<LoginTile>((u) => {
    const c = chipOf(u);
    const on = s.sel === u.k;
    return {
      ...c,
      key: u.k,
      nameUp: u.name.toUpperCase(),
      bd: on ? 'var(--acc)' : 'var(--gt-border)',
      rowBg: on ? 'var(--accSoft)' : 'var(--gt-field)',
    };
  });
  const resetUser = source.find((u) => u.k === s.resetFor);
  const recovering = s.resetMode === 'recover';
  const resetName = (resetUser ? resetUser.name : '').toUpperCase();
  return {
    loginUsers,
    sel: s.sel,
    loginPw: s.loginPw,
    loginErr: s.loginErr,
    hasLoginErr: !!s.loginErr,
    resetName,
    rp1: s.rp1,
    rp2: s.rp2,
    resetErr: s.resetErr,
    hasResetErr: !!s.resetErr,
    /** PORTING-NOTES D-01 — the reset screen's own minimum-length copy. */
    resetHint: PASSWORD_MIN_COPY,
    /* Signing in is the slowest thing a member does and the first — an auth
       round trip and then a whole hydrate, with nothing on screen moving. */
    loginBusy: s.authBusy === 'login',
    loginLbl: s.authBusy === 'login' ? 'SIGNING IN…' : 'LOG IN ▸',
    resetBusy: s.authBusy === 'reset',
    /* ONE SCREEN, TWO QUESTIONS (D-08). `first` is an account setting its own
       password for the first time; `recover` is somebody who has used this app
       for a year and forgot theirs. Greeting the second with FIRST LOGIN was
       the reason the mode exists — see `SessionSlice.resetMode`. */
    resetHead: recovering ? 'SET A NEW PASSWORD' : 'FIRST LOGIN, ' + resetName,
    /* The recovery arm names the ADDRESS rather than the player: the reader
       may hold two accounts, and the address is the half the emailed link
       actually identifies. */
    resetBody: recovering
      ? 'Choose a new password for ' +
        (s.pfAuthEmail || 'your account') +
        '. You will be signed in as soon as it is set.'
      : 'Your temp password worked. Set a real one to continue — you can add a recovery email later in your profile.',
    resetLbl:
      s.authBusy === 'reset'
        ? 'SETTING YOUR PASSWORD…'
        : recovering
          ? 'SET PASSWORD & SIGN IN ▸'
          : 'SET PASSWORD & ENTER ▸',
  };
}

/**
 * FORGOT PASSWORD (D-08, docs/EMAIL.md).
 *
 * Two states, and the interesting one is `sent`: it reports what was DONE, not
 * what was found. `sendPasswordReset` refuses to tell the client whether the
 * address has an account — a reset form that answered would be a free
 * membership oracle — so there is no "no such account" copy here to write, and
 * adding one later would mean weakening the repository first.
 *
 * The hint under the sent state is the honest half of the accounts that predate
 * email: their login address is still `<key>@crew.quarterdown.com`, a domain
 * with no MX, so a reset asked for at their real address finds nothing and
 * mails nothing. Nobody can tell that from this screen, so it names the person
 * who can fix it.
 */
export function recoverVM(s: QdStore) {
  return {
    fpEmail: s.fpEmail,
    fpErr: s.fpErr,
    hasErr: !!s.fpErr,
    sent: !!s.fpSent,
    sentTo: s.fpSent,
    busy: s.authBusy === 'forgot',
    lbl: s.authBusy === 'forgot' ? 'SENDING…' : 'SEND RESET LINK ▸',
  };
}

// ---------------------------------------------------------------------------
// discover
// ---------------------------------------------------------------------------

/**
 * The catalog as the BROWSE surfaces see it (G-03).
 *
 * `catalog_games` is crew-shared, and `ensure_game` mints provisional rows into
 * it (`prov`) for appids nobody curated. That was harmless while the only minter
 * was a live-search add — such a row lands on the crew list in the same breath,
 * so it belonged in Discover. GOTY auto-fill mints up to twenty rows per member
 * that nobody asked to browse: without this, one member pressing the button
 * would put their private Steam library in everyone's Discover grid.
 *
 * The rule is therefore "curated, or the crew actually put it on the list", which
 * leaves every pre-existing row exactly where it was. Provisional rows stay fully
 * reachable — by live search, by Detail, and on the board that minted them; they
 * just do not pad the browse grid or its "N OF M CATALOG GAMES" count.
 */
export function browsable(s: QdStore): Game[] {
  const onList = new Set(s.list.map((e) => String(e.id)));
  return s.catalog.filter((g) => g.prov !== true || onList.has(String(g.id)));
}

/**
 * Discover, in whichever of its two modes is live.
 *
 * LOCAL   nothing typed, no tag picked — the games table, matched and ranked
 *         client-side. Phase 1 behaviour, unchanged.
 * BROWSE  a term or a tag — a MERGED page, not Steam's search alone: Steam's
 *         50-row pages in Steam's order, led (on a term browse) by the local
 *         `discover_page` matches only our ecosystem knows, with any Steam row
 *         the catalog recognises swapped for its rich twin in place
 *         (`slices/browse.ts` owns the merge and its counts).
 *
 * The counts are the part worth reading carefully. In browse mode the screen
 * holds only the pages it has fetched, so it can state Steam's total honestly
 * but cannot state how many of ALL matches a filter removed — only how many of
 * the LOADED ones. `hiddenScope` carries that distinction into the copy rather
 * than letting the number quietly change meaning between modes.
 */
export function discoverVM(s: QdStore, now: number = Date.now()) {
  const ctx = tileCtx(s);
  // Discover's own set, NAMED — the screen owns one of them (`filterKey.ts`),
  // and this VM must never read "whatever screen we are on": half of what it
  // describes arrived through an async fetch keyed to exactly this set.
  const dFlt = { f: s.fltBy.discover, b: s.bTagsBy.discover, x: s.xTagsBy.discover };
  const browse = browseMode(s);
  const feed = feedMode(s);

  // In browse mode Steam has already done the matching, so `pass()` must NOT run
  // again: it would re-filter Steam's own results with a local substring rule and
  // silently drop rows that legitimately matched a TAG rather than the name.
  //
  // THE FEED IS THE SAME RULE FOR THE SAME REASON, and it is the easier one to
  // get wrong. `discover_page` has already matched, filtered, ranked and paged —
  // against a taste profile and an ownership join the client is not allowed to
  // hold. Re-running `pass`, `passFlt`, `sortArr` or `browsable` over its rows
  // would silently re-sort a page by a key it was not ordered on, and would let
  // `browsable()` hide pool rows the server deliberately included. The client's
  // job here is to render, not to re-decide.
  const base: Game[] = browse ? s.live : feed ? s.feedRows : [...browsable(s), ...s.live];
  const matched = browse || feed ? base : base.filter((g) => pass(g, [], s.q));
  // ONLY the feed skips `passFlt`. Browse still filters client-side and the
  // fallback obviously does. Skipping it for browse too would silently zero
  // `hiddenByFlt` on that screen.
  //
  // BROWSE NOW FILTERS ON BOTH SIDES, which is not double-counting. Steam
  // applies the price CAP, the discount flag and coming-soon over the whole
  // result set (`slices/browse.ts#browseNarrow`); what runs here is the
  // remainder Steam has no form for — the demo tri-state, released/EA, the
  // crew lens, playtest tracking — plus the exact discount percentage that
  // `specials=1` could only widen to "any sale". Re-testing a narrowed row
  // against the narrowing it already passed is a no-op by construction, so the
  // two compose rather than fight: what Steam removed never arrives, and what
  // `passFlt` removes here is counted in `hiddenByFlt` under the `· OF N
  // LOADED` scope the match line states.
  //
  // The tag EXCLUDES ride the same split. The feed sent them to
  // `discover_page` as `xtags`, so its rows and counts arrive already
  // excluded and re-applying anything here would double-count. The two
  // client-filtered modes apply them over `Game.tids` — Steam's ids, the
  // vocabulary the picker selected from — and a row with no `tids` (a
  // fallback catalog row, a companion row, any non-browse source) is
  // tag-membership-UNKNOWN and passes: unknown never excludes (decision 5).
  const xset = dFlt.x.length > 0 ? new Set(dFlt.x) : null;
  const passX = (g: Game): boolean => !xset || !g.tids || !g.tids.some((t) => xset.has(t));
  const filtered = feed
    ? matched
    : matched.filter((g) => passFlt(g, dFlt.f, s.pts, s.ownBy) && passX(g));
  // Neither server-paged mode re-sorts, and browse now EARNS that the way the
  // feed always did. Steam ordered this page on the sort the user picked
  // (`sort_by`, forwarded by `browseNarrow`), so re-running `sortArr` would
  // reorder page 1 by a key page 2 was not ordered on — and the moment LOAD
  // MORE appended, the seam would show. Before the sort was forwarded this line
  // was the whole of the bug: the client did not sort, nothing else did either,
  // and the dropdown moved nothing at all.
  const ordered = browse || feed ? filtered : sortArr(filtered, s.sort, s.q);

  const loaded = ordered.length;
  // Browse and the feed both page server-side, so every loaded row is shown; the
  // fallback keeps its client-side reveal window.
  const shown = browse || feed ? loaded : Math.min(s.discoverShown, loaded);
  // `liveStatsOf` ADDS keys and changes none: every string this tile renders
  // still comes from `tileVM` alone, and the popularity/freshness numbers ride
  // along unrendered so a design pass can reach for them without touching the
  // store, the repository or the RPCs.
  //
  // THE SPREAD ORDER IS THE ENFORCEMENT OF THAT, and it is stats-then-tile
  // because the LAST spread wins in JS. On any key the two objects share,
  // `tileVM`'s value survives and `liveStatsOf`'s is discarded — the tile owns
  // every name it and the stats might ever share, without exception.
  //
  // Nothing collides today, which is exactly why the order has to be right now
  // rather than when something does. `liveStatsOf` already carries a raw `ccu`;
  // the day `tileVM` gains a rendered one — plausible, since plumbing the data
  // for that design pass is what this slice is FOR — the other order would make
  // every tile in the grid silently render the unformatted integer instead of
  // the formatted string, with no type error anywhere, because both are
  // assignable to the same widened property.
  const results = ordered.slice(0, shown).map((g) => ({ ...liveStatsOf(g, now), ...tileVM(g, ctx) }));
  // The feed's counts come back WITH the page, because under server paging the
  // client no longer knows either number and computing them from rows in hand
  // would make both a lie about a different set (DISCOVERY.md §5.5).
  const hiddenByFlt = feed ? s.feedHidden : matched.length - filtered.length;

  // Steam's count of EVERYTHING matching in browse mode, the server's true total
  // in the feed, our own count in the fallback.
  const total = browse ? s.bTotal : feed ? s.feedTotal : loaded;
  const remaining = browse
    ? Math.max(0, total - s.bLoaded)
    : feed
      ? Math.max(0, total - loaded)
      : loaded - shown;
  const hasMore = browse
    ? remaining > 0 && !s.bCapped && s.bState !== 'busy'
    : feed
      ? // The CURSOR decides, not the arithmetic: the server nulls it on a short
        // page, and that is the only authority on whether another page exists.
        s.feedCursor !== null && s.feedState !== 'busy'
      : remaining > 0;

  let matchLine: string;
  if (browse) {
    matchLine = total + (total === 1 ? ' MATCH' : ' MATCHES') + ' ON STEAM';
    if (s.bState === 'busy') matchLine += ' · SEARCHING…';
    else if (s.bState === 'err') matchLine += ' · STEAM SEARCH OFFLINE';
    else if (s.bCapped) matchLine += ' · FIRST ' + s.bLoaded + ' SHOWN';
  } else {
    matchLine = total + (total === 1 ? ' MATCH' : ' MATCHES') + ' · PC VIA STEAM';
  }

  /**
   * THE CONTROLS STEAM CANNOT FULLY ANSWER, named rather than left inert —
   * with the POST-RESULT arm each one now carries stated in the same breath.
   *
   * Browse mode hands the question to Steam, and Steam's search has no form
   * for four of the things this screen offers (measured, not assumed —
   * `worker/src/steam.ts#BROWSE_SORTS`, `#BrowseNarrow`). What changed since
   * the copy said only "CAN'T": three of the four are now finished CLIENT-side
   * over the loaded rows, and the line's job shifted from naming a dead
   * control to naming the SCOPE of a live one — the same distinction
   * `hiddenTxt`'s "· OF N LOADED" already draws for the counts.
   *
   *   - SORT: PEAK PLAYERS — still no data to finish with. `Players_DESC` and
   *     `Concurrent_DESC` return the unsorted baseline upstream, and there is
   *     no per-row number to sort by down here either: `pk` is the curated
   *     editorial column (the 46 catalog rows), `ccu` rides only the overlay's
   *     head, and a browse row carries neither. Steam's own default order is
   *     popularity-shaped (§2.2: CS2, Palworld, Marvel Rivals first), so the
   *     fallback IS a popularity sort in practice, and the line says that
   *     instead of implying a shuffle. NOT mapped to `Reviews_DESC`: a review
   *     count is a different popularity proxy, silently substituted.
   *   - MINIMUM PRICE — `minprice` is ignored upstream, but the items heal
   *     (`steamFacts.ts#revalidate` patches `live` in place) lands real prices
   *     on the visible rows within a screenful round trip, and `passFlt` has
   *     always applied the floor where a price is present. So the floor now
   *     BITES on loaded rows and the line names the scope, not a dead control.
   *   - PLAYER COUNT — three row classes, and the line has to be honest about
   *     all of them at once. A browse row the POOL knows arrives as its rich
   *     twin (the swap joins every Steam page against the full catalog the
   *     client holds), so the lens filters it NUMERICALLY on the resolved
   *     seat count — migration 2906's override > editorial > IGDB > PCGW >
   *     steam-solo ladder, live for ~85% of the pool (measured 2026-08-23:
   *     1,031 of 1,209). A row OUTSIDE the pool has one answerable claim in
   *     each direction — `soloOnlyCats`' single-player-only negative for the
   *     together-lens, `togetherCats`' named-multiplayer negative for the SOLO
   *     rung — and hides on whichever the set lens asks for. The rest are
   *     seat-unknown and STAY — the surprising third class, and the reason the
   *     line states the scope ("where known") plus the default ("unknown
   *     stays") instead of claiming either "works" or "can't".
   *   - MINIMUM DISCOUNT — `specials=1` narrows to "on sale" upstream; the
   *     healed `p`/`o` pair lets `passFlt` finish the ≥N% threshold over the
   *     loaded rows, so this too is a scope statement now.
   *
   * This line exists because the alternative is the defect it replaced: a
   * control that moves, reads as applied, and does nothing — or its inverse, a
   * control that quietly applies to less than it claims. Each clause appears
   * only when that control is actually set, so the default browse says nothing.
   */
  const gaps: string[] = [];
  if (browse) {
    if (s.sort === 'peak') gaps.push("PEAK PLAYERS ISN'T A STEAM SORT — SHOWING STEAM'S OWN POPULARITY ORDER");
    if ((dFlt.f.pr?.[0] ?? 0) > 0) gaps.push('MINIMUM PRICE NARROWS THE LOADED ROWS ONLY');
    if (dFlt.f.p > 0) gaps.push('SEAT COUNTS APPLY WHERE KNOWN — GAMES WITH UNKNOWN SEATS STAY SHOWN');
    if (dFlt.f.disc > 1) gaps.push('MINIMUM DISCOUNT NARROWS THE LOADED ROWS ONLY');
    // Steam's search has a Reviews_DESC SORT and no review FILTER, so this one
    // runs entirely over what came back. Same shape and same honesty as the two
    // above; DISCOVERY.md 5.1 is why an unmeasured parameter is never forwarded.
    if (dFlt.f.rs > 0) gaps.push('REVIEW SCORE NARROWS THE LOADED ROWS ONLY');
  }
  const gapTxt = gaps.join(' · ');

  // The empty state has to name the RIGHT reason. There are five distinct ways
  // to end up with nothing on screen and the old single sentence — "TRY FEWER
  // FILTERS OR A DIFFERENT NAME" — was wrong for four of them: it blames filters
  // that may not be set, and a name that may not have been typed.
  const term = s.q.trim();
  let emptyTxt: string;
  if (feed && s.feedState === 'busy') {
    emptyTxt = 'FINDING GAMES FOR YOU…';
  } else if (feed && s.feedPoolN === 0) {
    // An empty POOL and an empty RESULT SET are different states and the screen
    // says different things about them (§5.6). This one is not the user's fault
    // and no filter change will fix it.
    emptyTxt = "NO GAMES YET — TONIGHT'S SWEEP WILL FILL THIS IN";
  } else if (feed && hiddenByFlt > 0) {
    emptyTxt = 'ALL ' + (hiddenByFlt + total) + ' GAMES ARE HIDDEN BY YOUR FILTERS';
  } else if (s.bState === 'err') {
    emptyTxt = "STEAM SEARCH ISN'T ANSWERING — TRY AGAIN IN A MOMENT";
  } else if (s.bState === 'busy') {
    emptyTxt = 'SEARCHING STEAM…';
  } else if (matched.length > 0 && filtered.length === 0) {
    // Rows arrived; the filters removed all of them. Blaming the search here
    // would send the user to change the one thing that was working.
    emptyTxt = 'ALL ' + matched.length + ' LOADED RESULTS ARE HIDDEN BY YOUR FILTERS';
  } else if (browse && term !== '' && dFlt.b.length > 0) {
    emptyTxt = 'NOTHING ON STEAM MATCHES THAT NAME WITH THOSE TAGS';
  } else if (browse && dFlt.b.length > 0) {
    emptyTxt =
      dFlt.b.length === 1 ? 'NO STEAM GAMES CARRY THAT TAG' : 'NO STEAM GAMES CARRY ALL THOSE TAGS';
  } else if (browse) {
    emptyTxt = 'NOTHING ON STEAM MATCHES THAT NAME';
  } else {
    emptyTxt = 'NO MATCHES — TRY FEWER FILTERS OR A DIFFERENT NAME';
  }

  /**
   * WHY THE FEED IS AS PERSONAL AS IT IS — §5.6's rule that none of these may
   * present as an empty screen, in one line of copy.
   *
   * The fallback branch is the one that matters most. "Ranking is offline" is
   * the sentence that stops a broken Phase 2 looking identical to a working one:
   * without it, a member whose RPC failed sees a plausible grid of local games
   * and has no way to know the personalisation silently stopped.
   *
   * Empty in browse mode: Steam's search is not personal and does not claim to be.
   */
  let feedTxt = '';
  if (s.feedFallback) {
    feedTxt = 'SHOWING THE LOCAL GAMES TABLE · RANKING IS OFFLINE';
  } else if (feed) {
    if (s.feedReason === 'no_link') feedTxt = "STEAM'S MOST PLAYED · LINK STEAM TO RANK THIS FOR YOU";
    else if (s.feedReason === 'private') feedTxt = "STEAM'S MOST PLAYED · YOUR STEAM PROFILE IS PRIVATE";
    else if (s.feedReason === 'no_taste') feedTxt = "STEAM'S MOST PLAYED · STILL LEARNING WHAT YOU LIKE";
    else if (s.feedReason === 'empty_table') feedTxt = '';
    else if (s.feedPersonalized) feedTxt = 'PICKED FOR YOU · YOUR LIBRARY AND WHAT STEAM IS PLAYING';
  }

  return {
    q: s.q,
    hasQ: !!s.q,
    /** True when Steam is answering rather than the local games table. */
    isBrowse: browse,
    /** True when the ranked server feed is answering. */
    isFeed: feed,
    /** The personalisation state line. Empty when there is nothing to say. */
    feedTxt,
    hasFeedTxt: feedTxt !== '',
    /** The feed could not be reached and this is the Phase 1 path. */
    isFallback: s.feedFallback,
    matchLine,
    /** Controls Steam's search cannot answer, named. Empty when none are set. */
    gapTxt,
    hasGapTxt: gapTxt !== '',
    emptyTxt,
    results,
    noResults: results.length === 0,
    /** Every match — Steam's total in browse mode, ours locally. */
    total,
    /** How many rows this screen actually holds. Differs from `total` in browse. */
    loaded,
    hiddenByFlt,
    hasHidden: hiddenByFlt > 0,
    hiddenTxt:
      hiddenByFlt +
      (hiddenByFlt === 1 ? ' MATCH HIDDEN BY FILTERS' : ' MATCHES HIDDEN BY FILTERS') +
      // The scope, stated, because in browse mode the denominator is the pages
      // fetched so far and NOT Steam's total. Without this the same sentence
      // means two different things on two screens.
      (browse ? ' · OF ' + matched.length + ' LOADED' : ''),
    hasMore,
    moreTxt: browse
      ? 'LOAD MORE · ' + remaining + ' MORE ON STEAM'
      : 'LOAD MORE · ' + remaining + ' MORE',
    /** Steam will not page past its own wall; say so rather than implying an end. */
    cappedTxt: s.bCapped ? "THAT'S AS DEEP AS STEAM SEARCH GOES" : '',
    sortVal: s.sort,
    isGallery: s.view === 'g',
    isList: s.view === 'l',
    ...filterButton(s),
  };
}

/** A picker chip's cycle position: off → include (search) → exclude → off. */
export type TagState = 'off' | 'inc' | 'exc';

export interface TagChip {
  key: string;
  id: number;
  l: string;
  st: TagState;
  /** The chip's prefix glyph: `✓ ` include · `✕ ` exclude · nothing off. */
  pre: string;
  bd: string;
  clr: string;
  bg: string;
}

/**
 * The tag picker's contents, shared by the mobile sheet and the desktop panel.
 *
 * `POPULAR_TAG_NAMES` are resolved against the LIVE vocabulary rather than
 * shipped as ids: if Steam ever renumbers, the list gets shorter instead of
 * silently browsing by the wrong tag. A name that no longer resolves simply does
 * not appear.
 *
 * One tap cycles a chip off → include → exclude → off (`cycleTag`) — Steam's
 * own search sidebar idiom. Includes are the browse question; excludes narrow
 * whatever mode is showing. The exclude palette is the `--acc2` negation
 * triple every NOT-shaped control already wears (`chipsOf`, the PLAYTEST HIDE
 * segment), so the two states read as different answers, not different tags.
 */
export function tagPickerVM(s: QdStore) {
  const byId = new Map(s.tagVocab.map((t) => [t.id, t.name]));
  const inc = new Set(bTagsOf(s));
  const exc = new Set(xTagsOf(s));
  const chip = (id: number, name: string): TagChip => {
    const st: TagState = inc.has(id) ? 'inc' : exc.has(id) ? 'exc' : 'off';
    return {
      key: 'tp' + id,
      id,
      l: name.toUpperCase(),
      st,
      pre: st === 'inc' ? '✓ ' : st === 'exc' ? '✕ ' : '',
      bd: st === 'inc' ? 'var(--acc)' : st === 'exc' ? 'var(--acc2)' : 'var(--gt-border-2)',
      clr: st === 'inc' ? 'var(--acc)' : st === 'exc' ? 'var(--acc2)' : 'var(--gt-text-5)',
      bg: st === 'inc' ? 'var(--accSoft)' : st === 'exc' ? 'var(--acc2Soft)' : 'transparent',
    };
  };

  const byName = new Map(s.tagVocab.map((t) => [t.name.toLowerCase(), t]));
  const popular: TagChip[] = [];
  for (const nm of POPULAR_TAG_NAMES) {
    const t = byName.get(nm.toLowerCase());
    if (t) popular.push(chip(t.id, t.name));
  }

  const q = s.tagQ.trim().toLowerCase();
  const results: TagChip[] =
    q === ''
      ? []
      : s.tagVocab
          .filter((t) => t.name.toLowerCase().includes(q))
          // Shortest name first: typing "co" should offer "Co-op" ahead of
          // "Local Co-Op" and "Co-op Campaign".
          .sort((a, b) => a.name.length - b.name.length || (a.name < b.name ? -1 : 1))
          .slice(0, 24)
          .map((t) => chip(t.id, t.name));

  const incN = bTagsOf(s).length;
  const excN = xTagsOf(s).length;
  const n = incN + excN;
  // Includes first, excludes after — the same order `filterChips` uses for the
  // rail, so the panel and the rail never disagree about what "picked" looks
  // like.
  const picked = [...bTagsOf(s), ...xTagsOf(s)].map((id) => chip(id, byId.get(id) ?? '#' + id));
  const pickedTitle =
    (incN > 0 ? incN + ' SEARCH — STEAM MATCHES ALL' : '') +
    (incN > 0 && excN > 0 ? ' · ' : '') +
    (excN > 0 ? excN + ' EXCLUDED' : '');
  return {
    picked,
    hasPicked: n > 0,
    pickedTitle,
    popular,
    results,
    hasResults: results.length > 0,
    noHit: q !== '' && results.length === 0 && s.tagVocab.length > 0,
    /** Steam's vocabulary never arrived; the picker still works from what is picked. */
    vocabDown: s.tagVocab.length === 0,
    vocabDownTxt: "STEAM'S TAG LIST DIDN'T LOAD — SEARCH BY NAME INSTEAD, OR TRY AGAIN LATER",
    searchPlaceholder:
      s.tagVocab.length > 0 ? 'SEARCH ' + s.tagVocab.length + ' STEAM TAGS…' : 'TAG LIST UNAVAILABLE',
    note: 'TAP: SEARCH · TAP AGAIN: EXCLUDE · AGAIN: OFF',
    /**
     * What a pick actually does HERE. The picker is offered on every screen that
     * draws the panel and does two different things across them — on Discover
     * it re-asks
     * Steam, elsewhere it narrows the rows already in hand — and a control that
     * changes meaning by host has to say which meaning is live.
     */
    appliesTxt:
      scopeOf(s) === 'discover'
        ? 'SEARCHES STEAM FOR THESE TAGS'
        : 'NARROWS THIS SCREEN TO GAMES CARRYING THESE TAGS',
    doneTxt: n === 0 ? 'DONE' : 'DONE · ' + n,
  };
}

function onOff(on: boolean) {
  return {
    bd: on ? 'var(--acc)' : 'var(--gt-border-2)',
    clr: on ? 'var(--acc)' : 'var(--gt-muted)',
    bg: on ? 'var(--accSoft)' : 'transparent',
  };
}

function filterButton(s: QdStore) {
  // Tags count into the badge now that they are a section of this panel rather
  // than a modal of their own. A number that ignored them would under-report
  // exactly the filter most likely to have emptied the screen.
  const n = fltCount(fltOf(s)) + bTagsOf(s).length + xTagsOf(s).length;
  return {
    fltBtnTxt: n ? '+ FILTERS · ' + n : '+ FILTERS',
    fltBtnBd: n || s.fltOpen ? 'var(--acc)' : 'var(--gt-border-2)',
    fltBtnClr: n || s.fltOpen ? 'var(--acc)' : 'var(--gt-text-5)',
    fltBtnBg: n || s.fltOpen ? 'var(--accSoft)' : 'transparent',
    fltN: n,
    fltChips: filterChips(s),
  };
}

// ---------------------------------------------------------------------------
// games tonight
// ---------------------------------------------------------------------------

export interface VoteChip {
  key: string;
  t: string;
  bg: string;
  bord: string;
  clr: string;
}

export interface SharedCapsule extends CapsuleVM {
  /** `AVG 2.7` — the rounded group average, always shown */
  tallyIn: string;
  /** `C ×2` — the PLAY IT WITHOUT ME count, only meaningful when `hasOut` */
  tallyOut: string;
  hasOut: boolean;
  chips: VoteChip[];
}

export interface SharedRow {
  key: SharedTier;
  big: string;
  mFs: string;
  bigClr: string;
  labBg: string;
  sub: string;
  cntTxt: string;
  bd: string;
  games: SharedCapsule[];
  isEmpty: boolean;
  /**
   * Always `true`. The rows are the four calls now, so each one means the same
   * thing however the crew is filtered and none of them can stop applying —
   * `sharedRowMeta` dropped the `show` gate with the player-count labels that
   * needed it. Kept as a field because both screens read it and upstream's row
   * VM emits it too; a screen that wants a row hidden has to say so itself.
   */
  show: boolean;
}

/**
 * The ordering Games Tonight sorts its tiers by when the board is looking at
 * exactly ONE member — that member's own MY INTERESTS ranking — and `null` in
 * every other case (3059, Marcus 2026-09-03: *"if I filter to just one person I
 * can see their actual personal board … the game order in each tier matches the
 * order that they actually set"*).
 *
 * `null` IS THE IMPORTANT RETURN and it means two different things that must
 * both fall back to the average sort: the board is looking at several people,
 * so there is no personal ranking to apply; or it is looking at one person
 * whose ranking has not been READ yet. An unread ordering must not degrade into
 * `[]` — `sharedRowGames` would then sort every game to the same `1e9` and hand
 * back an order that looks deliberate and is not.
 *
 * IT KEYS OFF `selUsers`, NOT `tFilter.length`, and the difference is not
 * pedantry. A key in `tFilter` that is no longer in the roster (a removed
 * member) filters the board to nobody, and that is not one person's board. The
 * other end of the same rule is the payoff: in a crew of ONE, ALL and "just me"
 * are the same selection, so the board reads in that member's order without
 * anybody having to touch the rail.
 *
 * MINE COMES OFF `intOrder`, everyone else's off `crewIntOrder`, which is the
 * same two-sources split `crewVM` and `linkStateOf` make and for the same
 * reason: my own copy is the one that can change mid-session, and it carries
 * the drag that has not been confirmed by the server yet.
 */
export function soloBoardOrder(s: QdStore, selUsers: readonly User[]): readonly GameId[] | null {
  if (selUsers.length !== 1) return null;
  const k = selUsers[0]?.k;
  if (!k) return null;
  return (k === s.session ? s.intOrder[k] : s.crewIntOrder[k]) ?? null;
}

export function tonightVM(s: QdStore) {
  const ctx = tileCtx(s);
  const entries = entriesOf(s, 'tonight');
  const rawN = listRawCount(s);
  const tFlt = s.tFilter;
  const selUsers = tFlt.length ? s.users.filter((u) => tFlt.includes(u.k)) : s.users;
  const selN = selUsers.length;
  const rows = agg(entries, selUsers);
  const meta = sharedRowMeta();
  const soloOrd = soloBoardOrder(s, selUsers);

  const sharedRows = SHARED_ROW_KEYS.map<SharedRow>((t) => {
    const rm = meta[t];
    const games = sharedRowGames(rows, t, soloOrd).map<SharedCapsule>((a) => {
      const tile = tileVM(a.x.g, ctx);
      return {
        ...capsuleOf(a.x.g, tile, t === 'C' ? 'saturate(.4)' : 'none'),
        tallyIn: 'AVG ' + (Math.round(a.avg * 10) / 10),
        tallyOut: 'C ×' + a.outN,
        hasOut: a.outN > 0,
        // The chip prints the CALL, not the caller: a letter on the player's own
        // colour, so the row reads as an average you can audit. Since 3012 the
        // colours are distinct enough that a `c` no longer needs the binary
        // board's accent-2-on-black ✕ treatment to be attributable — it sits on
        // the caller's colour like every other call (2026-08-27), and the ink
        // is derived from that colour because the palette spans dark to light.
        // No call at all is an empty dashed square, which is a different thing
        // from a `c` and must never collapse into one.
        //
        // Order is the call, highest first (S · A · B · C, then the silent),
        // roster order breaking ties — so a hovered card reads as its average
        // does, top down.
        chips: selUsers
          .map((u) => {
            const v = a.x.e.votes[u.k];
            return { u, L: v && isCall(v) ? LVL[v] : null };
          })
          .sort((p, q) => (q.L ? q.L.v + 1 : 0) - (p.L ? p.L.v + 1 : 0))
          .map<VoteChip>(({ u, L }) => {
            const c = chipOf(u);
            return {
              key: u.k,
              t: L ? L.g : '',
              bg: L ? c.hueBg : 'transparent',
              bord: L ? '1px solid var(--gt-ink)' : '1px dashed var(--gt-border-3)',
              clr: L ? inkOn(c.hueBg) : 'var(--gt-ink)',
            };
          }),
      };
    });
    return {
      key: t,
      big: rm.big,
      mFs: rm.fs,
      bigClr: rm.clr,
      labBg: rm.lab,
      sub: rm.sub,
      cntTxt: gamesCount(games.length),
      bd: t === 'S' && games.length ? 'var(--acc)' : 'var(--gt-border)',
      games,
      isEmpty: !games.length,
      show: true,
    };
  });

  // Below quorum, which is what `agg` leaves without a tier. The count is the
  // whole point of the row — `2/6 CALLED` says exactly how far off the game is.
  const waitGames = sharedWaitGames(rows, soloOrd)
    .map((a) => ({
      key: '' + a.x.e.id,
      id: a.x.e.id,
      nameUp: a.x.g.n.toUpperCase(),
      cntTxt: a.votedN + '/' + selN + ' CALLED',
    }));

  const tfChips = s.users.map((u) => {
    const c = chipOf(u);
    const on = tFlt.includes(u.k);
    return {
      key: u.k,
      ini: c.ini,
      hasAv: c.hasAv,
      avBg: c.avBg,
      bg: c.hueBg,
      bd: on ? '2px solid var(--acc)' : '1px solid var(--gt-ink)',
      op: !tFlt.length || on ? '1' : '.3',
    };
  });

  return {
    sharedRows,
    waitGames,
    hasWaiting: waitGames.length > 0,
    tfChips,
    tfAllBd: !tFlt.length ? 'var(--acc)' : 'var(--gt-border-2)',
    tfAllClr: !tFlt.length ? 'var(--acc)' : 'var(--gt-muted)',
    tfAllBg: !tFlt.length ? 'var(--accSoft)' : 'transparent',
    hasTfNote: tFlt.length > 0,
    tfNote: tFlt.length
      ? selUsers.map((u) => u.name.toUpperCase()).join(' + ') + (tFlt.length === 1 ? "'S CALLS" : ' — COMBINED')
      : '',
    tEmpty: entries.length === 0,
    /* A board searched or filtered down to nothing is not an empty list. */
    tEmptyTxt: boardEmptyTxt(rawN),
    entryCount: entries.length,
    selN,
    bq: s.bqBy.tonight,
    ...uncalledNudge(s, entries),
    ...filterButton(s),
  };
}

/**
 * The board's opening prompt: how many tracked games the signed-in player has
 * not called yet.
 *
 * It counts over `entries` — the SAME filtered set MY INTERESTS reads, because
 * both take it from `entriesOf` — so the number you tap here matches the number
 * you land on. That is the whole point of the strip, and a second derivation
 * would be free to disagree with it.
 *
 * **Deliberately blind to WHO'S PLAYING.** `tFilter` scopes whose calls the row
 * averages count; it says nothing about whether YOU have called. Passing
 * `selUsers` in here would make the prompt shrink when you filtered the board
 * down to one friend, which is a claim about your own to-do list that the chip
 * never made.
 *
 * The two mirrors used to disagree on the definition — desktop counted a
 * skip-aware `iQueue` the app did not have. It has one now (`intQueue`,
 * 2026-08-24), and this counts it: the SAME array the CTA hands
 * `openCallRun`, so the number you tap and the length of the run you get
 * cannot disagree. That is the entry's own requirement, and deriving both from
 * one call is the only way to keep it true by construction rather than by
 * two functions agreeing today.
 *
 * A skip reorders the queue without shortening it, so the count is unchanged
 * by skipping — which is right: a deferred call is still a call you owe.
 *
 * Signed out there is no "your call" to prompt for, so the strip is absent
 * rather than counting every entry — the prototype's `!me ||` arm is a
 * shortcut that only looks right because its board is never signed out.
 */
function uncalledNudge(s: QdStore, entries: Pair[]) {
  const me = s.me();
  const ids = me ? intQueue(entries.map((x) => x.e), me.k, s.intSkips[me.k] ?? []) : [];
  const n = ids.length;
  return {
    hasIntNudge: n > 0,
    intNudgeHead: n === 1 ? '1 GAME NEEDS YOUR CALL' : n + ' GAMES NEED YOUR CALL',
    intNudgeSub: 'NO CALL FROM YOU YET · YOUR S/A/B/C COUNTS TOWARD THE ROW AVERAGE',
    intNudgeCta: n === 1 ? 'MAKE THE CALL ▸' : 'MAKE YOUR CALLS ▸',
    intNudgeN: n,
    /** What the CTA runs, in MY INTERESTS queue order. */
    intNudgeIds: ids,
  };
}

// ---------------------------------------------------------------------------
// my interests
// ---------------------------------------------------------------------------

/**
 * One square in a list row's call strip. Four per row, in `CALLS` order, and
 * the whole strip is emitted whatever the current call is — the selected one is
 * the filled square, so "what did I say?" and "what else could I say?" are the
 * same glance. The button writes `vote(id, key)`, which is a toggle: tapping
 * the filled square clears back to no call.
 */
export interface IntCallBtn {
  key: VoteValue;
  /** the letter the square prints */
  g: string;
  /** the label, for desktop's `data-tip`; mobile has no tooltips and ignores it */
  tip: string;
  bd: string;
  clr: string;
  bg: string;
}

export interface IntListRow {
  key: string;
  id: GameId;
  name: string;
  fall: string;
  ini: string;
  hdrCands: ArtCand[];
  metaTxt: string;
  calls: IntCallBtn[];
}

export interface IntBoardRow {
  key: string;
  rowId: string;
  big: string;
  bigClr: string;
  labBg: string;
  sub: string;
  cntTxt: string;
  bord: string;
  games: Array<CapsuleVM & { shiftX: string; dragOp: string }>;
  isEmpty: boolean;
}

/**
 * The board's four rows — the calls in `CALLS` order, and nothing else.
 * Everything a row shows is `LVL`'s, so the letter, the label and both colours
 * cannot drift from the buttons that set them.
 *
 * THE FIFTH ROW IS GONE (DESIGN-CHANGELOG 2026-08-24). `?` NO CALL YET was
 * never a tier: it was a holding pen that grew with every game the crew added
 * and read as a rank below C. Uncalled games queue in the TO-DO panel instead,
 * one at a time, exactly as GOTY potentials do.
 *
 * What did NOT change is the DATA model: `INT_ROW_KEYS` still ends in `''` and
 * `intOrder` still flattens five buckets, so an uncalled game still has a
 * place to live and an order to live in. What DID change is who reads it — the
 * panel's queue is `intQueue` (oldest first, skips last), a different rule from
 * the `intRowIds(…, '')` the row used, and the panel takes no drop index at
 * all. The row stopped being rendered; the bucket it stood for did not move.
 */
const INT_ROWS: ReadonlyArray<{ key: string; v: IntRowKey; big: string; sub: string; clr: string; lab: string }> = [
  ...CALLS.map((l) => ({ key: l, v: l, big: LVL[l].g, sub: LVL[l].t, clr: LVL[l].c, lab: LVL[l].lab })),
];

/**
 * The TO-DO panel's `SKIP FOR NOW ↻` refusal, when the featured game is the
 * only one left to call. Its GOTY twin is spelled inline in both Goty screens;
 * this one is a constant because two screens print it and a fourth site is how
 * copy drifts. `CALL IT` rather than GOTY's `PLACE IT` — the verb is the one
 * the button next to it uses.
 */
export const INT_SKIP_LAST = 'LAST ONE — CALL IT OR LEAVE IT';

/** The `SKIP FOR NOW ↻` tooltip on the interests panel (desktop). */
export const INT_SKIP_TIP = 'NO CALL — CYCLES TO THE BACK OF YOUR TO-DO';

/**
 * The TO-DO panel's call legend, one line per rendered line.
 *
 * Spelled out rather than derived from `LVL`, deliberately: the panel is 320px
 * and the full labels do not fit two-to-a-line, so the design shortens
 * `I COULD BE CONVINCED` → `CONVINCE ME` and `PLAY IT WITHOUT ME` →
 * `WITHOUT ME`. Deriving would print the long form and wrap; this is the
 * design's copy verbatim (`design/GT Boards.dc.html`, TO DO panel).
 */
export const INT_TODO_LEGEND: readonly string[] = [
  'S MUST PLAY · A WANT TO PLAY',
  'B CONVINCE ME · C WITHOUT ME',
  'TAP A CALL — OR DRAG THE COVER ONTO A ROW',
];

/** The interests TO-DO panel's native `title` — its drop contract, spelled out. */
export const INT_TODO_TITLE = 'DROP A CALLED GAME HERE TO CLEAR YOUR CALL';

/**
 * The TO-DO panel's four call buttons. Colours come from `LVL` so the button
 * and the row it sends a game to cannot disagree; only the two frames that are
 * NOT the neutral one are spelled here — `S` wears the accent it means, `C`
 * the softened accent-2 that says "this is the other end of the ladder", and
 * the middle two stay quiet. Same division `G_BTN_META` makes one board over.
 */
const INT_BTN_META: Record<VoteValue, { bd: string; bg: string }> = {
  s: { bd: 'var(--acc)', bg: 'var(--accSoft)' },
  a: { bd: 'var(--gt-border-2)', bg: 'transparent' },
  b: { bd: 'var(--gt-border-2)', bg: 'transparent' },
  c: { bd: 'oklch(0.85 0.19 335 / .6)', bg: 'transparent' },
};

export function interestsVM(s: QdStore, now: number = Date.now()) {
  const ctx = tileCtx(s);
  const me = s.me();
  const entries = entriesOf(s, 'interests');
  const rawN = listRawCount(s);

  const intRows = entries
    .slice()
    .sort((a, b) => b.e.at - a.e.at)
    .map<IntListRow>((x) => {
      const t = tileVM(x.g, ctx);
      const v = me ? x.e.votes[me.k] : undefined;
      return {
        key: '' + x.e.id,
        id: x.e.id,
        name: x.g.n,
        fall: t.fall,
        ini: t.ini,
        hdrCands: t.hdrCands,
        metaTxt: fmt$(x.g) + ' · ' + t.playersTxt,
        calls: CALLS.map<IntCallBtn>((l) => {
          const L = LVL[l];
          const on = v === l;
          return {
            key: l,
            g: L.g,
            tip: L.t,
            bd: on ? L.c : 'var(--gt-border-2)',
            clr: on ? 'var(--gt-ink)' : L.c,
            bg: on ? L.c : 'transparent',
          };
        }),
      };
    });

  const ord = me ? (s.intOrder[me.k] ?? []) : [];
  const intBoardRows = INT_ROWS.map<IntBoardRow>((r) => {
    const rowKey = r.v || 'w';
    const intDrag = s.drag && s.drag.zone === 'int';
    const ovHere = !!(intDrag && s.dragOver && s.dragOver.row === rowKey);
    const ordered = me ? intRowIds(entries.map((x) => x.e), me.k, ord, r.v) : [];
    const byId = new Map(entries.map((x) => [x.e.id, x]));
    let nd = 0;
    const games = ordered
      .map((id) => byId.get(id))
      .filter((x): x is Pair => !!x)
      .map((x) => {
        const isDr = !!(intDrag && s.drag && s.drag.id === x.e.id);
        const myNd = nd;
        if (!isDr) nd++;
        return {
          ...capsuleOf(x.g, tileVM(x.g, ctx), r.v === 'c' ? 'saturate(.4)' : 'none'),
          shiftX: ovHere && !isDr && s.dragOver && myNd >= s.dragOver.idx ? '14px' : '0px',
          dragOp: isDr ? '.35' : '1',
        };
      });
    return {
      key: r.key,
      rowId: rowKey,
      big: r.big,
      bigClr: r.clr,
      labBg: r.lab,
      sub: r.sub,
      cntTxt: gamesCount(games.length),
      bord: ovHere
        ? '1px solid var(--acc)'
        : r.v === 's' && games.length
          ? '1px solid var(--acc)'
          : r.v
            ? '1px solid var(--gt-border)'
            : '1px dashed var(--gt-border-3)',
      games,
      isEmpty: !games.length,
    };
  });

  // ---- the TO-DO panel (DESIGN-CHANGELOG 2026-08-24) ----------------------
  // The GOTY panel's twin, and derived the same way: one queue, its head is
  // featured, the next three are thumbs, the rest is a count. What differs is
  // the ordering rule — `intQueue` is oldest-first because this is a chore
  // list, not an insertion-ordered wish list (see its doc comment).
  const iSkipArr = me ? (s.intSkips[me.k] ?? []) : [];
  const pairById = new Map(entries.map((x) => [x.e.id, x]));
  const iQueue = me ? intQueue(entries.map((x) => x.e), me.k, iSkipArr) : [];
  const iTodoN = iQueue.length;
  const ifx = iQueue.length ? (pairById.get(iQueue[0] as GameId) ?? null) : null;

  const iFeat = ifx
    ? {
        ...capsuleOf(ifx.g, tileVM(ifx.g, ctx)),
        name: ifx.g.n,
        // NOT the GOTY panel's `date · developer`: an interests game is
        // something a crewmate put in front of you, so the line that matters
        // is who, and how long it has been waiting.
        metaTxt: 'ADDED BY ' + chipOf(s.user(ifx.e.by)).name.toUpperCase() + ' · ' + ago(ifx.e.at, now),
      }
    : { key: '', id: 0 as GameId, name: '', metaTxt: '', fall: '', ini: '', sheen: '', coverCands: [], hdrCands: [], filt: 'none', nameUp: '' };

  const iBtns = ifx
    ? CALLS.map((l) => ({
        key: l,
        id: ifx.e.id,
        t: LVL[l].g,
        // `S — MUST PLAY`: the letter the button shows, then what it means.
        // One square has room for a character, so the tooltip carries the word.
        tip: LVL[l].g + ' — ' + LVL[l].t,
        clr: LVL[l].c,
        ...INT_BTN_META[l],
      }))
    : [];

  const iUpNext = iQueue
    .slice(1, 4)
    .map((id) => pairById.get(id))
    .filter((x): x is Pair => !!x)
    .map((x) => capsuleOf(x.g, tileVM(x.g, ctx)));

  // Mobile's panel-arming. Every capsule that can be dragged on this board is
  // a CALLED game now — the `?` row that held the uncalled ones is gone — so
  // an interests drag is by construction a drag the panel can accept, and
  // unlike desktop (whose featured cover is also a drag source) there is no
  // case to withhold the invite from. Border stays 1px through all states so a
  // drag cannot reflow the page under the pointer.
  const intDragOn = !!(s.drag && s.drag.zone === 'int');
  const iTodoOver = !!(intDragOn && s.dragOver && s.dragOver.row === INT_TODO_ROW);

  return {
    intRows,
    intBoardRows,
    intCountTxt: '' + entries.length,
    iTodoRow: INT_TODO_ROW,
    iFeat,
    hasIFeat: !!ifx,
    iBtns,
    iUpNext,
    hasIUpNext: iUpNext.length > 0,
    hasIMore: iTodoN > 4,
    iMoreTxt: '+' + (iTodoN - 4) + ' MORE',
    iTodoN,
    iLeftTxt: iTodoN + ' LEFT',
    /** `SKIP FOR NOW` has nowhere to rotate to — the screens toast `INT_SKIP_LAST`. */
    iSkipBlocked: iTodoN <= 1,
    /** Nothing left to call, vs nothing on the list at all — two different empties. */
    iAllDone: !ifx && entries.length > 0,
    iDoneTxt: entries.length
      ? 'ALL CALLED — DRAG ONE BACK TO CHANGE YOUR MIND'
      : boardEmptyTxt(rawN),
    /* The list-mode empty, same two states as the TO-DO panel's. */
    tEmptyTxt: boardEmptyTxt(rawN),
    tEmpty: entries.length === 0,
    bq: s.bqBy.interests,
    iTodoBd: '1px dashed ' + (intDragOn ? 'var(--acc2)' : 'var(--gt-border-3)'),
    iTodoBg: iTodoOver ? 'var(--acc2Soft)' : 'var(--gt-panel)',
    iBoard: s.iMode !== 'list',
    iList: s.iMode === 'list',
    ibBd: s.iMode !== 'list' ? 'var(--acc)' : 'var(--gt-border-2)',
    ibClr: s.iMode !== 'list' ? 'var(--acc)' : 'var(--gt-muted)',
    ibBg: s.iMode !== 'list' ? 'var(--accSoft)' : 'transparent',
    ilBd: s.iMode === 'list' ? 'var(--acc)' : 'var(--gt-border-2)',
    ilClr: s.iMode === 'list' ? 'var(--acc)' : 'var(--gt-muted)',
    ilBg: s.iMode === 'list' ? 'var(--accSoft)' : 'transparent',
    ...filterButton(s),
  };
}

// ---------------------------------------------------------------------------
// game of the year
// ---------------------------------------------------------------------------

/**
 * Per-row label copy and chrome. The palette walks the ladder down: `S` carries
 * the accent, A→F fade through the text ramp on progressively flatter label
 * backgrounds, and `X` alone gets accent-2 — the cut row is a different kind of
 * thing from a grade, not merely the lowest one.
 */
const G_ROW_META: Record<Tier, { sub: string; clr: string; lab: string; hint: string }> = {
  S: { sub: 'GOTY', clr: 'var(--acc)', lab: 'var(--accSoft)', hint: 'TAP A TIER BUTTON ABOVE TO PLACE YOUR GOTY' },
  A: { sub: 'CONTENDER', clr: 'var(--gt-text)', lab: 'var(--gt-raised)', hint: 'CONTENDERS LAND HERE' },
  B: { sub: 'HONORABLE', clr: 'var(--gt-text-5)', lab: 'var(--gt-field)', hint: 'HONORABLE MENTIONS LAND HERE' },
  C: { sub: 'SOLID', clr: 'var(--gt-text-5)', lab: 'var(--gt-field)', hint: 'SOLID PICKS LAND HERE' },
  D: { sub: 'MIDDLING', clr: 'var(--gt-muted)', lab: 'var(--gt-panel)', hint: 'MIDDLING ONES LAND HERE' },
  E: { sub: 'ROUGH', clr: 'var(--gt-muted)', lab: 'var(--gt-panel)', hint: 'ROUGH ONES LAND HERE' },
  F: { sub: 'DID NOT DELIVER', clr: 'var(--gt-faint)', lab: 'transparent', hint: 'THE LETDOWNS LAND HERE' },
  X: { sub: 'CUT', clr: 'var(--acc2)', lab: 'oklch(0.85 0.19 335 / .06)', hint: 'CUTS LAND HERE' },
};

/**
 * The TO-DO placement buttons' chrome. Only the two poles are coloured — `S`
 * reads as the accent-filled pick, `X` as the accent-2 cut — so the grades in
 * between stay a quiet ramp and the two decisions that matter keep their weight.
 */
const G_BTN_META: Record<Tier, { bd: string; clr: string; bg: string }> = {
  S: { bd: 'var(--acc)', clr: 'var(--acc)', bg: 'var(--accSoft)' },
  A: { bd: 'var(--gt-border-2)', clr: 'var(--gt-text)', bg: 'transparent' },
  B: { bd: 'var(--gt-border-2)', clr: 'var(--gt-text-5)', bg: 'transparent' },
  C: { bd: 'var(--gt-border-2)', clr: 'var(--gt-text-5)', bg: 'transparent' },
  D: { bd: 'var(--gt-border-2)', clr: 'var(--gt-muted)', bg: 'transparent' },
  E: { bd: 'var(--gt-border-2)', clr: 'var(--gt-muted)', bg: 'transparent' },
  F: { bd: 'var(--gt-border-2)', clr: 'var(--gt-faint)', bg: 'transparent' },
  X: { bd: 'oklch(0.85 0.19 335 / .6)', clr: 'var(--acc2)', bg: 'transparent' },
};

export function gotyVM(s: QdStore) {
  const ctx = tileCtx(s);
  const me = s.me();
  const pastYear = s.gotyYear < GOTY_YEAR;
  const pots = me ? (s.potentials[me.k] ?? []) : [];
  const myT = me ? (s.tiers[me.k] ?? {}) : {};
  const ordArr = me ? (s.tierOrder[me.k] ?? []) : [];
  const skipArr = me ? (s.skips[me.k] ?? []) : [];

  const tl = tagLensOf(s, 'goty');
  const gFlt = s.fltBy.goty;
  const visible = new Map<GameId, Game>();
  for (const id of pots) {
    const g = s.gById(id);
    // The tier board narrows like every other surface now. It is still a thing
    // the user arranged by hand, so the honest `+ FILTERS · N` count and the
    // chip rail above it are what stop a row vanishing from reading as data
    // loss — not the filter quietly declining to run.
    if (g && passBoard(g, gFlt, tl, s.bqBy.goty, s.pts, s.ownBy)) visible.set(id, g);
  }

  const gDrag = !!(s.drag && s.drag.zone === 'goty');

  const gRows = TIERS.map((t) => {
    const rm = G_ROW_META[t];
    const ovHere = !!(gDrag && s.dragOver && s.dragOver.row === t);
    const ids = tierRowIds([...visible.keys()], myT, ordArr, t);
    let nd = 0;
    const games = ids
      .map((id) => visible.get(id))
      .filter((g): g is Game => !!g)
      .map((g) => {
        const isDr = !!(gDrag && s.drag && s.drag.id === g.id);
        const myNd = nd;
        if (!isDr) nd++;
        return {
          ...capsuleOf(g, tileVM(g, ctx)),
          shiftX: ovHere && !isDr && s.dragOver && myNd >= s.dragOver.idx ? '14px' : '0px',
          dragOp: isDr ? '.35' : '1',
        };
      });
    return {
      key: t,
      big: t === 'X' ? '✕' : t,
      sub: rm.sub,
      bigClr: rm.clr,
      labBg: rm.lab,
      bd: ovHere ? 'var(--acc)' : 'var(--gt-border)',
      cntTxt: gamesCount(games.length),
      games,
      isEmpty: !games.length,
      hint: rm.hint,
    };
  });

  const queue = gotyQueue(pots, myT, skipArr, (id) => visible.has(id));
  const gTodoN = queue.length;
  const featId = queue[0];
  const featG = featId === undefined ? null : (visible.get(featId) ?? null);
  const gFeat = featG
    ? {
        ...capsuleOf(featG, tileVM(featG, ctx)),
        name: featG.n,
        metaTxt: fmtD(featG) + ' · ' + featG.dev,
      }
    : { key: '', id: 0 as GameId, name: '', metaTxt: '', fall: '', ini: '', sheen: '', coverCands: [], hdrCands: [], filt: 'none', nameUp: '' };

  const gBtns = featG
    ? TIERS.map((t) => ({
        key: t,
        id: featG.id,
        t: t === 'X' ? '✕' : t,
        ...G_BTN_META[t],
      }))
    : [];

  const gUpNext = queue
    .slice(1, 4)
    .map((id) => visible.get(id))
    .filter((g): g is Game => !!g)
    .map((g) => capsuleOf(g, tileVM(g, ctx)));

  const gPlacedN = [...visible.keys()].filter((id) => myT[String(id)] !== undefined).length;
  const gqn = s.gq.toLowerCase().replace(/[^a-z0-9]/g, '');
  const gMatches = gqn
    ? s.catalog
        .filter((g) => g.n.toLowerCase().replace(/[^a-z0-9]/g, '').indexOf(gqn) >= 0 && !pots.includes(g.id))
        .slice(0, 4)
        .map((g) => ({ key: '' + g.id, id: g.id, name: g.n.toUpperCase(), yearTxt: fmtD(g) }))
    : [];

  // A past year's fill works WITHOUT Steam (AA phase 5) — it falls back to that
  // year's releases — so the link is not its gate. With Steam linked it does
  // better than guess: achievement unlocks carry their own dates (3070).
  const steamLinked = !!(s.steamLink && s.steamLink.linked && s.steamLink.profilePublic);
  const canSteam = pastYear || steamLinked;
  // G-05. The label carries the busy state rather than a spinner, matching the
  // sync sheet's CHECKING… and PtAdd's checkLbl — this tree has no spinner
  // component and inventing one for a single button would be a bigger deviation
  // than the copy swap. Both trees read it so neither can drift.
  const steamBusy = s.steamFilling;
  // The SAME vocabulary carries the library check (3070): the server is asking
  // Steam about every game they own, which takes about a minute and a half, and
  // the honest thing to show meanwhile is how far it has got. The button stays
  // tappable — a tap re-asks, which is also the retry if the pass stalled.
  const checking = steamLinked && !steamBusy && !!s.steamBackfill?.waiting;
  const checkTotal = s.steamBackfill?.total ?? 0;
  const checkLbl = checkTotal > 0
    ? '⟳ CHECKING YOUR STEAM LIBRARY · ' + (s.steamBackfill?.done ?? 0) + ' OF ' + checkTotal
    : '⟳ CHECKING YOUR STEAM LIBRARY…';

  // The TO-DO legend, keyed to the row labels it explains — both screens print
  // it verbatim, so it is derived from `G_ROW_META` rather than spelled twice.
  // `X` shows the glyph it is labelled with, matching `gRows[].big`.
  const gLegendTxt = TIERS.map((t) => (t === 'X' ? '✕' : t) + ' ' + G_ROW_META[t].sub).join(' · ');

  // The mobile TO-DO panel is the unplace drop target — desktop's rail already
  // was (`todoRailBord`), mobile had no way back off the board but the move
  // sheet. Its drag state lives in the store, not in a screen, so unlike the
  // desktop lane's §5.5 helpers this is derived here rather than passed in.
  //
  // The border stays 1px through all three states so a drag cannot reflow the
  // page under the pointer; the invite is carried by colour, and the hovered
  // state adds the accent-2 wash. Only a placed game can be dragged at all on
  // this screen (`capPD` is bound to tier capsules only), so — unlike the
  // desktop rail, whose featured cover is also a drag source — there is no
  // unplaced-drag case to withhold the invite from.
  const gTodoOver = !!(gDrag && s.dragOver && s.dragOver.row === GOTY_TODO_ROW);
  const gTodoBd = '1px dashed ' + (gDrag ? 'var(--acc2)' : 'var(--gt-border-3)');
  const gTodoBg = gTodoOver ? 'var(--acc2Soft)' : 'var(--gt-panel)';

  return {
    gRows,
    gLegendTxt,
    gTodoRow: GOTY_TODO_ROW,
    gTodoBd,
    gTodoBg,
    gFeat,
    hasGFeat: !!featG,
    gAllDone: !featG && visible.size > 0,
    gBtns,
    gLeftTxt: gTodoN + ' LEFT',
    gUpNext,
    hasGUpNext: gUpNext.length > 0,
    hasGMore: gTodoN > 4,
    gMoreTxt: '+' + (gTodoN - 4) + ' MORE',
    gTodoN,
    gq: s.gq,
    gMatches,
    hasGMatches: gMatches.length > 0,
    gPotsTxt: pots.length + ' POTENTIALS · ' + gPlacedN + ' PLACED',
    gPlacedN,
    bq: s.bqBy.goty,
    canSteam,
    steamBusy,
    // ONE LIST PER YEAR (AA phase 5): the years you have a list for, current
    // year first, then `+ ANOTHER YEAR ▾` offering every year back to 1990 not
    // already on the row.
    gotyYear: s.gotyYear,
    yearRow: [...new Set([GOTY_YEAR, ...s.gotyYears, s.gotyYear])]
      .sort((a, b) => b - a)
      .map((y) => ({
        key: 'y' + y,
        y,
        l: String(y),
        on: y === s.gotyYear,
        bd: y === s.gotyYear ? 'var(--acc)' : 'var(--gt-border-2)',
        clr: y === s.gotyYear ? 'var(--acc)' : 'var(--gt-text-5)',
        bg: y === s.gotyYear ? 'var(--accSoft)' : 'transparent',
      })),
    yearMenuOpen: s.gotyYearMenu,
    moreYears: Array.from({ length: GOTY_YEAR - 1990 + 1 }, (_, i) => GOTY_YEAR - i).filter(
      (y) => !s.gotyYears.includes(y) && y !== s.gotyYear && y !== GOTY_YEAR,
    ),
    pastYear,
    steamLbl: steamBusy
      ? '⟳ READING YOUR STEAM LIBRARY…'
      : checking
        ? checkLbl
        // A past year reads FROM STEAM once there is a Steam to read from; the
        // releases-only wording stays for the member who has not linked one.
        : pastYear && !steamLinked
          ? '⟳ FILL FROM ' + s.gotyYear + ' RELEASES'
          : '⟳ AUTO-FILL FROM STEAM · ' + s.gotyYear,
    /** The library check is under way — desktop's tooltip reads this, because
     *  it has no sub-line to carry the explanation (`gotySteamTitle`). */
    steamChecking: checking,
    steamBd: canSteam ? 'var(--acc)' : 'var(--gt-border-2)',
    // Dimmed while busy OR checking, so the button reads as "working" rather
    // than "ready".
    steamClr: steamBusy || checking ? 'var(--gt-muted)' : canSteam ? 'var(--acc)' : 'var(--gt-faint)',
    steamSub: steamBusy
      ? 'ASKING STEAM WHAT YOU PLAYED — THIS TAKES A FEW SECONDS'
      : checking
        ? 'STEAM MAKES US ASK GAME BY GAME — ABOUT A MINUTE. YOU CAN LEAVE; WE WILL FINISH WITHOUT YOU.'
        : canSteam
          ? pastYear
            ? steamLinked
              ? 'YOUR STEAM ACHIEVEMENTS DATE WHAT YOU PLAYED IN ' + s.gotyYear + ' — THE REST ARE ITS RELEASES, A GUESS'
              : 'STEAM KEEPS NO PLAYTIME LEDGER FOR A PAST YEAR — THESE ARE ' + s.gotyYear + "'S RELEASES, A GUESS"
            : 'PULLS YOUR ' + s.gotyYear + ' PLAYTIME FROM YOUR LINKED STEAM ACCOUNT'
          : 'LINK STEAM + MAKE IT PUBLIC TO AUTO-FILL',
    ...filterButton(s),
  };
}

// ---------------------------------------------------------------------------
// GOTY auto-fill picker (G-07)
// ---------------------------------------------------------------------------

/** What each signal is called in front of a member, and whether it is something
 *  we SAW or something we GUESSED. Mirrors `_shared/gotyfill.ts#SIGNAL_ORDER`;
 *  the server ranks by that order and these are the same five, in it. */
const SIGNAL_LABEL: Record<GotySignal, string> = {
  played: 'STEAM SAYS YOU PLAYED IT',
  achievement: 'ACHIEVEMENT UNLOCKED',
  ledger: 'WE WATCHED YOUR PLAYTIME GROW',
  recent: 'PLAYED IN THE LAST TWO WEEKS',
  released: 'CAME OUT THIS YEAR — WE ARE GUESSING',
};

const PICK_MONTHS = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
];

function pickWhen(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  return PICK_MONTHS[d.getUTCMonth()] + ' ' + String(d.getUTCDate()).padStart(2, '0');
}

/** Lifetime playtime, in the roundest honest unit. Under an hour stays minutes
 *  so a 20-minute game does not render as the same `0H` as a never-launched one. */
function pickHrs(min: number): string {
  if (min <= 0) return '';
  if (min < 60) return min + 'M';
  return Math.round(min / 60) + 'H';
}

/**
 * The auto-fill picker's view model, shared verbatim by the mobile sheet and the
 * desktop modal (the `tagPickerVM` precedent — one VM, two trees, geometry the
 * only difference).
 *
 * Deliberately NOT folded into `gotyVM`: that selector is pinned by the golden
 * fixture in `data/fixtures/selectors.fixture.json`, and this is new surface
 * with no prototype to be faithful to. Adding an export is safe; growing
 * `gotyVM` would break the fixture for no gain.
 */
export function gotyPickVM(s: QdStore) {
  const on = new Set(s.gotyPickOn);

  const row = (
    g: { appid: number; name: string; playtimeMin: number },
    signal: GotySignal | null,
    when: number,
  ) => {
    const ticked = on.has(g.appid);
    const meta = [
      signal ? SIGNAL_LABEL[signal] : 'FROM YOUR LIBRARY',
      pickWhen(when),
      pickHrs(g.playtimeMin),
    ].filter((x) => x !== '');
    return {
      key: 'gp' + g.appid,
      appid: g.appid,
      name: g.name || 'STEAM APP #' + g.appid,
      meta: meta.join(' · '),
      on: ticked,
      // The app has no checkbox anywhere; the toggle idiom is a bordered row
      // whose border, text and mark flip to the accent together.
      mark: ticked ? '✓' : '',
      bd: ticked ? 'var(--acc)' : 'var(--gt-border-3)',
      clr: ticked ? 'var(--gt-text)' : 'var(--gt-text-5)',
      metaClr: signal === 'released' ? 'var(--gt-ghost-2)' : 'var(--gt-faint)',
    };
  };

  const rows = s.gotyPickCands.map((k) => row(k, k.signal, k.evidenceAt));

  // The search list is the REST of the library: a game already offered above
  // must not appear twice with two different explanations.
  const offered = new Set(s.gotyPickCands.map((k) => k.appid));
  const q = s.gotyPickQ.trim().toLowerCase();
  const libAll = s.gotyPickLib.filter((g) => !offered.has(g.appid));
  const libRows = (q === '' ? libAll : libAll.filter((g) => g.name.toLowerCase().includes(q)))
    // Most-played first is how the server sorted it, which is the useful order
    // when browsing and irrelevant when searching by name.
    .slice(0, 40)
    .map((g) => row(g, null, 0));

  const guessN = s.gotyPickCands.filter((k) => !k.confident).length;
  const n = s.gotyPickOn.length;

  // ONE line, and deliberately not a diagnostic.
  //
  // This started as four lines built from `counts` — library size, how many
  // playtimes came back, which endpoint withheld what, and how far the
  // achievement sweep got. All true, and all answering a question the member
  // never asked. What they need is why the list is a GUESS and what it was
  // built from; the mechanism behind it is our problem, not theirs.
  //
  // Still not a claim the backend cannot support: `steamFill` opens this picker
  // only when Steam refused to date the library, so the two-week window really
  // is the only dated history we were given, and achievements and release dates
  // really are what the rest was reconstructed from. `counts` still travels in
  // the envelope for support and is no longer rendered anywhere.
  const why: string[] = [
    'STEAM ONLY SHARES THE LAST TWO WEEKS OF PLAY HISTORY, SO WE TOOK OUR BEST ' +
      'GUESS BASED ON YOUR ACHIEVEMENT HISTORY AND GAME RELEASE DATES.',
  ];

  return {
    open: s.gotyPickOpen,
    title: 'WHAT DID YOU PLAY IN ' + s.gotyYear + '?',
    // A past year's list is ALL release-date guesses when there was no Steam to
    // ask, and the old note says so. Once achievements have dated some of the
    // rows (3070), calling the whole list a guess would undersell the rows that
    // are not — so the note follows what is actually on the list.
    note: s.gotyYear < GOTY_YEAR
      ? s.gotyPickCands.some((k) => k.signal !== 'released')
        ? 'YOUR STEAM HISTORY DATES THE TICKED ONES — THE REST CAME OUT THAT YEAR'
        : 'STEAM KEEPS NO LEDGER FOR A PAST YEAR — THESE CAME OUT THAT YEAR'
      : 'STEAM WOULD NOT SAY — HERE IS OUR BEST GUESS',
    why,
    rows,
    hasRows: rows.length > 0,
    // Reached when every signal came up empty. The library search below is then
    // the only path, and saying so is better than an unexplained blank list.
    emptyTxt: 'WE COULD NOT DATE ANYTHING AUTOMATICALLY — SEARCH YOUR LIBRARY BELOW.',
    guessTxt:
      guessN > 0
        ? guessN + ' OF THESE ARE GUESSES FROM RELEASE DATES — LEFT UNTICKED ON PURPOSE'
        : '',
    hasGuess: guessN > 0,
    libRows,
    hasLibRows: libRows.length > 0,
    libNoHit: q !== '' && libRows.length === 0,
    libNoHitTxt: 'NOTHING IN YOUR LIBRARY MATCHES THAT',
    q: s.gotyPickQ,
    searchPlaceholder: 'SEARCH YOUR ' + s.gotyPickLib.length + ' STEAM GAMES…',
    n,
    busy: s.gotyPickBusy,
    canConfirm: n > 0 && !s.gotyPickBusy,
    confirmLbl: s.gotyPickBusy ? 'ADDING…' : n === 0 ? 'PICK SOME GAMES' : 'ADD ' + n + ' TO MY BOARD',
    confirmBd: n > 0 && !s.gotyPickBusy ? 'var(--acc)' : 'var(--gt-border-2)',
    confirmClr: s.gotyPickBusy
      ? 'var(--gt-muted)'
      : n > 0
        ? 'var(--acc)'
        : 'var(--gt-faint)',
  };
}

// ---------------------------------------------------------------------------
// playtests
// ---------------------------------------------------------------------------

export interface PtAvatar {
  key: string;
  k: string;
  i: string;
  bg: string;
  hasAv: boolean;
  avBg: string;
  name: string;
  meta: string;
}

function avRow(e: Playtest, arr: readonly User[], kind: 'int' | 'st', meKey: string): PtAvatar[] {
  return arr.map((u) => {
    const c = chipOf(u);
    const st = e.st[u.k];
    return {
      key: u.k,
      k: u.k,
      i: c.ini,
      bg: c.hueBg,
      hasAv: c.hasAv,
      avBg: c.avBg,
      name: (u.name + (meKey && u.k === meKey ? ' — YOU' : '')).toUpperCase(),
      meta: kind === 'int' ? 'MARKED ' + ptFmt(e.int[u.k] ?? 0) : (st ? st.m : '').toUpperCase(),
    };
  });
}

export function playtestsVM(s: QdStore, now: number = Date.now()) {
  const meKey = s.session;
  // Hoisted out of the predicate: `ptPass` runs once per playtest and the lens
  // carries a 446-entry map.
  const ptTags = tagLensOf(s, 'playtests');
  const all = s.pts.filter((e) =>
    ptPass(e, {
      q: s.ptQ,
      chips: s.ptChips,
      meKey,
      flt: s.fltBy.playtests,
      gByAppid: (a) => s.gByAppid(a),
      pts: s.pts,
      tags: ptTags,
      owners: s.ownBy,
    }),
  );
  const gs: Record<PtGroup, Playtest[]> = { live: [], up: [], tba: [], ended: [] };
  for (const e of all) gs[ptGroupOf(e, now)].push(e);

  const rowOf = (e: Playtest) => {
    const a = ptArt(e, s.assets);
    const b = ptBuckets(e, s.users);
    const myStatus = meKey && e.st[meKey] ? e.st[meKey].s : '';
    const on = !!(meKey && e.int[meKey]);
    // `ask` is the SIGNUP hand-off arm only. It is what raises the
    // "DID YOU REQUEST ACCESS?" confirm when the tab opens — the only moment
    // the app can learn about a request, since Steam publishes grants and never
    // queues (selectors.3e.ts#ptAskVM has the argument in full). The `in` arm
    // opts out: INSTALL is somebody who is already through.
    let p: { pTxt: string; pBd: string; pClr: string; pBg: string; u: string; ask: boolean };
    if (myStatus === 'in') {
      p = {
        pTxt: e.kind === 'steam' && e.url ? 'IN LIBRARY — INSTALL ↗' : "YOU'RE IN ✓",
        pBd: 'var(--acc)',
        pClr: 'var(--acc)',
        pBg: 'var(--accSoft)',
        u: e.url,
        ask: false,
      };
    } else if (myStatus === 'req') {
      p = {
        pTxt: 'REQUESTED ✓',
        pBd: 'var(--gt-border-3)',
        pClr: 'var(--gt-text-5)',
        pBg: 'transparent',
        u: '',
        ask: false,
      };
    } else if (e.status === 'open' && e.url && !e.ended) {
      p = {
        pTxt: e.kind === 'off' ? 'SIGN UP ↗' : 'REQUEST ACCESS ↗',
        pBd: 'var(--acc)',
        pClr: 'var(--gt-ink)',
        pBg: 'var(--acc)',
        u: e.url,
        ask: true,
      };
    } else {
      p = {
        pTxt: e.ended ? 'ENDED' : 'NO PUBLIC SIGNUP',
        pBd: 'var(--gt-border-3)',
        pClr: 'var(--gt-text-5)',
        pBg: 'transparent',
        u: '',
        ask: false,
      };
    }
    return {
      key: e.id,
      id: e.id,
      appid: e.appid,
      name: e.name,
      dev: e.dev.toUpperCase(),
      note: e.note,
      ...a,
      ...ptWinBox(e, now, ptFmt),
      chips: [
        {
          key: 'c1',
          t: e.kind === 'off' ? 'OFF-STEAM · ' + e.srcLabel : 'STEAM',
          bd: 'var(--gt-border-2)',
          c: 'var(--gt-text-5)',
          bg: 'transparent',
        },
        { key: 'c2', t: e.party.lbl, bd: 'var(--gt-border-2)', c: 'var(--gt-text-5)', bg: 'transparent' },
      ],
      strips: [
        { key: 'i', l: 'INTERESTED', lc: 'var(--acc)', tri: true, avs: avRow(e, b.int, 'int', meKey), none: !b.int.length },
        { key: 'r', l: '⋯ REQUESTED', lc: 'var(--gt-text-3)', tri: false, avs: avRow(e, b.req, 'st', meKey), none: !b.req.length },
        { key: 'a', l: '✓ HAS ACCESS', lc: 'var(--acc)', tri: false, avs: avRow(e, b.inn, 'st', meKey), none: !b.inn.length },
      ],
      squads: ptSquads(e),
      pTxt: p.pTxt,
      pBd: p.pBd,
      pClr: p.pClr,
      pBg: p.pBg,
      pUrl: p.u,
      pAsk: p.ask,
      tTxt: on ? 'INTERESTED' : 'MARK INTEREST',
      tBd: on ? 'var(--acc)' : 'var(--gt-border-3)',
      tClr: on ? 'var(--acc)' : 'var(--gt-text-3)',
      tBg: on ? 'var(--accSoft)' : 'transparent',
    };
  };

  const groups: Array<{ key: string; t: string; tc: string; n: string; rows: ReturnType<typeof rowOf>[] }> = [];
  if (gs.live.length)
    groups.push({ key: 'l', t: 'LIVE NOW', tc: 'var(--acc)', n: gs.live.length + ' WINDOW' + (gs.live.length === 1 ? '' : 'S'), rows: gs.live.map(rowOf) });
  if (gs.up.length)
    groups.push({ key: 'u', t: 'UPCOMING WINDOWS', tc: 'var(--gt-text)', n: gs.up.length + ' DATED', rows: gs.up.map(rowOf) });
  if (gs.tba.length)
    groups.push({ key: 't', t: 'OPEN SIGNUPS — NO DATED WINDOW', tc: 'var(--gt-text)', n: '' + gs.tba.length, rows: gs.tba.map(rowOf) });
  if (s.ptShowEnded && gs.ended.length)
    groups.push({ key: 'e', t: 'ENDED', tc: 'var(--gt-muted-2)', n: '' + gs.ended.length, rows: gs.ended.map(rowOf) });

  const linked = !!(s.steamLink && s.steamLink.linked);
  const chip = freshnessChip(s, now);

  return {
    groups,
    /**
     * Every Steam appid on screen, for the screens' `queueAssets` effect.
     *
     * Playtests is the last tile surface that never asked `/api/steam/items`
     * about its own rows, and it is the surface that needs it MOST: a playtest
     * is an unreleased game, so its appid is recent, and `ptArtCands` can only
     * guess the legacy CDN shapes — which 404 for anything recent. The hashed
     * `store_item_assets` URL that does resolve arrives on this record and
     * nowhere else. Same gap `screens/Goty.tsx` closed for GOTY potentials.
     */
    appids: groups.flatMap((g) => g.rows.map((r) => r.appid)).filter((a) => a > 0),
    empty: groups.length === 0,
    hasEndedNote: !s.ptShowEnded && gs.ended.length > 0,
    endedNote:
      gs.ended.length + ' ENDED — ' + gs.ended.slice(0, 2).map((e) => e.name.toUpperCase()).join(' · ') + ' · SHOW ▾',
    endLbl: s.ptShowEnded ? 'HIDE ENDED' : 'SHOW ENDED',
    endBd: s.ptShowEnded ? 'var(--acc)' : 'var(--gt-border-2)',
    endClr: s.ptShowEnded ? 'var(--acc)' : 'var(--gt-muted)',
    endBg: s.ptShowEnded ? 'var(--accSoft)' : 'transparent',
    trackedN: '' + s.pts.length,
    /** §5.4: the sync chip IS the freshness chip now — it cannot lie. */
    syncLbl: chip.txt,
    syncDot: linked ? 'var(--acc)' : 'var(--acc2)',
    syncBd: linked ? 'var(--gt-border-2)' : 'var(--acc2)',
    syncClr: linked ? 'var(--gt-muted)' : 'var(--acc2)',
    q: s.ptQ,
    chips: [
      { id: 'canjoin', l: 'CAN JOIN NOW' },
      { id: 'coop', l: 'CO-OP' },
      { id: 'off', l: 'OFF-STEAM' },
      { id: 'fin', l: 'FRIENDS HAVE ACCESS' },
    ].map((c) => ({ key: c.id, id: c.id, l: c.l, ...onOff(s.ptChips.includes(c.id)) })),
    browse: PT_BROWSE.filter((x) => {
      const nq = s.ptBq.trim().toLowerCase();
      return !nq || x.name.toLowerCase().includes(nq) || x.dev.toLowerCase().includes(nq);
    }).map((x) => ({
      key: x.name,
      ...x,
      devUp: x.dev.toUpperCase(),
      tracked: s.pts.some((e) => (x.appid && e.appid === x.appid) || e.name === x.name),
    })),
    tracked: s.pts.map((e) => {
      const g = ptGroupOf(e, now);
      const myStatus = meKey && e.st[meKey] ? e.st[meKey].s : '';
      const stTxt =
        (myStatus === 'in'
          ? 'IN'
          : myStatus === 'req'
            ? 'REQUESTED'
            : e.status === 'none'
              ? 'NO PUBLIC SIGNUP'
              : g === 'ended'
                ? 'ENDED'
                : 'OPEN') +
        (g === 'live' ? ' · LIVE NOW' : '') +
        (e.kind === 'off' ? ' · OFF-STEAM' : '');
      const byU = s.user(e.by);
      return {
        key: e.id,
        id: e.id,
        n: e.name,
        st: stTxt,
        dim: g === 'ended' ? '.55' : '1',
        src: e.auto ? 'AUTO — GAMES TONIGHT?' : meKey && e.by === meKey ? 'YOU' : byU ? 'ADDED BY ' + byU.name.toUpperCase() : '—',
      };
    }),
    ...filterButton(s),
  };
}

// ---------------------------------------------------------------------------
// game detail
// ---------------------------------------------------------------------------

export interface DetailVM extends TileVM, LiveStatsVM {
  found: boolean;
  metaTxt: string;
  descTxt: string;
  tagChips: Array<{ key: string; l: string }>;
  buyUrl: string;
  buyTxt: string;
  /**
   * The YouTube *search page* — never an embed. The `listType=search` embed URL
   * that sat beside it is gone with the four iframes that bound it (P10-01).
   * `MediaCarousel` renders the same string as its `SEARCH YOUTUBE ↗` footer
   * link, but derives it from `mediaVM` so the carousel needs no host VM; this
   * field stays because the Detail screen is entitled to the escape hatch
   * whether or not the carousel is on it.
   */
  ytLink: string;
  hasChart: boolean;
  noChart: boolean;
  noChartWhy: string;
  bars: BarsVM['bars'];
  /**
   * The Game Detail V2 stats band.
   *
   * Four of the five cells have **no backend job at all**, and the design draws
   * every one of them filled — with seeded sample values,
   * labelled `SEEDED` in the prototype. So the whole hazard of this band is the
   * temptation to fill a slot with a neighbouring number, and the specific
   * temptation is REVIEWS: we hold a real all-time score, the design puts
   * `LAST 30D` beside `ALL TIME`, and sliding the one we have under the label
   * we cannot support would be exactly the class of false claim this port keeps
   * removing. So a pending cell renders its NAMED JOB and never a value.
   */
  stats: StatCell[];
  /** The series is a demo fixture, not a measurement — drives the SAMPLE chip. */
  chartIsSample: boolean;
  // ---- the 12-month price sparkline (migration 3045, DETAIL-BAND.md lane C) ----
  /** Sparkline buckets; a bucket keeps its cheapest day (see `mkPriceBars`). */
  priceBars: PriceBarsVM['bars'];
  hasPriceChart: boolean;
  noPriceChart: boolean;
  /** FREE / NO STEAM APPID / UNRELEASED / TRACKING… / LOADING… */
  noPriceChartWhy: string;
  /** `NOW $29.99 · LOW $9.99 · TRACKING 12D` */
  priceFoot: string;
  /** `$4.94`, withheld until the price has actually moved (see the VM note). */
  priceLowTxt: string;
  /** `DEC 2025`. Empty exactly when `priceLowTxt` is. */
  priceLowWhen: string;
  hasPriceLow: boolean;
  /** The series is a demo fixture — only `LocalRepository` can set it. */
  priceIsSample: boolean;
  // --- V2 left rail --------------------------------------------------------
  /**
   * The meta line's platform atom. `metaTxt` glues three of these together for
   * the old single-line header; V2 splits them across two columns, so the atom
   * has to be reachable on its own rather than re-derived by a screen.
   */
  platTxt: string;
  /** PLAY TOGETHER's figure — exact where `playersTxt` bands. See `capNumTxt`. */
  capNum: string;
  /** One cell per crew member; the first `min(mx, crew)` are lit. */
  seatCells: Array<{ key: string; bg: string; bd: string }>;
  /** `4 OF YOUR 6 AT ONCE` — the seat meter's caption. DESKTOP ONLY since the
   * 2026-08-13 trim: the phone cell dropped this line ("the seat-cell grid
   * already says it") and signals tap-to-edit with a chevron instead
   * (`design/GT Mobile Discover.dc.html`, DESIGN-CHANGELOG 2026-08-13). */
  fitTxt: string;
  /** The phone cell's only remaining third-line state — `NO COUNT — TAP TO SET
   * ONE` when no source has a max, `''` otherwise (a banded `MANY` row has a
   * count, so it gets the chevron and no line; mirror gates on `!mx` too). */
  capNudge: string;
  /**
   * The appid the player-count editor writes to, or `0` when there is none.
   *
   * `0` makes the PLAY TOGETHER cell inert, and that is the D-02 gate in this
   * cell: a CheapShark-only row has no appid at all, and the RPC's only possible
   * answer for it is `UNKNOWN_GAME`. A control whose sole outcome is an error
   * should not invite the press.
   */
  capAppid: number;
  /** The cell's tooltip: what we think the count is, and that it can be fixed. */
  capTip: string;
  /** RELEASED. Full day once the release-day lane lands; the month until then. */
  dateFull: string;
  /** TAGS as one mono line — V2 cuts the chip row. */
  tagsTxt: string;
  /** Whether a curated all-time peak exists at all — gates the rail's row. */
  hasPeak: boolean;
  /**
   * `12K PLAYING NOW` for the chart header, `''` when no fresh sample backs it.
   * This is where the live half of the old `peakTxt` composite survives V2's cut
   * of the PEAK line — same gate (`live.ccuFresh`), same wording as the feed's.
   */
  nowTxt: string;
  /** `TRACKING 3D` while the series is younger than the window, else `''`. */
  trackedTxt: string;
  /** `ADDED BY MARCUS · 2D AGO` on the meta line; `''` when not on the list. */
  addedTxt: string;
  hasAdded: boolean;

  // --- V2 FRIENDS panel ----------------------------------------------------
  /** The verdict badge letter, or `—`. No quorum — see `detailVerdict`. */
  groupBig: string;
  /** `MUST PLAY` / `NO CALLS YET`. */
  groupSub: string;
  /** The badge's background colour. */
  groupBg: string;
  /** `2 OF 6 CALLED`, and `''` when the game is not on the list. */
  calledTxt: string;
  /** The four joined 46px call cells; the selected one lifts above its siblings. */
  callCells: Array<{
    key: VoteValue;
    g: string;
    tip: string;
    ml: string;
    pos: 'relative' | 'static';
    z: number | 'auto';
    bd: string;
    bg: string;
    clr: string;
  }>;
  /** `S · MUST PLAY`, or `YOU HAVEN'T CALLED IT YET`. */
  myTxt: string;
  myClr: string;
  /** `COMMENTS · 3`. */
  commentsTxt: string;
  /** The panel footer's crew-wide remove, armed or not (`listRmArm`). */
  listRmTxt: string;
  listRmClr: string;

  inList: boolean;
  notInList: boolean;
  listBtnTxt: string;
  listBtnBd: string;
  listBtnClr: string;
  listBtnBg: string;
  /**
   * `+ ADD TO MY BACKLOG` / `✓ ON YOUR BACKLOG` (DESIGN-CHANGELOG 2026-09-03
   * "Detail cleanup"): a PERSONAL action, so it sits with VIEW ON STEAM in the
   * left rail and not in the party panel. Yours alone — nobody else sees it.
   */
  soloOn: boolean;
  soloTxt: string;
  soloTip: string;
  soloClr: string;
  /**
   * The score control (DESIGN-CHANGELOG 2026-09-12 "GAME DETAIL carries the
   * score control"): `SCORE THIS GAME` + `—` unscored, `MY SCORE` + the number
   * once set. The box's border is `--gt-border-2` unscored and the score's own
   * colour once set, so the control states the value twice — number and edge
   * — without a second label. Opens the one YOUR SCORE modal; any game.
   */
  scoreTxt: string;
  scoreNum: string;
  scoreClr: string;
  scoreBd: string;
  scoreTip: string;
  /**
   * RECOMMEND ▸ (AA phase 3; `design/GT Discover.dc.html:182,555`): with
   * nobody else in the party the control stays, inert, and the tip says so.
   */
  sendOn: boolean;
  sendTip: string;
  /** the bell (AA phase 4): solid + count when something is armed, hollow when not */
  bellOn: boolean;
  bellN: string;
  bellTip: string;
  bellBd: string;
  bellClr: string;
  comments: Array<{
    key: string;
    name: string;
    ini: string;
    hueBg: string;
    hasAv: boolean;
    avBg: string;
    agoTxt: string;
    parts: ReturnType<typeof commentParts>;
  }>;
  hasComments: boolean;
}

/**
 * The third rung of the TAGS ladder: Steam's own tag IDS, named from the
 * vocabulary the picker already loads.
 *
 * A game reached through Discover's Steam search has neither of the first two.
 * `Game.tags` is the crew's editorial word list, empty on anything the catalog
 * has not curated; `Game.stags` is the `games.steam_tags` column, so it exists
 * only for rows that came out of our own database. A live browse row has only
 * `tids` — the ≤20 weight-ordered ids `/api/steam/browse` carries — so opening
 * a searched game showed an empty TAGS row while its catalog neighbours on the
 * same screen showed a full one.
 *
 * `tids` and `tagVocab` are the SAME vocabulary (Steam's `GetTagList`), which
 * is what makes this legal: it is the join `games.steam_tags` already is
 * server-side, done client-side for a row that never went through the database.
 * The editorial namespace is still never touched — ids are named from Steam's
 * table and keyed `ti:`, the way `stags` is keyed `st:`.
 *
 * An id the vocabulary cannot name is DROPPED rather than rendered as `#1685`.
 * A picked filter chip shows its bare id because a chip you cannot see is a
 * filter you cannot remove; this row is not actionable, so a number would be
 * noise. That also means an unloaded vocabulary yields nothing at all, which is
 * why `openGame` asks for it.
 */
function steamTagNames(s: Pick<QdStore, 'tagVocab'>, g: Game): Array<{ key: string; l: string }> {
  if (!g.tids?.length || s.tagVocab.length === 0) return [];
  const byId = new Map(s.tagVocab.map((t) => [t.id, t.name]));
  const out: Array<{ key: string; l: string }> = [];
  for (const id of g.tids) {
    const name = byId.get(id);
    if (name) out.push({ key: 'ti:' + id, l: name.toUpperCase() });
  }
  return out;
}

export function detailVM(s: QdStore, id: GameId, now: number = Date.now()): DetailVM | null {
  const dg = s.gById(id);
  if (!dg) return null;
  const ctx = tileCtx(s);
  const t = tileVM(dg, ctx);
  const entry: ListEntry | undefined = s.list.find((e) => e.id === dg.id);
  const me = s.me();
  // Shelved by the signed-in account? (3060 / AA phase 2)
  const soloOn = !!me && (s.backlog[me.k] ?? []).some((e) => e.id === id);
  // Your score on THIS game, backlog or not (2026-09-12) — the rail's control.
  const mySc: number | null = me ? (s.scores[me.k]?.[String(id)]?.v ?? null) : null;
  const bellN = armedN(s.watch[String(id)], dg);
  // The chart reads real samples now. `dg.id`/`dg.pk` seed nothing: the Lehmer
  // PRNG that used to draw this box lives only in `LocalRepository`'s fixture
  // series, behind the `sample` flag the chip renders. Closes P6 gap 12.9.
  const series = dg.appid ? s.ccuSeries[dg.appid] : undefined;
  const seriesLoaded = series !== undefined;
  const ch = mkBars(series?.points ?? [], now);
  // The price sparkline (3045). Same three-part shape as the chart above —
  // the series, whether it has loaded, and the bars — because the empty states
  // differ and "no data" must be distinguishable from "not asked yet".
  const dFacts = dg.appid ? s.detailFacts[dg.appid] : undefined;
  const pSeries = dg.appid ? s.priceSeries[dg.appid] : undefined;
  const pLoaded = pSeries !== undefined;
  const pc = mkPriceBars(pSeries?.points ?? [], now);
  const myL = me && entry ? entry.votes[me.k] : undefined;
  const rev = revMeta(dg);
  // PLAYING NOW is a LIVE claim, and the sample has to be provably fresh to
  // make it: `ccu` is a stored number, sampled by a sweep this screen cannot
  // see. The 24-hour rule is the server's own — 2901's `why` serializer
  // refuses the same caption without `ccu_at < 24h` — and a stale or absent
  // stamp renders exactly as having no sample at all does. `liveStatsOf` holds
  // that gate for both surfaces so Detail and a Discover tile can never drift
  // into disagreeing about which rows are live; its other fields ride onto the
  // VM unrendered, for the same reason they do on a tile.
  const live = liveStatsOf(dg, now);
  // Hoisted because V2 renders the same list twice in two shapes: the chip row
  // this has always fed, and the rail's one-line `A · B · C`. Deriving the line
  // from the chips rather than re-reading `dg.tags` is what keeps the editorial
  // /`stags` fallback below from having to be stated — and agree — twice.
  const tagChips = dg.tags.length
    ? dg.tags.map((x) => ({ key: x, l: x.toUpperCase() }))
    : dg.stags?.length
      ? dg.stags.map((x) => ({ key: 'st:' + x, l: x.toUpperCase() }))
      : steamTagNames(s, dg);
  return {
    ...t,
    ...live,
    found: true,
    revTxt: rev.t,
    revClr: rev.c,
    // The expansion targets only the numeric `4P` / `2-8P` forms: `playersTxt`
    // now also answers `CO-OP` / `MULTI` from `pcats`, and a bare
    // `.replace('P', …)` would chew the P inside CO-OP.
    metaTxt:
      platTxt(dg.plat) + ' · ' + t.playersTxt.replace(/^(\d[\d-]*)P$/, '$1 PLAYERS') + ' · ' + t.dateTxt,
    // Pool-minted rows carry the pk-0 sentinel (no curated all-time peak) but a
    // `peakTxt` IS NOT OVERRIDDEN HERE, and that is the point.
    //
    // This selector used to replace `tileVM`'s bare peak with a composite that
    // read `— · 12K PLAYING NOW` on a row with no curated peak but a fresh live
    // count. That was right under the old screen's hard-coded `PEAK ` caption,
    // which the em dash answered — and wrong the moment V2 put the same field
    // under a label reading ALL-TIME PEAK, where it would state a LIVE number
    // as an all-time one.
    //
    // So the composite is gone rather than merely unread: the rail takes the
    // inherited bare `fmtPk(dg.pk)`, gated on `hasPeak` so an absent peak omits
    // the row instead of answering its own label, and the live half is `nowTxt`
    // below, under a chart header where "right now" is what the copy already
    // means. Re-introducing the override is the one edit here that would put a
    // false claim back on screen; `selectors.test.ts` walks the whole catalog
    // asserting no `peakTxt` can contain `PLAYING NOW`.
    hasPeak: !!dg.pk,
    nowTxt: dg.ccu > 0 && live.ccuFresh ? fmtPk(dg.ccu) + ' PLAYING NOW' : '',
    // The window's age, separated from `chartFoot`, which also carried a
    // 90-day-window PEAK that would duplicate the rail's all-time one — the
    // duplication V2's cut list names. The window max survives in the per-bar
    // tooltips, which already read `144K PLAYERS · TODAY`.
    trackedTxt: ch.has && ch.young ? 'TRACKING ' + ch.trackedDays + 'D' : '',
    ...railOf(s, dg, entry, tagChips, now),
    ...verdictOf(s, entry, myL),
    descTxt: dg.ds,
    // Editorial tags own the row; a pool-minted row (tags '{}' sentinel) falls
    // back to the Steam display tags so Detail is not blank. The `st:` key
    // prefix keeps these out of the editorial namespace by construction —
    // today the keys are only React list keys (Detail screens), but an
    // accidental reuse as a filter id must fail visibly rather than match
    // (`repo/steamApi.ts` on why the two vocabularies must never mix).
    tagChips,
    buyUrl: dg.appid
      ? 'https://store.steampowered.com/app/' + dg.appid
      : 'https://store.steampowered.com/search/?term=' + encodeURIComponent(dg.n),
    buyTxt: dg.up ? 'WISHLIST ON STEAM' : 'VIEW ON STEAM',
    ytLink: 'https://www.youtube.com/results?search_query=' + encodeURIComponent(dg.n + ' gameplay'),
    hasChart: ch.has,
    noChart: !ch.has,
    // Keyed on FACTS about this row, not on the old `ccu > 0` heuristic — the
    // series either has samples in the window or it does not, and we now know
    // which rather than inferring it from a live count.
    //
    // `UNRELEASED` first: an unreleased game has nobody playing it, so "no
    // history" is not the interesting thing to say. Then no appid at all — a
    // curated non-Steam row can never be swept, which is permanent rather than
    // pending, and promising a first bar would be a lie. Otherwise the sweep
    // covers it and the only honest answer is when: twice a day, so ≤12h.
    noChartWhy: dg.up
      ? 'UNRELEASED'
      : !dg.appid
        ? 'NO STEAM APPID'
        : seriesLoaded
          ? 'TRACKING — FIRST BAR AFTER THE NEXT SWEEP (≤12H)'
          : 'LOADING…',
    bars: ch.bars,
    /**
     * The chart is fabricated and says so. `LocalRepository` sets this; the
     * Supabase path cannot — it returns measurements or an empty series — so
     * production has no way to render an unlabelled invented chart. That is the
     * property the detail-page redesign needs, since it drops the unconditional
     * `SAMPLE` chip the old fake relied on.
     */
    // `rev.t` is 'NO RATING DATA' / 'WISHLIST TRENDING' / '—' when Steam did not
    // rate this game — none is a score, so none may fill the ALL TIME cell.
    // `rev.scored` is that question asked of the function that decides it; the
    // `!!dg.rp` proxy this replaces got the answer wrong in both directions (see
    // the field's doc-comment in `lib/format.ts`).
    stats: statsBand(rev30CellOf(dFacts), rev.t, rev.scored, rev.c, patchCellOf(dFacts, now)),
    chartIsSample: ch.has && !!series?.sample,
    // ---- the 12-month price sparkline (3045, docs/DETAIL-BAND.md lane C) ----
    priceBars: pc.bars,
    hasPriceChart: pc.has,
    noPriceChart: !pc.has,
    /**
     * The empty state, and the four cases are genuinely different questions.
     *
     * `FREE` first: a free game's price history is a year of zeroes, which is
     * not a chart and not a gap — it is the answer. Then no appid at all, which
     * is permanent rather than pending (a curated non-Steam row can never be
     * swept). Then unreleased, where there is no price to have had a history
     * of yet. Otherwise the nightly walk covers it and the only honest answer
     * is WHEN — `op=facts` runs once a night, so ≤24h.
     */
    noPriceChartWhy:
      dg.p === 0 && !dg.up
        ? 'FREE — NO PRICE TO TRACK'
        : !dg.appid
          ? 'NO STEAM APPID'
          : dg.up
            ? 'UNRELEASED'
            : pLoaded
              ? 'TRACKING — FIRST POINT AFTER TONIGHT\'S SWEEP (≤24H)'
              : 'LOADING…',
    priceFoot: pc.foot,
    /**
     * `LOW $4.94` / `DEC 2025`, or both empty.
     *
     * Withheld until the price has actually moved below its ceiling in the
     * window — see `mkPriceBars`. On the first night every game's low IS its
     * current price, and `LOW $29.99` beside `NOW $29.99` is a superlative
     * standing in for a history we do not have. This is migration 3045's
     * "our own samples or nothing" expressed as a render gate.
     */
    priceLowTxt: pc.lowTxt,
    priceLowWhen: pc.lowWhen,
    hasPriceLow: !!pc.lowTxt,
    /** The series is a demo fixture — only `LocalRepository` can set it. */
    priceIsSample: pc.has && !!pSeries?.sample,
    inList: !!entry,
    notInList: !entry,
    listBtnTxt: entry ? '✓ ON THE LIST — REMOVE' : '+ ADD TO GAMES TONIGHT?',
    listBtnBd: entry ? 'var(--gt-border-3)' : 'var(--acc)',
    listBtnClr: entry ? 'var(--gt-text-5)' : 'var(--acc)',
    listBtnBg: entry ? 'transparent' : 'var(--accSoft)',
    soloOn,
    scoreTxt: mySc === null ? 'SCORE THIS GAME' : 'MY SCORE',
    scoreNum: mySc === null ? '—' : String(mySc),
    scoreClr: scoreClr(mySc),
    scoreBd: mySc === null ? 'var(--gt-border-2)' : scoreClr(mySc),
    scoreTip: mySc === null ? 'PUT A NUMBER ON IT — 0 TO 10 · THE PARTY SEES IT' : 'SCORED ' + mySc + ' — CHANGE IT',
    soloTxt: soloOn ? 'IN YOUR BACKLOG' : 'ADD TO MY BACKLOG',
    soloTip: soloOn ? 'YOURS ALONE — CLICK TO TAKE IT OUT' : 'YOUR OWN BACKLOG — NOBODY ELSE IN THE PARTY SEES IT',
    soloClr: soloOn ? 'var(--gt-text-5)' : 'var(--gt-text-2)',
    bellOn: bellN > 0,
    bellN: bellN > 0 ? String(bellN) : '',
    bellTip: bellN > 0 ? 'WATCHING ' + bellN + ' — CHANGE WHAT WAKES YOU' : 'TELL ME WHEN — NOTHING WATCHED YET',
    bellBd: bellN > 0 ? 'var(--acc)' : 'var(--gt-border-2)',
    bellClr: bellN > 0 ? 'var(--acc)' : 'var(--gt-text-4)',
    sendOn: s.users.length > 1,
    sendTip: s.users.length > 1 ? 'PICK ANYONE IN THE PARTY, WRITE THEM A NOTE' : 'NOBODY ELSE IN THE PARTY YET',
    // YOUR CALL — the whole ladder, always, with the current call filled in.
    comments: entry
      ? entry.comments.map((cm) => {
          const c = chipOf(s.user(cm.by));
          return {
            key: cm.id,
            name: c.name.toUpperCase(),
            ini: c.ini,
            hueBg: c.hueBg,
            hasAv: c.hasAv,
            avBg: c.avBg,
            agoTxt: ago(cm.at, now),
            parts: commentParts(cm.t),
          };
        })
      : [],
    hasComments: !!(entry && entry.comments.length),
  };
}

// ---------------------------------------------------------------------------
// media carousel
// ---------------------------------------------------------------------------

/**
 * One tile. A discriminated union rather than a widened struct with optional
 * fields, because the three sources have genuinely different render paths — an
 * iframe, a `<video>`, an `<img>` — and `kind` is what a component switches on.
 * A shared shape would have made "which fields are real for this tile?" a
 * runtime question.
 */
/**
 * One cell of the Game Detail V2 stats band.
 *
 * `pending` is not a styling flag — it is the difference between reporting a
 * measurement and naming a job that does not exist yet. A pending cell has NO
 * value; `why` is what renders in its place, and it names the job by the name
 * it will have when somebody builds it, so the copy stays checkable against the
 * code (`docs/DISCOVERY.md`'s provenance rule).
 */
export interface StatCell {
  key: string;
  label: string;
  /** `''` whenever `pending` — a pending cell must not carry a number at all. */
  value: string;
  /** Sub-label under a real value, e.g. `ALL TIME · STEAM`. */
  caption: string;
  pending: boolean;
  /** The job we are waiting on. Empty when the cell has a real value. */
  why: string;
  /**
   * The colour `value` renders in — `var(--gt-text)` for a plain figure, and
   * for the review cell the RUNG's own colour, the same `revMeta().c` the hero
   * review line above it already uses (`revClr`).
   *
   * A cell carries this rather than a renderer deciding, because the two trees
   * would otherwise each need the rung ladder to pick a colour from, and the
   * one thing `REV_RUNGS` must not become is a table that exists in three
   * places. Never applied to `why`: a pending cell's copy is muted by the
   * renderer and a rung colour on it would read as a value.
   */
  color: string;
}

/**
 * The band, in the design's order. Everything except the all-time review score
 * is pending, and each names the job rather than the absence.
 *
 * Sources checked 2026-08-13 rather than assumed:
 *  - 30-day reviews — nothing calls Steam's `appreviews` with a day range;
 *    `steam_facts.review_pct` is all-time (`summary_filtered`), and
 *    `summary_unfiltered` is the review-bomb toggle, NOT a time window.
 *  - median playtime — THE CELL IS GONE (2026-08-27). SteamSpy stopped
 *    publishing it: `median_forever` and `average_forever` are 0 for Divinity:
 *    Original Sin 2, Counter-Strike 2, Baldur's Gate 3 and Dota 2, measured
 *    2026-08-26. The fields are present and zeroed, so a job written against
 *    them would run cleanly, write zeros, and render `0H` on every game.
 *
 *    A CREW MEDIAN IS NOT THIS CELL WITH A SMALLER SAMPLE, which is the reason
 *    it was cut rather than re-sourced. `steam_owned_apps.playtime_min` is
 *    swept nightly and a median over it is trivial SQL — but the design's own
 *    caption, `LIFETIME, NOT PER SESSION`, asks how big a commitment the game
 *    is. A crew median answers how much WE have played it. Same slot, different
 *    question, and swapping one for the other would keep the layout while
 *    quietly changing the claim. If crew playtime is worth showing it wants
 *    designing on its own terms, and the FRIENDS roster — which already knows
 *    who owns the game (`crew_game_ownership`, 3007) — is the better home.
 *  - last patch — nothing calls `ISteamNews/GetNewsForApp`; it is not among the
 *    outbound hosts in the Worker or the edge functions.
 *  - price history — the ONLY one where the fetch already exists: the nightly
 *    `discover-pool ?op=facts` step holds today's price and overwrites it. The
 *    gap is storage, not upstream, which is why its copy said so.
 *
 * PRICE · 12 MONTHS IS NO LONGER IN THIS BAND (migration 3045). It never fit:
 * a `StatCell` is a value and a caption, and that pair cannot hold a sparkline
 * plus a LOW chip. It sat here only because it had no data and a pending cell
 * is all label. Now that it has a series it renders as its own block beside the
 * players chart — `priceBars` / `priceFoot` / `priceLowTxt` on the VM — which is
 * where the design draws it too. Its old copy, `THE NIGHTLY FACTS JOB KEEPS ONLY
 * TODAY'S PRICE`, was an exact description of the defect 3045 fixed and became
 * false the night it shipped.
 */
/**
 * LAST PATCH — the four states migration 3048's `patch_seen_at` exists to keep
 * apart, and three of them are NOT the same absence.
 *
 *   never asked        the walk has not reached this appid — pending, naming
 *                      the job, which is the only one of the four that is
 *                      genuinely "we do not know"
 *   asked, no notes    this game has no patchnotes announcement AT ALL. Common
 *                      rather than exceptional on this catalog, and a real
 *                      answer to the question the cell asks
 *   asked, has a date  the figure
 *   no facts row yet   indistinguishable from never asked, and rendered as it
 *
 * `STILL SHIPPING` is deliberately absent from the caption (Marcus, 2026-08-27).
 * The mirror prints it beside a four-month-old patch, but shipped it would be a
 * claim about the present tense needing a threshold, and any threshold is a
 * cliff a game crosses on a day nothing happened to it: a title patched in April
 * would silently lose the phrase in July because time passed. `4M AGO` carries
 * the same information continuously and `6Y AGO` says the opposite just as well,
 * so both trees now render the phone's shorter caption.
 */
function patchCellOf(f: DetailFacts | null | undefined, now: number): StatCell {
  const base = { key: 'patch', label: 'LAST PATCH', color: 'var(--gt-text)' };
  // Absent (not fetched) and a null row both mean "we have not got an answer".
  if (!f || !f.patchSeenAt) {
    return { ...base, value: '', caption: '', pending: true, why: 'NEEDS THE STEAM NEWS JOB' };
  }
  if (!f.patchAt) {
    return { ...base, value: '', caption: '', pending: true, why: 'NO PATCH NOTES ON STEAM' };
  }
  const at = Date.parse(f.patchAt);
  if (!Number.isFinite(at)) {
    return { ...base, value: '', caption: '', pending: true, why: 'NO PATCH NOTES ON STEAM' };
  }
  const d = new Date(at);
  return {
    ...base,
    value: (MONTHS[d.getUTCMonth()] ?? '') + ' ' + d.getUTCFullYear(),
    caption: ago(at, now),
    pending: false,
    why: '',
  };
}

/**
 * REVIEWS · LAST 30D — four states, mirroring `patchCellOf` (migration 3049).
 *
 * THE VALUE IS A RUNG AND A PERCENTAGE, not a bare percentage, and it goes
 * through the SAME `rungOf` + `REV_RUNGS` path the all-time cell uses. Migration
 * 3040 established that a percentage alone cannot name a rung because Steam
 * folds review count into the ladder; over a 30-day window that bites harder,
 * because the window holds a fraction of the reviews and "high percentage, small
 * count" becomes the common case. Rendering `96%` here and letting a reader
 * infer a rung would reproduce the exact defect 3040 fixed.
 *
 * `rungOf` takes a `{rs, rp}` shape, so the window's pair is handed to it as
 * one — which also means the fallback for a window Steam gave no rung for is
 * `rungFromPct`'s deliberate partiality, already tested, rather than a second
 * ladder living here.
 */
function rev30CellOf(f: DetailFacts | null | undefined): StatCell {
  const base = { key: 'rev30', label: 'REVIEWS · LAST 30D' };
  const PLAIN = 'var(--gt-text)';
  if (!f || !f.rev30At) {
    return { ...base, value: '', caption: '', pending: true, why: 'NEEDS THE APPREVIEWS JOB', color: PLAIN };
  }
  // Asked, and the window was empty. A real answer about the game, not a gap.
  if (f.rev30Ct === 0 || f.rev30Pct === null) {
    return {
      ...base,
      value: '',
      caption: '',
      pending: true,
      why: 'NO REVIEWS IN THE LAST 30 DAYS',
      color: PLAIN,
    };
  }
  // `exactOptionalPropertyTypes` is on, so `rs: undefined` is not the same as
  // an absent `rs` — the key has to be omitted rather than set to undefined,
  // which is also the distinction `rungOf` reads (absent = Steam gave no rung).
  const m = revMeta({
    live: false,
    up: false,
    rp: f.rev30Pct,
    ...(f.rev30Score === null ? {} : { rs: f.rev30Score }),
  });
  return {
    ...base,
    value: m.t,
    // The COUNT, which the rung encodes only qualitatively. 91% of 976 and 91%
    // of 8 are different claims, and over 30 days the small one is common.
    caption: f.rev30Ct + (f.rev30Ct === 1 ? ' REVIEW' : ' REVIEWS'),
    pending: false,
    why: '',
    color: m.c,
  };
}

function statsBand(rev30: StatCell, revTxt: string, hasRev: boolean, revClr: string, patch: StatCell): StatCell[] {
  return [
    rev30,
    {
      key: 'revAll',
      label: 'REVIEWS · ALL TIME',
      // The one real figure in the band. It keeps its ALL TIME label whatever
      // the layout does with it.
      value: hasRev ? revTxt : '',
      caption: hasRev ? 'ALL TIME · STEAM' : '',
      pending: !hasRev,
      why: hasRev ? '' : 'NO REVIEW SCORE FROM STEAM YET',
      // The rung's own colour, matching the hero review line directly above
      // (`revClr`) rather than the design's muted `--gt-text-3`. Marcus,
      // 2026-08-27: the band's one real figure should read like a review score
      // everywhere else in the app reads, and a green OVERWHELMING beside a
      // grey one was the two surfaces disagreeing about what the number is.
      color: revClr,
    },
    patch,
  ];
}

/**
 * The V2 left rail's ledger, plus the two meta-line atoms V2 needs on their own.
 *
 * Split out of `detailVM` because it is one coherent claim — five facts about
 * the game, each of which the ledger either states or omits — and because
 * `detailVM` is already the longest selector in the file.
 */
function railOf(
  s: QdStore,
  dg: Game,
  entry: ListEntry | undefined,
  tagChips: ReadonlyArray<{ key: string; l: string }>,
  now: number,
) {
  const n = s.users.length;
  const mx = dg.mx || 0;
  // A sentinel max lights NO seats. `Math.min(65535, 6)` is 6, which would fill
  // the meter off a number `capNumTxt` has already refused to print — the meter
  // would be asserting "all six of you fit" from a value that is not a count.
  const fit = dg.punb ? 0 : Math.min(mx, n);
  const byU = entry ? s.user(entry.by) : undefined;
  return {
    platTxt: platTxt(dg.plat),
    capNum: capNumTxt(dg),
    // One cell per ACTUAL crew member, not a fixed six. The prototype's grid is
    // two columns of three because its seed crew is six; a crew of four should
    // not render two dead cells, and a crew of eight should not silently drop
    // two members. W.md open question 5 flags the above-six case as unspecified
    // — this answers it by letting the grid grow, which is the only answer that
    // keeps "one cell per person" true.
    seatCells: s.users.map((u, i) => ({
      key: u.k,
      bg: i < fit ? 'var(--acc)' : 'transparent',
      bd: i < fit ? '0' : '1px solid var(--gt-border-3)',
    })),
    fitTxt: fitTxt(mx, n, dg.punb),
    // Phone wording, verbatim from the mirror; the desktop no-count string
    // stays `NO COUNT — SET ONE` inside `fitTxt`. TAP vs SET is the input
    // vocabulary difference, not a drift.
    capNudge: mx ? '' : 'NO COUNT — TAP TO SET ONE',
    capAppid: dg.appid || 0,
    // Names the source in the tip, so "why does this say 4?" is answered before
    // the editor is opened rather than inside it.
    capTip:
      dg.psrc === 'override'
        ? 'CLICK TO EDIT THE PLAYER COUNT'
        : !dg.mx
          ? 'NO SOURCE HAS A COUNT — CLICK TO SET ONE'
          : playersSrcTxt(dg) + ' — CLICK TO CORRECT IT',
    // The full day when Steam gave one, the month when it did not — `fmtDFull`
    // falls back through `fmtD`, so the TBA and em-dash rules stay stated once.
    //
    // No `!dg.up` gate, and that earlier note was over-cautious: the day here
    // comes from the SAME display string `rd` was parsed out of, and both of
    // the patterns that produce it require a literal day. An unreleased title
    // whose string is `Q4 2026` yields no day at all, and one that genuinely
    // announces `15 NOV 2026` should say so rather than `TBA`. The placeholder
    // hazard belongs to the epoch-derived nightly path, which is deliberately
    // not a writer for this column.
    dateFull: fmtDFull(dg),
    tagsTxt: tagChips.map((c) => c.l).join(' · '),
    // `ADDED BY MARCUS · 2D AGO`. Only listed games have an adder, so this is
    // empty rather than partial when the game is not on the list.
    addedTxt: byU ? 'ADDED BY ' + byU.name.toUpperCase() + ' · ' + ago(entry?.at ?? 0, now) : '',
    hasAdded: !!byU,
  };
}

/**
 * The FRIENDS panel's header, call strip and footer.
 *
 * `detailVerdict` carries the badge maths and the argument for why it has no
 * quorum; everything here is the chrome around it.
 */
function verdictOf(s: QdStore, entry: ListEntry | undefined, myL: VoteValue | undefined) {
  const v = detailVerdict(entry?.votes, s.users);
  const my = myL && isCall(myL) ? LVL[myL] : null;
  return {
    groupBig: v.big,
    groupSub: v.sub,
    groupBg: v.bg,
    // Empty rather than `0 OF 6 CALLED` when the game is not on the list: with
    // no entry there is nothing to call, so a count would be answering a
    // question nobody asked.
    calledTxt: entry ? v.calledN + ' OF ' + s.users.length + ' CALLED' : '',
    // Four joined cells: every cell after the first pulls 1px left so the
    // borders overlap into one rule, and the selected cell lifts out of the
    // stacking order so its own border is not the one that loses.
    callCells: CALLS.map((l, i) => {
      const L = LVL[l];
      const on = myL === l;
      return {
        key: l,
        g: L.g,
        tip: L.g + ' ' + L.t,
        ml: i ? '-1px' : '0',
        pos: (on ? 'relative' : 'static') as 'relative' | 'static',
        z: (on ? 2 : 'auto') as number | 'auto',
        bd: on ? L.c : 'var(--gt-border-2)',
        bg: on ? L.c : 'transparent',
        clr: on ? 'var(--gt-ink)' : L.c,
      };
    }),
    myTxt: my ? my.g + ' · ' + my.t : "YOU HAVEN'T CALLED IT YET",
    myClr: my ? my.c : 'var(--gt-muted)',
    commentsTxt: 'COMMENTS · ' + (entry ? entry.comments.length : 0),
    // Arms before it fires. Removing takes the game off the list for the WHOLE
    // crew, its calls and comments with it, and every comparable control in the
    // app already arms (`rmArm`, `ptRmArm`). W.md C7 caught this one shipping
    // unarmed in the design and ruled that it must not.
    // Bare `SURE?` (DESIGN-CHANGELOG 2026-08-26, restated 2026-09-04 as the
    // implementer note): the consequence belongs in the control's tip, never
    // in the armed label.
    listRmTxt: s.listRmArm ? 'SURE?' : "✕ REMOVE FROM TONIGHT'S LIST",
    listRmClr: s.listRmArm ? 'var(--acc2)' : 'var(--gt-text-5)',
  };
}

export type MediaItem =
  | {
      kind: 'yt';
      id: string;
      key: string;
      embed: string;
      thumb: string;
      /** the member's own words — NOT uppercased, same rule as a comment body */
      title: string;
      byTxt: string;
      canDel: boolean;
    }
  | {
      kind: 'tw';
      id: string;
      key: string;
      embed: string;
      /**
       * A Twitch thumbnail needs a Helix call (GET /helix/videos or /clips)
       * that would put a client id and an app token in the bundle — the exact
       * posture the iframe-only rule protects. Callers pass the game's hue
       * gradient instead. NOT a final answer: it is a placeholder waiting on a
       * server-side Helix fetch nobody has built.
       */
      thumbBg: string;
      /** the member's original link, for the open-on-Twitch escape hatch */
      url: string;
      title: string;
      byTxt: string;
      canDel: boolean;
    }
  | { kind: 'mov'; id: string; hls: string; poster: string; title: string }
  | { kind: 'shot'; id: string; thumb: string; full: string; title: string };

export interface MediaVM {
  items: MediaItem[];
  count: number;
  canAdd: boolean;
  /** why the composer is hidden; `''` when it is not */
  addTip: string;
  /** the YouTube search page — the empty state's way out, and always safe */
  ytLink: string;
}

/** D-02 again: no appid, no `game_videos` FK, no composer. */
export const NO_APPID_VIDEO_TIP = "NO STEAM APPID — CAN'T ATTACH VIDEOS";
/** `is_active()` would reject the insert; say so before the round trip. */
export const TEMP_PW_VIDEO_TIP = 'YOU ARE ON A TEMP PASSWORD — SET A REAL ONE';

/**
 * The carousel that replaces the dead `listType=search` iframe.
 *
 * Ordering is the whole product decision and it is fixed here rather than in the
 * component: crew videos first (oldest first, so the crew's own choice leads and
 * a new clip does not shove the established one aside), then Steam trailers in
 * payload order, then screenshots. Steam's own store page does trailers first;
 * we do not, because a link somebody in the crew picked out is worth more than
 * the publisher's reel.
 *
 * Everything Steam-side reads out of the session-only `media` map, so a game
 * nobody has opened yet renders its crew videos alone and fills in the rest when
 * `ensureMedia` / `enrichGame` lands.
 */
export function mediaVM(s: QdStore, id: GameId): MediaVM {
  const g = s.gById(id);
  if (!g) return { items: [], count: 0, canAdd: false, addTip: '', ytLink: '' };

  const me = s.me();
  const meAdmin = !!me && me.role === 'admin';
  const appid = g.appid;
  const items: MediaItem[] = [];

  // 1 — crew videos, created_at ASC.
  if (appid > 0) {
    // YouTube and Twitch share ONE created_at bucket rather than sorting into
    // two blocks: they are both "a link somebody in the crew picked out", and
    // the ordering rule is whose choice came first, not which service it was on.
    const mine = s.videos.filter((v) => v.appid === appid).sort((a, b) => a.at - b.at);
    for (const v of mine) {
      const c = chipOf(s.user(v.by));
      // `ADDED BY <NAME> · <AGO>` — the age was specified from the start and
      // never emitted; `v.at` has always been on the row.
      const byTxt = 'ADDED BY ' + c.name.toUpperCase() + ' · ' + ago(v.at);
      // Mirrors the `game_videos` DELETE policy exactly: author or admin.
      const canDel = !!me && (v.by === me.k || meAdmin);
      if (v.provider === 'twitch') {
        items.push({
          kind: 'tw',
          id: v.id,
          key: v.key,
          embed: twitchEmbedUrl(v.kind === 'clip' ? 'clip' : 'vod', v.key, embedParentHost()),
          thumbBg: fallGradient(g.id),
          url: v.url,
          title: v.title,
          byTxt,
          canDel,
        });
        continue;
      }
      items.push({
        kind: 'yt',
        id: v.id,
        key: v.key,
        embed: ytEmbedUrl(v.key),
        thumb: ytThumbUrl(v.key),
        title: v.title,
        byTxt,
        canDel,
      });
    }
  }

  // 2 / 3 — Steam trailers then screenshots, both in payload order.
  const steam = appid > 0 ? s.media[appid] : undefined;
  for (const m of steam?.movies ?? []) {
    items.push({
      kind: 'mov',
      id: 'mov' + m.id,
      hls: m.hls,
      poster: m.poster,
      // Uppercased so the two Steam kinds read as one set next to SCREENSHOT N.
      title: m.name.toUpperCase(),
    });
  }
  (steam?.shots ?? []).forEach((sh, i) => {
    items.push({
      kind: 'shot',
      id: 'shot' + i,
      thumb: sh.thumb,
      full: sh.full,
      title: 'SCREENSHOT ' + (i + 1),
    });
  });

  const canAdd = appid > 0 && !!me && !me.mustReset;
  const addTip = canAdd ? '' : appid <= 0 ? NO_APPID_VIDEO_TIP : me ? TEMP_PW_VIDEO_TIP : '';

  return {
    items,
    count: items.length,
    canAdd,
    addTip,
    ytLink: 'https://www.youtube.com/results?search_query=' + encodeURIComponent(g.n + ' gameplay'),
  };
}

// ---------------------------------------------------------------------------
// friends + profile
// ---------------------------------------------------------------------------

/**
 * `CREW CREATED MAR 2026 · 6 MEMBERS · QUARTERDOWN.COM/C/ALPHA` — the MANAGE
 * CREW subheader, computed the way both shells compute it.
 *
 * No accent segment: the 2026-08-18 changelog dropped it. The address is shown
 * because it is the one an invite link is built from, so an owner reading it
 * out loud is reading the right thing.
 */
function crewMetaTxt(s: QdStore): string {
  const crew = s.activeCrew;
  if (!crew) return '';
  const at = new Date(crew.createdAt);
  const n = s.users.length;
  return (
    'PARTY FORMED ' +
    (Number.isNaN(at.getTime()) ? '—' : MON[at.getMonth()] + ' ' + at.getFullYear()) +
    ' · ' +
    n +
    (n === 1 ? ' MEMBER' : ' MEMBERS') +
    ' · QUARTERDOWN.COM/C/' +
    crew.slug.toUpperCase()
  );
}

/**
 * The PENDING section of MANAGE CREW (3026, docs/INVITES.md).
 *
 * SEPARATE FROM `adminRows` ON PURPOSE. Marcus: "Invites should not count as
 * joined." Folding these into the roster would put them in the member count,
 * the transfer picker and the seat meter — so they are their own list, above
 * the members and outside every total.
 *
 * EXPIRED INVITATIONS STAY HERE. Expiry does not close an invitation, it only
 * makes it unusable, so the row survives and RESEND is the way out — which is
 * exactly what was asked for, and it costs nothing because re-inviting an
 * address supersedes whatever it finds.
 */
function inviteRows(s: QdStore, now: number) {
  const rows = s.pending.map((p) => {
    const expiresAt = new Date(p.expiresAt).getTime();
    const invitedAt = new Date(p.invitedAt).getTime();
    const expired = Number.isFinite(expiresAt) && expiresAt <= now;
    const days = Math.max(0, Math.ceil((expiresAt - now) / 86_400_000));
    return {
      key: p.email,
      email: p.email,
      /* `INVITED 2D AGO · EXPIRES IN 5 DAYS`, or the same line admitting it is
         dead. The two halves answer different questions — how long they have
         been sitting on it, and how long is left. */
      whenTxt:
        (Number.isFinite(invitedAt) ? 'INVITED ' + ago(invitedAt, now) : 'INVITED') +
        ' · ' +
        (expired ? 'EXPIRED' : 'EXPIRES IN ' + days + (days === 1 ? ' DAY' : ' DAYS')),
      expired,
      /* Only says SENT when a message actually went out. Until a provider was
         configured every invitation was hand-delivered, and the two cannot be
         told apart after the fact unless the row records it. */
      deliveryTxt: p.sent ? 'EMAILED' : 'LINK ONLY',
      clr: expired ? 'var(--acc2)' : 'var(--gt-muted)',
      busy: s.invBusy === p.email,
      resendLbl: s.invBusy === p.email ? 'SENDING…' : 'RESEND',
    };
  });
  return {
    pendingRows: rows,
    hasPending: rows.length > 0,
    /* Its own count, never added to the member count. */
    pendingHdr: 'PENDING — ' + rows.length,
    invEmail: s.invEmail,
    invBusy: Boolean(s.invBusy),
    inviteLbl: s.invBusy === 'new' ? 'INVITING…' : '+ INVITE',
    /* Must stay true to what the backend does (CREW-ONBOARDING "never claim
       what the backend does not do"): nothing is created until they accept. */
    inviteNote: 'THEY PICK THEIR OWN NAME AND PASSWORD WHEN THEY JOIN.',
  };
}

/**
 * The ASK screen (3056, docs/JOIN-LINKS.md).
 *
 * TWO STEPS ON ONE SCREEN, and `codeSent` is the hinge. Address first, so the
 * code is already in flight while they fill in the rest — and so a typo in the
 * address costs one step rather than the whole form.
 *
 * THREE STATES DELIBERATELY SAY DIFFERENT THINGS where the invite screen says
 * one. A wrong code, an expired one and a locked challenge need three different
 * actions from the reader (try again, ask for a new one, ask for a new one), so
 * collapsing them would leave somebody retyping into a challenge that is
 * already dead. A dead LINK still says one thing, because none of its causes
 * are actionable.
 */
export function askVM(s: QdStore) {
  const p = s.askPeek;
  const me = s.me();
  const signedIn = Boolean(s.session);
  const live = p?.state === 'live';
  const crew = (p?.crewName ?? '').toUpperCase();
  const leader = p?.leaderName ?? '';

  /* Signed in and ALREADY IN. Offer the party rather than inviting them to ask
     for something they have — the server tells us, so this is not a guess. */
  const alreadyIn = signedIn && live && Boolean(p?.alreadyMember);

  return {
    loading: s.askBusy && !p,
    live,
    alreadyIn,
    headline: !live ? '' : 'ASK TO JOIN ' + crew,
    /** `MARCUS RUNS THIS PARTY` — a display name, never an address. Naming the
     *  leader is the difference between a join link and a phishing page. */
    leaderTxt: live && leader ? leader + ' RUNS THIS PARTY' : '',
    closedTxt: live ? '' : "THIS JOIN LINK ISN'T VALID.",
    alreadyInTxt: alreadyIn ? "YOU'RE ALREADY IN " + crew + '.' : '',

    /** Signed in and not a member: one tap, no fields, no code. Existing
     *  accounts are verified by decision (§3.3). */
    askAsMe: signedIn && live && !alreadyIn,
    /** Signed out: prove the address first. */
    needsCode: !signedIn && live,
    codeSent: s.askCodeSent,

    askEmail: s.askEmail,
    emailHint: "WE'LL MAIL YOU A CODE TO PROVE IT'S YOURS.",
    sendLbl: s.askBusy ? 'SENDING…' : 'SEND ME A CODE',
    /** `CODE SENT TO MA****@EXAMPLE.COM` — never the whole address back. */
    sentTxt: s.askMasked ? 'CODE SENT TO ' + s.askMasked.toUpperCase() : '',
    expiryTxt: 'IT EXPIRES IN 15 MINUTES.',
    resendLbl: 'SEND ANOTHER CODE',

    askCode: s.askCode,
    askName: s.askName,
    askPw: s.askPw,
    askPw2: s.askPw2,
    /* Live, but only once the second field has something in it — flagging a
       mismatch against an empty box would be scolding somebody mid-type. */
    pwMismatch: s.askPw2.length > 0 && s.askPw !== s.askPw2,
    pwHint: 'USE AT LEAST 8 CHARACTERS',
    /** The one thing this screen must not overclaim: a code proves an address,
     *  it does not admit anybody. Somebody later denied was not misled. */
    gateHint: 'THE PARTY LEADER HAS TO LET YOU IN.',

    submitLbl: s.askBusy
      ? 'ASKING…'
      : signedIn && me
        ? 'ASK AS ' + me.name.toUpperCase()
        : 'ASK TO JOIN',
    openLbl: 'OPEN ' + crew,
    busy: s.askBusy,
    askErr: s.askErr,
    hasErr: Boolean(s.askErr),
    /** The address is already an account. The screen changes SHAPE for this —
     *  there is nothing to retype, so it offers the only door that helps. */
    known: s.askKnown,
    knownTxt: 'YOU ALREADY HAVE AN ACCOUNT ON THIS EMAIL.',
    knownHint: 'LOG IN AND YOU WILL COME STRAIGHT BACK HERE TO ASK.',
    knownLbl: 'LOG IN',
    haveAccountTxt: 'ALREADY HAVE AN ACCOUNT? LOG IN',
  };
}

/**
 * The WAITING screen — asked, and the leader has not decided.
 *
 * Reachable with no token at all, which is why it is its own screen: somebody
 * who asked yesterday and came back to look lands here from an ordinary boot.
 */
export function waitingVM(s: QdStore) {
  const w = s.askWaiting;
  const crew = (w?.crewName ?? '').toUpperCase();
  const leader = w?.leaderName ?? '';
  return {
    waiting: Boolean(w),
    headline: crew ? 'WAITING FOR ' + crew + ' TO LET YOU IN' : 'WAITING',
    /** Names who will see it, so the wait has a shape rather than being a
     *  spinner with no end. */
    whoTxt: leader ? leader + ' WILL SEE YOUR REQUEST.' : 'THE PARTY LEADER WILL SEE YOUR REQUEST.',
    hintTxt: "WE'LL LET YOU IN THE MOMENT THEY SAY YES.",
    withdrawLbl: s.askBusy ? 'WITHDRAWING…' : 'WITHDRAW',
    busy: s.askBusy,
  };
}

/**
 * The JOIN LINK block and the REQUESTS list in MANAGE PARTY.
 *
 * REQUESTS SIT ABOVE PENDING INVITES, so the screen reads requests → invites →
 * members, most-urgent first — and NEITHER of the first two is inside any
 * count. That is the same rule `inviteRows` states, now with a second half:
 * Marcus's "invites should not count as joined" applies identically to an ask.
 */
function joinLinkRows(s: QdStore, now: number) {
  const rows = s.jlRequests.map((r) => {
    const at = new Date(r.requestedAt).getTime();
    return {
      key: r.id,
      id: r.id,
      name: (r.displayName || '—').toUpperCase(),
      whenTxt: Number.isFinite(at) ? 'ASKED ' + ago(at, now) : 'ASKED',
      busy: s.jlBusy === r.id,
      approveLbl: s.jlBusy === r.id ? '…' : 'LET IN',
      denyLbl: 'NO',
    };
  });
  return {
    requestRows: rows,
    hasRequests: rows.length > 0,
    /* Its own count, never added to the member count. */
    requestsHdr: 'REQUESTS — ' + rows.length,

    jlLive: s.jlLive,
    jlBusy: Boolean(s.jlBusy),
    jlHdr: 'JOIN LINK',
    /** The URL, re-readable from 3057 so a link can be shared more than once. */
    jlUrl: s.jlUrl,
    /** Live, but minted before 3057, so there is nothing stored to show. */
    jlUnreadable: s.jlUnreadable,
    jlUnreadableTxt:
      'THIS LINK WAS MADE BEFORE LINKS COULD BE SHOWN AGAIN. MAKE A NEW ONE TO SEE IT.',
    jlNewLbl: s.jlBusy === 'link' ? 'WORKING…' : s.jlLive ? 'NEW LINK' : 'MAKE A JOIN LINK',
    jlOffLbl: 'TURN OFF',
    jlOnTxt: 'ANYONE WITH THIS LINK CAN ASK. THEY PROVE THEIR EMAIL; YOU DECIDE WHO GETS IN.',
    jlOffTxt: 'NO JOIN LINK. ONLY PEOPLE YOU INVITE BY EMAIL CAN JOIN.',
    /** Said on the surface because it is true and cannot be softened: the old
     *  token was never stored, so rotating really does kill the copied link. */
    jlRotateWarn: 'MAKING A NEW LINK KILLS THE OLD ONE.',
  };
}

export function crewVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const meAdmin = !!(me && me.role === 'admin');
  const crewCards = s.users.map((u) => {
    const c = chipOf(u);
    const st = stats(s.list, u.k);
    const isYou = !!(me && u.k === me.k);
    /* P2-08 CLOSED — migration 3010. This used to be `u.k === me.k && …`, which
       meant every card but your own reported `STEAM NOT LINKED` whether or not
       that member had linked. The deviation's stated reason was that
       `steam_links` is self-select-only; migration 3007 had already ended that
       on 2026-08-13 by disclosing the same `linked` boolean per member by name
       (it is what the detail panel draws as `NO STEAM LINK`), and 3010 is that
       read without a game in hand.

       TWO SOURCES, and not by accident. Your own answer comes off `steamLink`,
       which boot loads and link/unlink keep current, so the one row that can
       change mid-session is never served from a cache that nothing invalidates.
       Everyone else comes off `crewLinks`, fetched once per session on the way
       into this screen. */
    const sLink: boolean | undefined = isYou
      ? !!(s.steamLink && s.steamLink.linked)
      : s.crewLinks?.[u.k];
    return {
      ...c,
      key: u.k,
      k: u.k,
      name: u.name,
      roleTxt: u.role === 'admin' ? 'LEADER' : 'PLAYER',
      roleClr: u.role === 'admin' ? 'var(--acc)' : 'var(--gt-text-5)',
      isYou,
      isTemp: u.mustReset,
      statsTxt: 'ADDED ' + st.a + ' · VOTES ' + st.v + ' · COMMENTS ' + st.c,
      /* `undefined` is PENDING and blanks the chip — the roster stayed silent
         about people it had not asked about for a reason, and an RPC in flight
         is the same not-knowing the old code turned into an assertion. Same
         rule as `libState`'s empty `libTxt` on the detail panel. */
      stDot: sLink === undefined ? 'transparent' : sLink ? 'var(--acc)' : 'var(--gt-border-3)',
      stTxt: sLink === undefined ? '' : sLink ? 'STEAM LINKED' : 'STEAM NOT LINKED',
      stClr: sLink ? 'var(--gt-text-5)' : 'var(--gt-ghost-2)',
    };
  });

  const adminRows = meAdmin
    ? s.users.map((u) => {
        const c = chipOf(u);
        const isSelf = !!(me && u.k === me.k);
        const armed = s.rmArm === u.k;
        return {
          ...c,
          key: u.k,
          name: u.name,
          /* THE LOGIN ADDRESS, at last — `auth.users.email`, via migration
             3029's `crew_login_emails()`. Marcus's call, 2026-08-21.

             This row used to render `profiles.recovery_email`, the legacy note,
             and every version of that was wrong in a different way: it showed
             one address here and another on ACCOUNT for the same person; it
             gated RESET PW so the button appeared for exactly the members the
             server refuses; and when three accounts were seeded with real login
             addresses it kept reporting them as having no email at all. Four
             bugs, one cause, one day.

             `loginEmail` is `''` ONLY when the account still has the
             synthesized `<user_key>@crew.quarterdown.com` placeholder — a
             domain with no MX. That is not a cosmetic gap: it means FORGOT
             PASSWORD cannot reach them and nothing else can either, since
             RESET PW is gone. So the row says the consequence, not the fact. */
          emailTxt: u.loginEmail || 'NO EMAIL — CANNOT RECOVER PASSWORD',
          emailClr: u.loginEmail ? 'var(--gt-muted)' : 'var(--acc2)',
          /* Read-only now: the select is gone with CO-ADMIN (DESIGN-CHANGELOG
             2026-08-18), and `op=role` is retired server-side. Two values only
             — LEADER or PLAYER — because a party has exactly one leader (D4). */
          roleTxt: u.role === 'admin' ? 'LEADER' : 'PLAYER',
          roleClr: u.role === 'admin' ? 'var(--gt-gold)' : 'var(--gt-muted)',
          roleBd: u.role === 'admin' ? 'var(--gt-gold)' : 'var(--gt-border-2)',
          isSelf,
          statusTxt: u.mustReset ? 'TEMP PASSWORD' : 'ACTIVE',
          statusClr: u.mustReset ? 'var(--acc2)' : 'oklch(0.62 0.09 150)',
          /* GONE FROM HERE, 2026-08-21: `copyTxt` / `copyValue` /
             `canResetPw` / `pwNote` / `pwTxt` / `pwBusy`.

             Both controls were removed from the two MANAGE CREW screens the
             same day — COPY LINK built the legacy `/join#user=` link the 3026
             invitation flow does not use, and RESET PW gated on
             `profiles.recovery_email`, so it rendered for exactly the members
             `admin-users` refuses. Nothing has rendered these since, and
             nothing calls `doResetPw`.

             They are deleted rather than left because the RESET PW pair keyed
             off the LEGACY email column while the row beside them now reads the
             LOGIN one. Two fields meaning different things under similar names
             is precisely what produced four bugs in a day, and dead code is the
             easiest place for the next reader to pick the wrong one.

             The store action and `admin-users op=reset` survive for now, unused
             and unreachable — retiring a server operation belongs with phase E
             and `op=create`, not with a view-model tidy. */
          canRm: !isSelf,
          rmTxt: armed ? 'SURE?' : 'REMOVE',
          rmBd: armed ? 'var(--acc2)' : 'var(--gt-border-2)',
          rmClr: armed ? 'var(--acc2)' : 'var(--gt-text-5)',
        };
      })
    : [];

  return {
    crewCards,
    // Just the player count (DESIGN-CHANGELOG 2026-09-03 "MANAGE PARTY is its
    // own full-screen page": the subhead lost its tail).
    crewSub: s.users.length + (s.users.length === 1 ? ' PLAYER' : ' PLAYERS'),
    /** the PARTY page's entry row carries the N WAITING chip, not the panels */
    hasJqWait: meAdmin && s.jlRequests.length > 0,
    jqWaitTxt: s.jlRequests.length + ' WAITING',
    meAdmin,
    /* The MANAGE PARTY header, which the mirrors give three parts: the party
       it manages, the role you hold in it, and — on its own row beneath — when
       the party started, how big it is and where it lives. All three are the
       same panel's, and only the leader sees the panel. */
    crewNameUp: (s.activeCrew?.name ?? '').toUpperCase(),
    /* LEADER, not OWNER (2026-08-28). `profiles.role='admin'` and
       `crew_members.role='owner'` are one person by construction — create and
       transfer both write the pair together (`create_crew_tx`,
       `transfer_crew_ownership`) — so the two old labels ADMIN and OWNER were
       always the same rank wearing two names. One word now. */
    myRoleLbl: meAdmin ? 'LEADER' : 'PLAYER',
    myRoleClr: meAdmin ? 'var(--gt-gold)' : 'var(--gt-muted)',
    crewMetaTxt: crewMetaTxt(s),
    /* RENAME (3030, Marcus 2026-08-21: "I'm not sure what the prod crew will be
       named, but I am sure we will want to pick a new name").

       Owner-only, like TRANSFER and DELETE, because the name is the crew's
       identity to everybody in it — the switcher, the header, every invitation
       email. `canRename` is separate from `meAdmin` only so the intent reads at
       the call site; the server checks it again either way. */
    crewNameDraft: s.crewNameDraft,
    canRename: meAdmin,
    /* Nothing to save when it matches, so the button says so rather than
       offering a write that would change nothing. The comparison is against the
       TRIMMED draft: trailing space is not a rename. */
    renameDirty: s.crewNameDraft.trim() !== (s.activeCrew?.name ?? '') && !!s.crewNameDraft.trim(),
    renameBusy: s.crewBusy === 'rename',
    /* Every OTHER crew write blocks this one too — they share `crewBusy`, and a
       rename landing mid-delete would be a write against a crew on its way out. */
    renameLocked: s.crewBusy !== '' && s.crewBusy !== 'rename',
    renameLbl: s.crewBusy === 'rename' ? 'RENAMING…' : 'RENAME',
    notAdmin: !meAdmin,
    /* The shells compute this from the roster rather than naming a person, so
       the note is right in any party — the repo's hardcoded "ASK MARCUS" was
       only ever true in his. `join(' OR ')` survives from the mirror even
       though one leader is now the rule: a transfer commits two role writes,
       and a roster read landing between them would otherwise say "ASK ."  */
    askAdmTxt:
      'ONLY THE LEADER CAN ADD PLAYERS OR RESET PASSWORDS — ASK ' +
      (s.users
        .filter((u) => u.role === 'admin')
        .map((u) => u.name.toUpperCase())
        .join(' OR ') || 'THE LEADER') +
      '.',
    adminRows,
    hasInvite: !!s.invite,
    invName: s.invite ? s.invite.name : '',
    invPw: s.invite ? s.invite.pw : '',
    invLink: s.invite ? s.invite.link : '',
    /* WHAT THEY TYPE, which is not always their name. The server strips the
       name to `[a-z0-9]` and bumps on collision, so the second "New Owner" is
       `newowner2` — and the panel used to hand over "NEW OWNER", which
       resolves to the FIRST one and refuses the password. Falls back to the
       name only for invites minted before this field existed. */
    invKey: s.invite ? s.invite.key || s.invite.name.toLowerCase() : '',
    addName: s.addName,
    addEmail: s.addEmail,
    /* The one action in the app where a second press produced a second real
       account — the server bumps the login key on collision instead of
       refusing it. `addBusy` is the whole guard; this is it saying so. */
    addBusy: s.addBusy,
    addLbl: s.addBusy ? 'ADDING…' : '+ ADD USER',
    ...inviteRows(s, now),
    ...joinLinkRows(s, now),
  };
}

/**
 * The crew switcher — desktop dropdown and phone sheet read the same values.
 * `design/Games Tonight.dc.html` lines 51-71 (the lockup and its menu) and
 * `design/Games Tonight Mobile.dc.html` line 56 (the chip).
 *
 * The switcher lives in the SHELL mirrors, not in `GT Crew.dc.html` — that file
 * owns the crew screens only. Worth saying because looking for it there finds
 * nothing.
 *
 * A SINGLE-CREW ACCOUNT KEEPS THE MENU, which reverses the 2026-08-13 spec and
 * is deliberate: `+ START ANOTHER CREW` lives in that menu and is the only door
 * to a second crew, so a plain label would make starting one unreachable. The
 * shell's gate is `mine.length > 0`, not `> 1`. Only an account in NO crews
 * loses the caret — and it gets the literal label `NO CREW`.
 */
export function switcherVM(s: QdStore) {
  const crews = s.crews;
  const activeId = s.activeCrew?.id ?? '';
  const has = crews.length > 0;
  return {
    /** The lockup's label; `NO PARTY` is the mirror's literal fallback. */
    nameUp: (s.activeCrew?.name || 'NO PARTY').toUpperCase(),
    /** Caret, pointer and menu all hang off "am I in any party at all". */
    canOpen: has,
    open: s.swOpen && has,
    /** `SWITCH PARTY` only reads as an instruction when there is one to switch to. */
    tip: crews.length > 1 ? 'SWITCH PARTY' : 'YOUR PARTIES',
    /** Any party-lifecycle write is open — rows stop accepting clicks. */
    busy: Boolean(s.crewBusy),
    /**
     * What the lockup SAYS, which is not always the party's name.
     *
     * Switching and deleting both null `activeCrew` before re-hydrating, so for
     * the whole multi-second window the label above falls back to the mirror's
     * literal `NO PARTY` — the header announcing you have no party is how a
     * working app looked broken. This says what is happening instead.
     */
    lockupTxt:
      s.crewBusy === 'switch'
        ? 'SWITCHING…'
        : s.crewBusy === 'delete'
          ? 'DELETING…'
          : s.crewBusy === 'create'
            ? 'STARTING…'
            : (s.activeCrew?.name || 'NO PARTY').toUpperCase(),
    rows: crews.map((c) => {
      const on = c.crewId === activeId;
      return {
        key: c.crewId,
        nameUp: c.name.toUpperCase(),
        /* Two values only — CO-ADMIN was removed on 2026-08-18 and the mirrors
           implement LEADER | PLAYER. Anyone building from the earlier same-day
           entry would ship a dead third role. The rank earns its place in THIS
           list (2026-08-28): the rows are your parties, and which of them you
           lead is the reason to look. */
        roleTxt: c.role === 'owner' ? 'LEADER' : 'PLAYER',
        roleClr: c.role === 'owner' ? 'var(--gt-gold)' : 'var(--gt-muted)',
        membersTxt: c.memberCount + (c.memberCount === 1 ? ' MEMBER' : ' MEMBERS'),
        on,
        bg: on ? 'var(--gt-raised)' : 'transparent',
        clr: on ? 'var(--acc)' : 'var(--gt-text-2)',
      };
    }),
  };
}

const MON = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

/**
 * OWNER CONTROLS — the panel MANAGE CREW's destructive half was split into
 * (DESIGN-CHANGELOG 2026-08-18 "Owner controls split out of MANAGE CREW").
 *
 * NO ACCENT IN THE META LINE. An earlier changelog entry describes
 * "created · members · URL · accent", and a LATER one pulled crew-level styling
 * entirely — "crews have no accent column; do not migrate one". The mirrors
 * carry three segments. Building the earlier entry would invent a column.
 *
 * The transfer candidate list is PLAYERS only, and excludes anyone still on a
 * temp password: migration 3020 refuses them, because handing the crew to an
 * account that cannot yet act as owner strands it — and the transfer cannot be
 * undone from the old owner's side.
 */
export function ownerVM(s: QdStore) {
  const crew = s.activeCrew;
  const mine = s.crews.find((c) => c.crewId === crew?.id);
  const isOwner = mine?.role === 'owner';

  const candidates = s.users
    .filter((u) => u.k !== s.session && !u.mustReset)
    .map((u) => ({ key: u.k, nameUp: u.name.toUpperCase() }));

  const typed = s.delTxt.trim();
  /* Both confirmations stay MOUNTED while their write runs — the unmount used
     to be the only thing stopping a second press, and it was accidental. The
     flag is the guard now, so the dialog can also be where a refusal lands
     instead of a toast on a screen that has already navigated away. */
  const delBusy = s.crewBusy === 'delete';
  const trBusy = s.crewBusy === 'transfer';
  return {
    show: Boolean(isOwner && crew),
    crewNameUp: (crew?.name ?? '').toUpperCase(),
    modal: s.ownModal,
    candidates,
    /** Nobody eligible: the panel says so instead of offering an empty picker. */
    canTransfer: candidates.length > 0,
    trPick: s.trPick,
    /** Arms only once a real member is chosen. */
    trReady: Boolean(s.trPick) && candidates.some((c) => c.key === s.trPick),
    delTxt: s.delTxt,
    /* The typed-name gate, case-insensitively, because the prompt asks for the
       name in caps and refusing what it asked for would be a trap. The server's
       gate is EXACT, so `removeCrew` sends the canonical name once this one
       passes — see the note there. */
    delReady: typed.length > 0 && typed.toUpperCase() === (crew?.name ?? '').toUpperCase(),
    crewId: crew?.id ?? '',
    /* The two labels live here rather than in the bodies so the sheet and the
       modal cannot drift apart — which they already have on the one surface
       that got a busy state early (PtAdd prints 'CHECK' inline on desktop and
       reads the view-model on the phone). No trailing glyph while busy: the
       arrow means "go". */
    delBusy,
    delLbl: delBusy ? 'DELETING…' : 'DISBAND THIS PARTY ✕',
    trBusy,
    trLbl: trBusy ? 'HANDING IT OVER…' : 'HAND OVER THE PARTY ▸',
  };
}

export function profileVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const meC = chipOf(me);
  const st = me ? stats(s.list, me.k) : { a: 0, v: 0, c: 0 };
  const linked = !!(s.steamLink && s.steamLink.linked);
  const persona = s.steamLink ? s.steamLink.personaName : '';
  return {
    pfIni: meC.ini,
    pfHueBg: meC.hueBg,
    /* Adaptive ink for the big profile tile — the one avatar drawn by hand
       rather than through `Avatar` (which derives its own). 3012. */
    pfInk: inkOn(meC.hueBg),
    pfHasAv: meC.hasAv,
    pfAvBg: meC.avBg,
    pfName: s.pfName,
    pfEmail: s.pfEmail,
    /** The address currently in force — what the row shows and the sheet edits. */
    pfAuthEmail: s.pfAuthEmail,
    emOpen: s.emOpen,
    emNew: s.emNew,
    emCur: s.emCur,
    /** Nothing to save until it actually differs from the address in force. */
    emDirty: s.emNew.trim().toLowerCase() !== s.pfAuthEmail.toLowerCase(),
    /* Both of these are two round trips deep: the reauth is a full sign-in
       before the change itself even starts. */
    emBusy: s.authBusy === 'email',
    emLbl: s.authBusy === 'email' ? 'UPDATING…' : 'UPDATE EMAIL',
    pwBusy: s.authBusy === 'password',
    pwLbl: s.authBusy === 'password' ? 'UPDATING…' : 'UPDATE PASSWORD',
    pfCur: s.pfCur,
    pfN1: s.pfN1,
    pfN2: s.pfN2,
    showCur: !!(me && !me.mustReset),
    hasPwNote: !!(me && me.mustReset),
    pwNote: 'YOU ARE ON A TEMP PASSWORD — SET A REAL ONE',
    // The phone PASSWORD row (`design/GT Mobile Crew.dc.html`, DESIGN-CHANGELOG
    // 2026-08-18 "CHANGE PASSWORD is a sheet"). On a temp password the row
    // itself warns — border and copy go `--acc2` — mirroring the shell's
    // `pwRow*` formulas. The `8+` is derived from `PASSWORD_MIN`, not written
    // out: D-01 keeps every stated number tied to the enforced one.
    pwOpen: s.pwOpen,
    pwRowTxt: me && me.mustReset ? 'TEMP PASSWORD — SET A REAL ONE' : 'CHANGE IT · ' + PASSWORD_MIN + '+ CHARACTERS',
    pwRowClr: me && me.mustReset ? 'var(--acc2)' : 'var(--gt-muted)',
    pwRowBd: me && me.mustReset ? 'var(--acc2)' : 'var(--gt-border-2)',
    myStatsTxt: me
      ? 'SIGNED IN AS ' + me.name.toUpperCase() + ' · ADDED ' + st.a + ' GAMES · ' + st.v + ' VOTES · ' + st.c + ' COMMENTS'
      : '',
    crewSubTxt: s.users.length + ' PLAYERS · ROSTER + ADMIN',
    // "Never reintroduce the faked MARCUS_PLAYS" (ARCHITECTURE §3.2): the
    // persona comes from the link record, and is simply omitted when absent.
    pfSteamTxt: linked
      ? (persona ? 'LINKED AS ' + persona + ' · ' : 'LINKED · ') + 'CHECKED ' + chkAgo(s.freshness.lastOkAt, now)
      // `/ REQUESTED` dropped: linking has never switched request detection on,
      // because there is nothing to switch on. Matches the `PRIVATE — GOT-IN
      // DETECTION OFF` chip on the sync sheet, which was already right.
      : 'NOT LINKED — GOT-IN DETECTION OFF',
    pfSteamBtn: linked ? 'MANAGE STEAM LINK' : 'LINK STEAM',
    pfSteamBd: linked ? 'var(--gt-border)' : 'var(--acc2)',
  };
}

// ---------------------------------------------------------------------------
// what's new
// ---------------------------------------------------------------------------

export function whatsNewVM(s: QdStore, now: number = Date.now()) {
  const me = s.me();
  const queue = newForMe(s.list, me, (id) => s.gById(id));
  // `calls` runs the same card over the MY INTERESTS chore list rather than
  // over unread news (DESIGN-CHANGELOG 2026-08-24). Everything the two modes
  // differ by is here — the card views read these, never `wnMode` itself.
  const callRun = s.wnMode === 'calls';
  const empty = {
    open: s.wnOpen,
    hasCard: false,
    queueIds: queue.map((x) => x.e.id),
    newCount: queue.length,
    hdrTxt: callRun ? '◆ MAKE YOUR CALLS' : "◆ WHAT'S NEW",
    hdrClr: callRun ? 'var(--acc)' : 'var(--gt-info)',
    /** Nothing to mark reviewed in a call run — these are not unread news. */
    showClear: !callRun,
    emptyTxt: callRun ? 'EVERY GAME CALLED' : 'YOU ARE ALL CAUGHT UP',
  };
  if (!s.wnOpen || !s.wnQueue.length) return empty;
  const idx = Math.min(s.wnIdx, s.wnQueue.length - 1);
  const id = s.wnQueue[idx];
  if (id === undefined) return empty;
  const g = s.gById(id);
  const entry = s.list.find((e) => e.id === id);
  if (!g) return empty;
  const t = tileVM(g, tileCtx(s));
  const byC = chipOf(entry ? s.user(entry.by) : null);
  const votes = entry
    ? Object.keys(entry.votes)
        .filter((k) => {
          const v = entry.votes[k];
          return !!v && isCall(v);
        })
        .map((k) => ({ key: k, l: entry.votes[k] as VoteValue, ...chipOf(s.user(k)) }))
    : [];
  // One column per call, in `CALLS` order, aligned 1:1 under the vote strip
  // (DESIGN-CHANGELOG 2026-08-25). ALL FOUR are emitted and empties are kept —
  // the opposite of the clustered `voteGroups` this replaced, and for the
  // opposite reason: a cluster carried its own `S`/`A` letter, so an empty one
  // was four characters of nothing. A column says which call it is by sitting
  // under that button, so an empty column is the fact that nobody picked it.
  //
  // The title is composed here rather than in the two views because the column
  // knows the level and the avatar no longer prints it: without a letter beside
  // it, hovering is the only way to ask "what did Dave say?".
  const callCols = CALLS.map((l) => ({
    key: l,
    avs: votes
      .filter((v) => v.l === l)
      .map((v) => ({ ...v, title: v.name + ' — ' + LVL[l].g + ' · ' + LVL[l].t })),
  }));

  // ---- THE NUMBERS (DESIGN-CHANGELOG 2026-08-24) --------------------------
  // The card's third band, in the detail page's cell vocabulary. Every value
  // is one this app already holds; nothing here computes a fact of its own.
  //
  // TWO DELIBERATE DIFFERENCES FROM THE DESIGN, both recorded in
  // docs/porting/Y.md (C1, C2 / deviation Y-01):
  //
  //   * NO `LOW EVER $x`. The prototype reads a twelve-month price series out
  //     of its seed; this app stores today's price and the pre-discount one
  //     and nothing else — which is exactly why the detail band's `price12`
  //     cell is pending. The design's own fallback string is the honest half,
  //     so the sub-line says `NO PRICE HISTORY YET` and names the gap.
  //   * NO `SEEDED SNAPSHOT` note. There it is honesty — those numbers ARE
  //     games-data.js. Here the same words would be the inverted lie: these
  //     come from `steam_facts`, refreshed nightly.
  const crewN = s.users.length;
  const mx = g.mx || 0;
  const rev = revMeta(g);
  const nums = {
    revBig: g.rp ? g.rp + '%' : '—',
    revClr: t.revClr,
    // The rung name alone — the number is already the big line, and `revMeta`
    // now hands the two halves over separately so this no longer strips a
    // percentage back off with a regex that had to agree with how the label was
    // built.
    //
    // `rev.l` and NOT a `'NO RATING DATA'` fallback for a scored row: `revMeta`
    // returns an empty label for a row Steam has not rated whose percentage
    // cannot choose between three rungs (80%+ or under 20%), and a card reading
    // `84%` above `NO RATING DATA` states the opposite of its own value. A score
    // with no rung gets no sub-line; only a game with no score at all says so.
    revSub: g.rp || g.rs !== undefined ? rev.l : 'NO RATING DATA',
    priceBig: t.priceTxt,
    hasDisc: !!t.hasDisc,
    disc: t.disc,
    // `oldTxt` is Steam's own pre-discount price, not a floor we inferred.
    priceSub: t.hasOld ? 'WAS ' + t.oldTxt : 'NO PRICE HISTORY YET',
    // `capNumTxt` and not arithmetic of our own: it is the one surface allowed
    // to print exact seat digits, and the only one that refuses a sentinel `mx`
    // instead of doing subtraction on it (W4's bug).
    seatBig: capNumTxt(g),
    // `fitTxt`'s no-count arms read `… — SET ONE`, which is honest on the
    // detail rail because the cell IS the editor's click target. This cell is
    // inert text, so an imperative here asks for something the card cannot do:
    // a missing count states itself and stops.
    seatSub: mx && !g.punb ? fitTxt(mx, crewN, g.punb) : 'NO COUNT YET',
    peakBig: t.peakTxt,
    // ALL TIME, and NOT `· STEAM`. `pk` is `catalog_games.peak_ccu` — curated
    // editorial, the one column `v_games` deliberately does not COALESCE from
    // `steam_facts` (types.ts: "never healed from Steam"). Naming Steam as its
    // source would be this band's own honesty rule broken by the band. The
    // detail rail labels the same number `ALL-TIME PEAK` and names nobody, for
    // exactly this reason.
    peakSub: g.pk ? 'ALL TIME' : 'NOT RECORDED',
  };

  return {
    ...empty,
    hasCard: true,
    nums,
    calledTxt: votes.length + ' OF ' + crewN + ' CALLED',
    noVotes: votes.length === 0,
    ...t,
    // `3 OF 7` counts what you have done; `4 MORE` counts what is left. A chore
    // list wants progress, a news queue wants a backlog.
    progressTxt: callRun ? idx + 1 + ' OF ' + s.wnQueue.length : s.wnQueue.length - idx - 1 + ' MORE',
    metaTxt: t.priceTxt + ' · ' + t.playersTxt + ' · ' + t.dateTxt,
    descTxt: g.ds || 'No description yet — open the full page for details.',
    byTxt: byC.name.toUpperCase(),
    byIni: byC.ini,
    byHueBg: byC.hueBg,
    byHasAv: byC.hasAv,
    byAvBg: byC.avBg,
    agoTxt: entry ? ago(entry.at, now) : '',
    hasVotes: votes.length > 0,
    callCols,
    // The search page, never an embed. `MediaCarousel` owns the card's media
    // now and gets the same link from `mediaVM` (P10-01); this stays as the
    // card's own escape hatch.
    ytLink: 'https://www.youtube.com/results?search_query=' + encodeURIComponent(g.n + ' gameplay'),
    steamUrl: g.appid
      ? 'https://store.steampowered.com/app/' + g.appid
      : 'https://store.steampowered.com/search/?term=' + encodeURIComponent(g.n),
    currentId: id,
  };
}

// ---------------------------------------------------------------------------
// filters sheet
// ---------------------------------------------------------------------------

export interface FilterChip {
  key: string;
  l: string;
  neg: boolean;
  bd: string;
  clr: string;
  bg: string;
  /** which filter field the ✕ clears — the UI maps this to a store action */
  field: string;
  value: string;
}

const FLT_ST_NAMES: Record<string, string> = {
  demo: 'DEMO',
  ea: 'EARLY ACCESS',
  rel: 'RELEASED',
  unrel: 'UNRELEASED',
};

export function filterChips(s: QdStore): FilterChip[] {
  // The rail belongs to the trigger, which belongs to whatever screen is up.
  const F = fltOf(s);
  const raw: Array<{ l: string; neg?: boolean; field: string; value: string }> = [];
  if (F.p) {
    raw.push({
      // `SOLO` first: on a crew of one it is also `>= s.users.length`, and
      // `ALL 1 OF US` would name the wrong filter for the segment that set it.
      l:
        F.p === 1
          ? 'SOLO'
          : F.p >= s.users.length
            ? 'ALL ' + s.users.length + ' OF US'
            : F.p + '+ PLAYERS',
      field: 'p',
      value: '',
    });
  }
  // Unscoped now: every filter is in force on every surface, so every active
  // one earns a chip on every surface. The rail is the only thing that says
  // out loud why a board looks shorter than it did.
  const PR = F.pr;
  if (PR[0] > 0 || PR[1] < 60) raw.push({ l: 'PRICE: ' + priceLabel(PR), field: 'pr', value: '' });
  for (const k of ['demo', 'ea', 'rel', 'unrel'] as const) {
    const v = F.st[k];
    if (v && v !== 'any') {
      raw.push({
        neg: v === 'hide',
        l: v === 'only' ? FLT_ST_NAMES[k] + ' ONLY' : 'HIDE ' + FLT_ST_NAMES[k],
        field: 'st',
        value: k,
      });
    }
  }
  if (F.disc) raw.push({ l: F.disc > 1 ? '≥' + F.disc + '% OFF' : 'ON SALE', field: 'disc', value: '' });
  // Steam's word for the rung, not ours — the chip has to read like the panel
  // option that set it, and both now read like Steam.
  if (F.rs) {
    const name = REV_RUNG_NAMES.find((r) => r.v === F.rs)?.l ?? '#' + F.rs;
    raw.push({ l: F.rs >= 9 ? name : name + '+', field: 'rs', value: '' });
  }
  if (F.pt !== 'any') {
    raw.push({ neg: F.pt === 'hide', l: F.pt === 'only' ? 'HAS PLAYTEST' : 'NO PLAYTEST', field: 'pt', value: '' });
  }
  // OWNED BY — one chip per member, in roster order, so a four-name AND can be
  // taken apart one name at a time. `OWNS: JEFF` rather than a single
  // `OWNED BY: MARCUS, JEFF` chip for exactly that: the collapsed form's ✕ can
  // only clear the whole section, and dropping one member from a set you spent
  // four clicks building is the common correction.
  //
  // Named from the ROSTER, never from the key, and a key with no roster row is
  // dropped rather than rendered raw: the only way to reach one is a member
  // removed from the crew mid-session, and `FORMER FRIEND OWNS IT` is not a
  // filter anybody set.
  if (F.own.length > 0) {
    const nameByKey = new Map(s.users.map((u) => [u.k, u.name]));
    for (const k of F.own) {
      const n = nameByKey.get(k);
      if (n) raw.push({ l: 'OWNS: ' + n.toUpperCase(), field: 'own', value: k });
    }
  }
  // TAG CHIPS LIVE HERE NOW. The `# TAGS` trigger and its separate rail are
  // gone — the picker is the last section of the one filter panel — so this is
  // the rail that has to carry them, and `removeChip` routes their ✕ to
  // `removeTag` rather than to `setFlt`.
  //
  // Includes first, excludes after: the order the picker's own PICKED row uses,
  // so the two never disagree about what "picked" looks like.
  const byId = new Map(s.tagVocab.map((t) => [t.id, t.name]));
  // A picked tag whose name has not arrived yet still renders — as its id,
  // never as nothing. A chip you cannot see is a filter you cannot remove.
  const nameOf = (id: number): string => (byId.get(id) ?? '#' + id).toUpperCase();
  for (const id of bTagsOf(s)) raw.push({ l: nameOf(id), field: 'tag', value: String(id) });
  for (const id of xTagsOf(s)) {
    raw.push({ neg: true, l: 'NOT: ' + nameOf(id), field: 'tag', value: String(id) });
  }
  return chipsOf(raw);
}

function chipsOf(raw: ReadonlyArray<{ l: string; neg?: boolean; field: string; value: string }>): FilterChip[] {
  return raw.map((c, i) => ({
    key: 'fc' + i,
    l: c.l,
    neg: !!c.neg,
    bd: c.neg ? 'var(--acc2)' : 'var(--acc)',
    clr: c.neg ? 'var(--acc2)' : 'var(--acc)',
    bg: c.neg ? 'var(--acc2Soft)' : 'var(--accSoft)',
    field: c.field,
    value: c.value,
  }));
}

interface Seg {
  bd: string;
  clr: string;
  bg: string;
  zi: string;
}

export interface StatusRow {
  key: string;
  l: string;
  opts: Array<{ key: string; v: TriState; l: string } & Seg>;
}

/**
 * The five tri-state rows. `PLAYTEST` is separate from the four `flt.st` rows
 * because it writes `flt.pt` and renders HIDE in `--acc2` (prototype line 2264).
 */
function statusRows(F: Filters, fseg: (on: boolean) => Seg): StatusRow[] {
  const tri: readonly TriState[] = ['any', 'only', 'hide'];
  const rows: StatusRow[] = (['demo', 'ea', 'rel', 'unrel'] as const).map((k) => ({
    key: k,
    l: FLT_ST_NAMES[k] ?? k,
    opts: tri.map((v) => ({ key: k + v, v, l: v.toUpperCase(), ...fseg(F.st[k] === v) })),
  }));
  rows.push({
    key: 'pt',
    l: 'PLAYTEST',
    opts: tri.map((v) => ({
      key: 'pt' + v,
      v,
      l: v.toUpperCase(),
      ...(v === 'hide' && F.pt === 'hide'
        ? { bd: 'var(--acc2)', clr: 'var(--acc2)', bg: 'var(--acc2Soft)', zi: '2' }
        : fseg(F.pt === v)),
    })),
  });
  return rows;
}

/** One member's toggle in the OWNED BY section, plus the `ALL` shortcut. */
export interface OwnOpt extends Seg {
  key: string;
  /** `User.k`, or `''` on the `ALL` shortcut — the panels branch on this. */
  k: string;
  l: string;
  ini: string;
  hueBg: string;
  hasAv: boolean;
  avBg: string;
  on: boolean;
  /** Greyed and inert. Always paired with a non-empty `tip`. */
  dis: boolean;
  /** `data-tip` copy; `''` when the option is live. */
  tip: string;
  /** Two words ON the chip — the phone cannot hover. `''` when live. */
  sub: string;
}

/**
 * OWNED BY — the crew as toggles (2026-08-27, Marcus).
 *
 * AND, and the note says so in four words rather than making anyone infer it
 * from a result. Two names is the games BOTH own; `ALL` is the whole crew, which
 * is the question a game night actually asks.
 *
 * THE DISABLED STATE IS THE FEATURE HALF THAT IS EASY TO GET WRONG. A member
 * who has not linked Steam owns nothing we can see, so a toggle for them could
 * only ever return an empty board — it is disabled with the reason, and
 * `store/ownedBy.ts` enforces the same rule under both panels. A member we have
 * NOT YET ASKED about is also disabled, with a different tooltip, because
 * "hasn't linked" is a claim and we do not have it yet (deviation P2-08).
 *
 * `ALL` counts only the linked, so pressing it twice is a clean round trip.
 */
function ownedByVM(s: QdStore, F: Filters) {
  const on = new Set(F.own);
  const able = ownableKeys(s);
  const opts: OwnOpt[] = s.users.map((u) => {
    const st = linkStateOf(s, u);
    const isOn = on.has(u.k);
    const c = chipOf(u);
    return {
      key: 'own' + u.k,
      k: u.k,
      l: u.name.toUpperCase(),
      ini: c.ini,
      hueBg: c.hueBg,
      hasAv: c.hasAv,
      avBg: c.avBg,
      on: isOn,
      dis: st !== 'linked',
      tip: ownDisabledTip(st, u.name),
      sub: ownDisabledBadge(st),
      // A disabled option must not wear the OFF border either: `--gt-border-2`
      // is the same edge an available-but-unpicked member gets, and the two
      // states have to be told apart at a glance or the tooltip is the only
      // signal — which on a phone means no signal at all.
      ...(st === 'linked'
        ? onOff(isOn)
        : { bd: 'var(--gt-line)', clr: 'var(--gt-ghost-2)', bg: 'transparent' }),
      zi: isOn ? '2' : '1',
    };
  });
  const allOn = able.length > 0 && F.own.length >= able.length;
  return {
    /** No linked member at all — the section renders, and says why it is inert. */
    none: able.length === 0,
    noneTxt: 'NOBODY IN THE PARTY HAS LINKED STEAM YET, SO THERE ARE NO LIBRARIES TO FILTER ON.',
    note: 'EVERY NAME YOU PICK HAS TO OWN IT.',
    all: {
      key: 'ownall',
      k: '',
      l: allOn ? 'ALL ✓' : 'ALL',
      ini: '',
      hueBg: '',
      hasAv: false,
      avBg: '',
      on: allOn,
      dis: able.length === 0,
      tip: '',
      sub: '',
      ...(able.length === 0
        ? { bd: 'var(--gt-line)', clr: 'var(--gt-ghost-2)', bg: 'transparent' }
        : onOff(allOn)),
      zi: allOn ? '2' : '1',
    } satisfies OwnOpt,
    opts,
  };
}

export function filtersVM(s: QdStore) {
  // The panel is mounted once and hosted by every filtering screen, so this —
  // and only this — is where "the current screen's set" is the right question.
  const F = fltOf(s);
  const fseg = (on: boolean): Seg => ({ ...onOff(on), zi: on ? '2' : '1' });

  const browse = browsable(s);
  const matchN = browse.filter((g) => passFlt(g, F, s.pts, s.ownBy)).length;

  return {
    open: s.fltOpen,
    /**
     * ONE PANEL, ONE SHAPE, FIVE SCREENS (2026-08-27, Marcus). Every section
     * renders on every host now, because every filter applies on every host —
     * the panel used to hide four of its five sections outside Discover and
     * that is exactly what made it read as five different controls. The
     * `isLens` / `isDiscover` flags the panel gated on are gone with them; what
     * the host still changes is what a TAG pick DOES, and `tags.appliesTxt`
     * says that in words rather than by hiding anything.
     */
    hasActive: fltCount(F) + bTagsOf(s).length + xTagsOf(s).length > 0,
    chips: filterChips(s),
    matchTxt: matchN + ' OF ' + browse.length + ' CATALOG GAMES MATCH',
    matchN: matchesCount(matchN),
    /**
     * PLAYERS — HOW MANY OF US, now with its bottom rung. `SOLO` sits between
     * `ANY` and `2+` because the row reads as a ladder and that is where one
     * belongs; `Filters.p` has the argument for why its value is an exact 1
     * rather than the floor every other segment sets.
     *
     * DEDUPED BY VALUE, which `SOLO` forces and which the row wanted anyway:
     * `ALL <n>` collides with a fixed segment on any crew of 2, 3 or 4 — two
     * segments setting the identical filter, sharing a React key, both lighting
     * at once — and with `SOLO` on a crew of 1. First wins, so the fixed rungs
     * keep their labels and `ALL` appears only when it says something new.
     */
    players: [
      { v: 0, l: 'ANY' },
      { v: 1, l: 'SOLO' },
      { v: 2, l: '2+' },
      { v: 3, l: '3+' },
      { v: 4, l: '4+' },
      { v: s.users.length, l: 'ALL ' + s.users.length },
    ]
      .filter((o, i, all) => all.findIndex((x) => x.v === o.v) === i)
      .map((o) => ({ key: 'p' + o.v, v: o.v, l: o.l, ...fseg(F.p === o.v) })),
    status: statusRows(F, fseg),
    owned: ownedByVM(s, F),
    disc: [
      { v: 0, l: 'OFF' },
      { v: 1, l: 'ANY' },
      { v: 25, l: '≥25%' },
      { v: 50, l: '≥50%' },
    ].map((o) => ({ key: 'd' + o.v, v: o.v, l: o.l, ...fseg(F.disc === o.v) })),
    /**
     * REVIEWS — a floor on Steam's own rung, five of its nine.
     *
     * Not all nine, and not a percentage. The four rungs left out are the
     * negative ones plus `Positive`: nobody browses for "Mostly Negative or
     * better", and `Positive` (80%+ on 10–49 reviews) sits BELOW Very Positive
     * on Steam's ladder, so `≥ MOSTLY POSITIVE` already admits it. Five options
     * matches the PLAYERS row, which is the widest this panel draws.
     *
     * There is no companion minimum-review-COUNT control on purpose: Steam
     * folded the count into the rung, so `VERY POSITIVE+` already means 50+
     * reviews and `OVERWHELMING` means 500+. A second knob could ask for two
     * contradictory things (2026-08-27, Marcus: "drop the min reviews filter").
     */
    rev: [
      { v: 0, l: 'ANY' },
      { v: 5, l: 'MIXED+' },
      { v: 6, l: 'MOSTLY POS+' },
      { v: 8, l: 'VERY POS+' },
      { v: 9, l: 'OVERWHELMING' },
    ].map((o) => ({ key: 'rs' + o.v, v: o.v, l: o.l, ...fseg(F.rs === o.v) })),
    // The tag picker is the panel's last section now, and its fields come from
    // `tagPickerVM` — still Steam's own 446-word vocabulary, still tap-to-cycle
    // search → exclude → off. What is gone is the separate `# TAGS` modal and
    // its trigger, not the vocabulary discipline: the preset editorial chips
    // that once sat here are still never coming back (docs/DISCOVERY.md §1.4).
    tags: tagPickerVM(s),
    prLo: F.pr[0],
    prHi: F.pr[1],
    prLoPct: (F.pr[0] / 60) * 100,
    prHiPct: (F.pr[1] / 60) * 100,
    prWPct: ((F.pr[1] - F.pr[0]) / 60) * 100,
    prLabel: priceLabel(F.pr),
    prLoBub: s.fltPrDrag === 'lo',
    prHiBub: s.fltPrDrag === 'hi',
    prLoTxt: '$' + F.pr[0],
    prHiTxt: F.pr[1] === 60 ? '$60+' : '$' + F.pr[1],
  };
}

// ---------------------------------------------------------------------------
// move sheet
// ---------------------------------------------------------------------------

export function moveSheetVM(s: QdStore) {
  const sh = s.sheet;
  if (!sh) return { shShow: false as const };
  const g = s.gById(sh.id);
  const me = s.me();
  if (!g || !me) return { shShow: false as const };
  const t = tileVM(g, tileCtx(s));
  if (sh.kind === 'int') {
    const v = s.myVote(sh.id);
    const rowLen = intRowIds(s.list, me.k, s.intOrder[me.k] ?? [], v).length;
    return {
      shShow: true as const,
      kind: 'int' as const,
      id: sh.id,
      shName: g.n,
      shSub: 'YOUR CALL: ' + (v ? LVL[v].g + ' · ' + LVL[v].t : '? NO CALL YET'),
      shIni: t.ini,
      shFall: t.fall,
      shCoverCands: t.coverCands,
      // Built from `CALLS` the way the goty branch below is built from `TIERS`,
      // then the clear act.
      //
      // The clear act is keyed `'w'`, not `'c'`. It was `'c'` on the binary
      // board — for CLEAR — and `MoveSheet`'s `ACT_TONE` still keys its grey
      // tone off the act key, so leaving it there would have handed PLAY IT
      // WITHOUT ME the clear row's grey and the clear row accent-2. `'w'` is
      // the Interests board's own id for the same row (`intRowVote`), so the
      // two now agree on what to call it.
      shActs: [
        ...CALLS.map((l) => ({
          key: l as string,
          v: l as VoteInput,
          l: LVL[l].g + ' · ' + LVL[l].t + (v === l ? ' ✓' : ''),
          on: v === l,
        })),
        { key: 'w', v: '' as VoteInput, l: '? NO CALL YET' + (v === '' ? ' ✓' : ''), on: v === '' },
      ],
      shHasNudge: rowLen > 1,
      shCanUnplace: false,
    };
  }
  const curT = (s.tiers[me.k] ?? {})[String(sh.id)];
  const rowLen = curT === undefined ? 0 : tierRowIds(s.potentials[me.k] ?? [], s.tiers[me.k] ?? {}, s.tierOrder[me.k] ?? [], curT).length;
  return {
    shShow: true as const,
    kind: 'goty' as const,
    id: sh.id,
    shName: g.n,
    shSub: curT !== undefined ? 'PLACED: ' + TIER_NAMES_SHEET[curT] : 'IN YOUR TO-DO',
    shIni: t.ini,
    shFall: t.fall,
    shCoverCands: t.coverCands,
    shActs: TIERS.map((tt) => ({
      key: tt,
      v: tt,
      l: TIER_NAMES_SHEET[tt] + (curT === tt ? ' ✓' : ''),
      on: curT === tt,
    })),
    shHasNudge: rowLen > 1,
    shCanUnplace: curT !== undefined,
  };
}
